// ============================================================================
// SS2 FURTHER MATHEMATICS — COMPLETE LEARNING MODULE (33 TOPICS)
// NERDC Scheme of Work — First Term to Third Term
// ============================================================================

export const WAEC_FURTHER_MATHEMATICS_LEARN_SS2 = [
  {
    topic: 'Solution to Quadratic Equations',
    topicCode: 'SS2-FMATH-01',
    module: 'Algebra',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">quadratic equation</span> is any equation that can be written in the form <strong>ax&sup2; + bx + c = 0</strong>, where a, b, c are constants and a &ne; 0. In SS1 you met factorization; in Further Mathematics we sharpen every method &mdash; factorization, completing the square, and the quadratic formula &mdash; and learn to read off the <span class="learn-keyword">nature of the roots</span> without fully solving the equation.\n</div>\n\n<h3 class="learn-subheading">1. Solving by Factorization</h3>\n<p class="learn-p">Write the equation as a product of two linear brackets equal to zero. <strong>Example:</strong> Solve x&sup2; &minus; 5x + 6 = 0.<br>We need two numbers that multiply to 6 and add to &minus;5: they are &minus;2 and &minus;3.<br>(x &minus; 2)(x &minus; 3) = 0 &rArr; x = 2 or x = 3.</p>\n\n<h3 class="learn-subheading">2. Completing the Square</h3>\n<p class="learn-p">Used when factorization is not obvious. <strong>Example:</strong> Solve x&sup2; + 6x + 2 = 0.<br>x&sup2; + 6x = &minus;2 &rArr; (x + 3)&sup2; &minus; 9 = &minus;2 &rArr; (x + 3)&sup2; = 7 &rArr; x = &minus;3 &plusmn; &radic;7.</p>\n\n<h3 class="learn-subheading">3. The Quadratic Formula</h3>\n<p class="learn-p">For ax&sup2; + bx + c = 0, x = (&minus;b &plusmn; &radic;(b&sup2; &minus; 4ac)) / 2a. <strong>Example:</strong> Solve 2x&sup2; &minus; 3x &minus; 5 = 0.<br>a = 2, b = &minus;3, c = &minus;5. Discriminant = 9 + 40 = 49. x = (3 &plusmn; 7)/4 &rArr; x = 2.5 or x = &minus;1.</p>\n\n<h3 class="learn-subheading">4. The Discriminant and Nature of Roots</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Discriminant &Delta; = b&sup2; &minus; 4ac</th><th>Nature of Roots</th></tr></thead>\n    <tbody>\n      <tr><td>&Delta; &gt; 0</td><td>Two distinct real roots</td></tr>\n      <tr><td>&Delta; = 0</td><td>One repeated (equal) real root</td></tr>\n      <tr><td>&Delta; &lt; 0</td><td>No real roots (complex/imaginary roots)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Word Problems</h3>\n<p class="learn-p"><strong>Example:</strong> Two consecutive positive integers have a product of 132. Find them.<br>Let the numbers be n and n + 1: n(n + 1) = 132 &rArr; n&sup2; + n &minus; 132 = 0 &rArr; (n + 12)(n &minus; 11) = 0 &rArr; n = 11 (rejecting n = &minus;12). The numbers are 11 and 12.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> If a question only asks for the <em>nature</em> of the roots, do not solve the whole equation &mdash; just compute &Delta; = b&sup2; &minus; 4ac and use the table above. This saves valuable time in the exam.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Quadratic equations can be solved by factorization, completing the square, or the formula. The discriminant b&sup2; &minus; 4ac tells us the nature of the roots before we even solve. Sum of roots = &minus;b/a; product of roots = c/a.\n</div>\n',
    questions: [
      {
        q: 'Solve x² − 7x + 12 = 0.',
        o: ['x = 3 or x = 4', 'x = −3 or x = −4', 'x = 2 or x = 6', 'x = 1 or x = 12'],
        a: 0,
        e: 'We need two numbers that multiply to 12 and add to −7: these are −3 and −4. So (x − 3)(x − 4) = 0, giving x = 3 or x = 4.',
        h: 'Find two numbers that multiply to 12 and add to −7.',
        yr: 'SS2',
      },
      {
        q: 'Solve x² + 2x − 15 = 0.',
        o: ['x = −5 or x = 3', 'x = 5 or x = −3', 'x = −3 or x = 5', 'x = 15 or x = −1'],
        a: 0,
        e: 'Two numbers that multiply to −15 and add to 2 are 5 and −3. So (x + 5)(x − 3) = 0, giving x = −5 or x = 3.',
        h: 'Find two numbers multiplying to −15 and adding to 2.',
        yr: 'SS2',
      },
      {
        q: 'Use completing the square to solve x² − 4x − 5 = 0.',
        o: ['x = 5 or x = −1', 'x = −5 or x = 1', 'x = 4 or x = 1', 'x = 2 or x = 3'],
        a: 0,
        e: 'x² − 4x = 5 ⇒ (x − 2)² − 4 = 5 ⇒ (x − 2)² = 9 ⇒ x − 2 = ±3 ⇒ x = 5 or x = −1.',
        h: 'Halve the coefficient of x (−4 → −2), then form (x − 2)².',
        yr: 'SS2',
      },
      {
        q: 'Use the quadratic formula to solve 2x² + 5x − 3 = 0.',
        o: ['x = 0.5 or x = −3', 'x = 3 or x = −0.5', 'x = 1 or x = −3', 'x = −0.5 or x = 3'],
        a: 0,
        e: 'a = 2, b = 5, c = −3. Δ = 25 + 24 = 49, √Δ = 7. x = (−5 ± 7)/4, giving x = 0.5 or x = −3.',
        h: 'Compute Δ = b² − 4ac first, then apply the formula.',
        yr: 'SS2',
      },
      {
        q: 'Find the discriminant of 3x² − 2x + 5 = 0 and state the nature of the roots.',
        o: [
          'Δ = −56, no real roots',
          'Δ = 56, two real roots',
          'Δ = 4, repeated root',
          'Δ = −4, complex roots',
        ],
        a: 0,
        e: 'Δ = b² − 4ac = (−2)² − 4(3)(5) = 4 − 60 = −56. Since Δ < 0, the equation has no real roots.',
        h: 'Δ = b² − 4ac; a negative value means no real roots.',
        yr: 'SS2',
      },
      {
        q: 'For what value of k does x² − 6x + k = 0 have equal roots?',
        o: ['k = 9', 'k = 6', 'k = 3', 'k = 36'],
        a: 0,
        e: 'Equal roots require Δ = 0: 36 − 4k = 0 ⇒ 4k = 36 ⇒ k = 9.',
        h: 'Set the discriminant b² − 4ac equal to zero and solve for k.',
        yr: 'SS2',
      },
      {
        q: 'Find the sum and product of the roots of 2x² − 7x + 3 = 0.',
        o: [
          'Sum = 3.5, Product = 1.5',
          'Sum = 7, Product = 3',
          'Sum = −3.5, Product = −1.5',
          'Sum = 3.5, Product = 3',
        ],
        a: 0,
        e: 'Sum of roots = −b/a = 7/2 = 3.5. Product of roots = c/a = 3/2 = 1.5.',
        h: 'Sum of roots = −b/a; product of roots = c/a.',
        yr: 'SS2',
      },
      {
        q: 'Solve x² = 9 − 8x.',
        o: ['x = −9 or x = 1', 'x = 9 or x = −1', 'x = −1 or x = 9', 'x = −9 or x = −1'],
        a: 0,
        e: 'Rearranging gives x² + 8x − 9 = 0 ⇒ (x + 9)(x − 1) = 0 ⇒ x = −9 or x = 1.',
        h: 'Move all terms to one side to get the standard form ax² + bx + c = 0 first.',
        yr: 'SS2',
      },
      {
        q: 'Express x² + 10x + 7 in the form (x + a)² + b.',
        o: ['(x + 5)² − 18', '(x + 5)² + 18', '(x − 5)² − 18', '(x + 10)² − 18'],
        a: 0,
        e: 'Half of 10 is 5, so (x + 5)² = x² + 10x + 25. Since we only want +7, we subtract 18: (x + 5)² − 18.',
        h: 'Halve the coefficient of x to get the value of a, then adjust the constant.',
        yr: 'SS2',
      },
      {
        q: 'Use the quadratic formula to solve x² − x − 1 = 0.',
        o: ['x = (1 ± √5)/2', 'x = (1 ± √3)/2', 'x = (−1 ± √5)/2', 'x = 1 ± √5'],
        a: 0,
        e: 'a = 1, b = −1, c = −1. Δ = 1 + 4 = 5. x = (1 ± √5)/2 (this is related to the golden ratio!).',
        h: 'Substitute a = 1, b = −1, c = −1 into the quadratic formula.',
        yr: 'SS2',
      },
      {
        q: 'Two numbers differ by 3 and their product is 108. Find the numbers.',
        o: ['9 and 12', '−9 and −12', '8 and 13.5', '6 and 18'],
        a: 0,
        e: 'Let the smaller number be n: n(n + 3) = 108 ⇒ n² + 3n − 108 = 0 ⇒ (n + 12)(n − 9) = 0. Taking the positive value, n = 9, so the numbers are 9 and 12.',
        h: 'Let the smaller number be n, and write n(n + 3) = 108.',
        yr: 'SS2',
      },
      {
        q: 'If one root of x² − 5x + k = 0 is 2, find k and the other root.',
        o: [
          'k = 6, other root = 3',
          'k = 3, other root = 2',
          'k = 10, other root = 5',
          'k = −6, other root = 7',
        ],
        a: 0,
        e: 'Substituting x = 2: 4 − 10 + k = 0 ⇒ k = 6. Since sum of roots = 5, the other root = 5 − 2 = 3.',
        h: 'Substitute the known root into the equation to find k, then use sum of roots = −b/a.',
        yr: 'SS2',
      },
      {
        q: 'Which quadratic equation has roots −2 and 5?',
        o: ['x² − 3x − 10 = 0', 'x² + 3x − 10 = 0', 'x² − 3x + 10 = 0', 'x² − 7x + 10 = 0'],
        a: 0,
        e: 'Sum of roots = 3, product of roots = −10. Equation: x² − (sum)x + product = 0 ⇒ x² − 3x − 10 = 0.',
        h: 'Use x² − (sum of roots)x + (product of roots) = 0.',
        yr: 'SS2',
      },
      {
        q: 'Find the discriminant of 4x² − 4x + 1 = 0 and describe the roots.',
        o: [
          'Δ = 0, one repeated root x = 0.5',
          'Δ = 16, two distinct roots',
          'Δ = −16, no real roots',
          'Δ = 4, two distinct roots',
        ],
        a: 0,
        e: 'Δ = 16 − 16 = 0, so there is one repeated root. Solving gives (2x − 1)² = 0 ⇒ x = 0.5.',
        h: 'Compute Δ = b² − 4ac; Δ = 0 means equal roots.',
        yr: 'SS2',
      },
      {
        q: 'Solve 3x² − 12 = 0.',
        o: ['x = 2 or x = −2', 'x = 4 or x = −4', 'x = ±2√3', 'x = 12 or x = −12'],
        a: 0,
        e: '3x² = 12 ⇒ x² = 4 ⇒ x = ±2.',
        h: 'Isolate x² first, then take the square root of both sides.',
        yr: 'SS2',
      },
      {
        q: 'Find the product of the roots of 5x² + 3x − 2 = 0.',
        o: ['−0.4', '0.4', '−0.6', '1.5'],
        a: 0,
        e: 'Product of roots = c/a = −2/5 = −0.4.',
        h: 'Product of roots = c/a.',
        yr: 'SS2',
      },
      {
        q: 'Find the sum of the roots of x² + 7x − 18 = 0.',
        o: ['−7', '7', '18', '−18'],
        a: 0,
        e: 'Sum of roots = −b/a = −7/1 = −7.',
        h: 'Sum of roots = −b/a.',
        yr: 'SS2',
      },
      {
        q: 'Solve (x − 3)² = 16.',
        o: ['x = 7 or x = −1', 'x = 7 or x = 1', 'x = −7 or x = 1', 'x = 13 or x = −1'],
        a: 0,
        e: 'x − 3 = ±4 ⇒ x = 3 + 4 = 7 or x = 3 − 4 = −1.',
        h: 'Take the square root of both sides, remembering the ± sign.',
        yr: 'SS2',
      },
      {
        q: 'A rectangular field has length 4 m more than its width, and area 96 m². Find its dimensions.',
        o: [
          'Width 8 m, Length 12 m',
          'Width 12 m, Length 16 m',
          'Width 6 m, Length 10 m',
          'Width 10 m, Length 14 m',
        ],
        a: 0,
        e: 'Let width = w: w(w + 4) = 96 ⇒ w² + 4w − 96 = 0 ⇒ (w + 12)(w − 8) = 0. Taking the positive root, w = 8, so length = 12.',
        h: 'Let width = w, length = w + 4, and set up w(w + 4) = 96.',
        yr: 'SS2',
      },
      {
        q: 'If the discriminant of a quadratic equation is negative, what can we conclude about its roots?',
        o: [
          'The roots are not real (complex conjugates)',
          'The roots are equal',
          'The roots are distinct and real',
          'The roots are always negative numbers',
        ],
        a: 0,
        e: 'A negative discriminant (Δ < 0) means the square root of a negative number is required, so the roots are not real — they are a pair of complex conjugates.',
        h: 'Recall the discriminant table: Δ < 0 means no real roots.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Tangent and Normal to a Curve',
    topicCode: 'SS2-FMATH-02',
    module: 'Coordinate Geometry',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  At any point on a curve, we can draw a straight line that just touches the curve &mdash; this is the <span class="learn-keyword">tangent</span>. The line perpendicular to the tangent at that same point is the <span class="learn-keyword">normal</span>. Both are essential tools for describing the behaviour of curves at a specific point, and they connect coordinate geometry directly to calculus.</div>\n\n<h3 class="learn-subheading">1. Gradient of a Tangent</h3>\n<p class="learn-p">The gradient of the tangent to a curve y = f(x) at a point is the value of the derivative dy/dx evaluated at that point. <strong>Example:</strong> For y = x&sup2;, dy/dx = 2x. At x = 3, gradient = 6.</p>\n\n<h3 class="learn-subheading">2. Equation of the Tangent</h3>\n<p class="learn-p">Using the point-gradient form y &minus; y&#8321; = m(x &minus; x&#8321;), where m is the gradient at that point. <strong>Example:</strong> Find the tangent to y = x&sup2; at (3, 9).<br>Gradient m = 2(3) = 6. Equation: y &minus; 9 = 6(x &minus; 3) &rArr; y = 6x &minus; 9.</p>\n\n<h3 class="learn-subheading">3. Gradient and Equation of the Normal</h3>\n<p class="learn-p">Since the normal is perpendicular to the tangent, its gradient is the negative reciprocal: m<sub>normal</sub> = &minus;1/m<sub>tangent</sub>. <strong>Example:</strong> For the curve above at (3, 9), normal gradient = &minus;1/6. Equation: y &minus; 9 = &minus;(1/6)(x &minus; 3) &rArr; 6y &minus; 54 = &minus;x + 3 &rArr; x + 6y = 57.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Line</th><th>Gradient</th><th>Passes through</th></tr></thead>\n    <tbody>\n      <tr><td>Tangent</td><td>m = dy/dx at the point</td><td>The point on the curve</td></tr>\n      <tr><td>Normal</td><td>&minus;1/m</td><td>The same point on the curve</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Points Where the Tangent Is Horizontal</h3>\n<p class="learn-p">A horizontal tangent occurs where dy/dx = 0. These are the turning points of the curve, which we study in depth under maxima and minima.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always find dy/dx first, substitute the x-value to get the numeric gradient, THEN decide whether you need the tangent (use m directly) or the normal (use &minus;1/m). Mixing these up is the most common error.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> The tangent touches the curve with gradient dy/dx at that point; the normal is perpendicular to the tangent with gradient &minus;1/m. Both use the point-gradient equation y &minus; y&#8321; = m(x &minus; x&#8321;).\n</div>\n',
    questions: [
      {
        q: 'Find the gradient of the tangent to y = x² at the point (2, 4).',
        o: ['4', '2', '8', '16'],
        a: 0,
        e: 'dy/dx = 2x. At x = 2, gradient = 2(2) = 4.',
        h: 'Differentiate y = x² to get dy/dx = 2x, then substitute x = 2.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the tangent to y = x² at the point (3, 9).',
        o: ['y = 6x − 9', 'y = 3x − 9', 'y = 6x + 9', 'y = 9x − 6'],
        a: 0,
        e: 'dy/dx = 2x, so gradient at x = 3 is 6. Using y − 9 = 6(x − 3): y = 6x − 18 + 9 = 6x − 9.',
        h: 'Find the gradient, then use y − y₁ = m(x − x₁).',
        yr: 'SS2',
      },
      {
        q: 'If the gradient of the tangent to a curve at a point is 4, what is the gradient of the normal at that point?',
        o: ['−1/4', '4', '1/4', '−4'],
        a: 0,
        e: "The normal is perpendicular to the tangent, so its gradient is the negative reciprocal of the tangent's gradient: −1/4.",
        h: 'Normal gradient = −1/(tangent gradient).',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the normal to y = x² at the point (3, 9).',
        o: ['x + 6y = 57', '6x + y = 27', 'x − 6y = 57', '6y − x = 9'],
        a: 0,
        e: 'Tangent gradient = 6, so normal gradient = −1/6. Using y − 9 = −(1/6)(x − 3): 6y − 54 = −x + 3 ⇒ x + 6y = 57.',
        h: 'Use the negative reciprocal of the tangent gradient with the point-gradient formula.',
        yr: 'SS2',
      },
      {
        q: 'Find the gradient of the tangent to y = x³ at the point where x = 2.',
        o: ['12', '8', '6', '24'],
        a: 0,
        e: 'dy/dx = 3x². At x = 2, gradient = 3(4) = 12.',
        h: 'Differentiate y = x³ to get 3x², then substitute x = 2.',
        yr: 'SS2',
      },
      {
        q: 'At what point(s) on y = x² − 4x + 3 is the tangent horizontal?',
        o: ['(2, −1)', '(0, 3)', '(4, 3)', '(1, 0)'],
        a: 0,
        e: 'dy/dx = 2x − 4. A horizontal tangent means dy/dx = 0: 2x − 4 = 0 ⇒ x = 2. Then y = 4 − 8 + 3 = −1. Point: (2, −1).',
        h: 'Set dy/dx = 0 and solve for x, then find the corresponding y.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the tangent to y = 2x² − 3x at the point where x = 1.',
        o: ['y = x − 2', 'y = 4x − 5', 'y = x + 2', 'y = 2x − 3'],
        a: 0,
        e: 'At x = 1, y = 2 − 3 = −1. dy/dx = 4x − 3, so at x = 1, gradient = 1. Equation: y − (−1) = 1(x − 1) ⇒ y = x − 2.',
        h: 'Find the y-coordinate first, then the gradient, then use the point-gradient formula.',
        yr: 'SS2',
      },
      {
        q: 'The tangent to a curve at (1, 5) has gradient 3. Find the equation of the normal at this point.',
        o: ['x + 3y = 16', '3x + y = 8', '3x − y = −2', 'x − 3y = −14'],
        a: 0,
        e: 'Normal gradient = −1/3. Using y − 5 = −1/3(x − 1): 3y − 15 = −x + 1 ⇒ x + 3y = 16.',
        h: "Take the negative reciprocal of the tangent's gradient for the normal.",
        yr: 'SS2',
      },
      {
        q: 'Find the gradient of the normal to y = x² + 1 at the point where x = −2.',
        o: ['1/4', '−4', '4', '−1/4'],
        a: 0,
        e: 'dy/dx = 2x, so at x = −2, tangent gradient = −4. Normal gradient = −1/(−4) = 1/4.',
        h: 'Find the tangent gradient first, then take its negative reciprocal.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the tangent to y = x² − 2x + 1 at the point (2, 1).',
        o: ['y = 2x − 3', 'y = 2x + 3', 'y = x − 1', 'y = 2x − 1'],
        a: 0,
        e: 'dy/dx = 2x − 2, so at x = 2, gradient = 2. Equation: y − 1 = 2(x − 2) ⇒ y = 2x − 3.',
        h: 'Compute dy/dx, substitute x = 2 for the gradient, then use the point-gradient equation.',
        yr: 'SS2',
      },
      {
        q: 'A curve has gradient function dy/dx = 3x² − 6x. Find the gradient of the tangent at x = 1.',
        o: ['−3', '3', '−6', '6'],
        a: 0,
        e: 'Substituting x = 1: dy/dx = 3(1) − 6(1) = 3 − 6 = −3.',
        h: 'Simply substitute x = 1 into the gradient function.',
        yr: 'SS2',
      },
      {
        q: 'If the equation of a tangent is y = 4x − 7, what is the gradient of the normal at the point of tangency?',
        o: ['−1/4', '4', '1/4', '−4'],
        a: 0,
        e: "The tangent's gradient is the coefficient of x, which is 4. The normal gradient is the negative reciprocal: −1/4.",
        h: 'Read the gradient of a line y = mx + c directly from m, then find its negative reciprocal.',
        yr: 'SS2',
      },
      {
        q: 'Find the point on y = x² where the tangent has gradient 8.',
        o: ['(4, 16)', '(8, 64)', '(2, 4)', '(4, 8)'],
        a: 0,
        e: 'dy/dx = 2x = 8 ⇒ x = 4. Then y = 16. Point: (4, 16).',
        h: 'Set dy/dx equal to the given gradient and solve for x, then find y.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the normal to y = x³ at the point where x = 1 (curve passes through (1,1)).',
        o: ['x + 3y = 4', '3x + y = 4', 'x − 3y = −2', '3x − y = 2'],
        a: 0,
        e: 'dy/dx = 3x², so at x = 1, tangent gradient = 3. Normal gradient = −1/3. Equation: y − 1 = −1/3(x − 1) ⇒ 3y − 3 = −x + 1 ⇒ x + 3y = 4.',
        h: 'Find the tangent gradient at x = 1, take the negative reciprocal, then form the equation.',
        yr: 'SS2',
      },
      {
        q: 'Two curves y = x² and y = 2x − 1 meet at x = 1. What is the gradient of the tangent to y = x² at this point?',
        o: ['2', '1', '4', '0'],
        a: 0,
        e: 'dy/dx = 2x. At x = 1, gradient = 2(1) = 2.',
        h: 'Only the curve y = x² is relevant for finding its own tangent gradient.',
        yr: 'SS2',
      },
      {
        q: 'Which statement about the tangent and normal to a curve at a given point is TRUE?',
        o: [
          'They are perpendicular to each other and meet at that point',
          'They are always parallel',
          'They have the same gradient',
          'The normal never passes through the point of tangency',
        ],
        a: 0,
        e: 'By definition, the normal is perpendicular to the tangent at the point of contact, and both lines pass through that same point on the curve.',
        h: 'Recall the perpendicular relationship between tangent and normal.',
        yr: 'SS2',
      },
      {
        q: 'Find the gradient of the tangent to y = 5 − x² at the point where x = 1.',
        o: ['−2', '2', '4', '−4'],
        a: 0,
        e: 'dy/dx = −2x. At x = 1, gradient = −2(1) = −2.',
        h: 'Differentiate y = 5 − x² carefully, remembering the negative sign.',
        yr: 'SS2',
      },
      {
        q: 'Find the value of x where the tangent to y = x² − 5x + 6 is horizontal.',
        o: ['x = 2.5', 'x = 3', 'x = 5', 'x = 0'],
        a: 0,
        e: 'dy/dx = 2x − 5. A horizontal tangent means dy/dx = 0: 2x − 5 = 0 ⇒ x = 2.5. This is the turning point (minimum) of the parabola.',
        h: 'Set the gradient function dy/dx equal to zero and solve for x.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the tangent to y = 3x² at the point where x = −1.',
        o: ['y = −6x − 3', 'y = 6x + 3', 'y = −6x + 3', 'y = 3x − 3'],
        a: 0,
        e: 'At x = −1, y = 3(1) = 3. dy/dx = 6x, so at x = −1, gradient = −6. Equation: y − 3 = −6(x − (−1)) ⇒ y − 3 = −6x − 6 ⇒ y = −6x − 3.',
        h: 'Find y at x = −1 first, then the gradient, then apply the point-gradient formula carefully with signs.',
        yr: 'SS2',
      },
      {
        q: 'A normal line to a curve at point P has gradient −2/5. What is the gradient of the tangent at P?',
        o: ['5/2', '−5/2', '2/5', '−2/5'],
        a: 0,
        e: 'Since normal gradient = −1/(tangent gradient), we have tangent gradient = −1/(normal gradient) = −1/(−2/5) = 5/2.',
        h: 'Reverse the negative reciprocal relationship to find the tangent gradient from the normal gradient.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Polynomials I',
    topicCode: 'SS2-FMATH-03',
    module: 'Polynomials',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">polynomial</span> is an expression made up of variables and coefficients, involving only addition, subtraction, and non-negative integer powers, such as 3x&sup3; &minus; 2x&sup2; + x &minus; 5. Understanding how to add, subtract, multiply, and divide polynomials is essential before we tackle factor theorem, remainder theorem, and cubic equations later in this term.</div>\n\n<h3 class="learn-subheading">1. Degree and Terms</h3>\n<p class="learn-p">The <span class="learn-keyword">degree</span> of a polynomial is the highest power of the variable present. In 4x&#8308; &minus; 3x&sup2; + 7, the degree is 4. The <span class="learn-keyword">leading coefficient</span> is the coefficient of the highest-degree term (here, 4).</p>\n\n<h3 class="learn-subheading">2. Addition and Subtraction</h3>\n<p class="learn-p">Combine like terms only. <strong>Example:</strong> (3x&sup3; + 2x&sup2; &minus; x) + (x&sup3; &minus; 5x&sup2; + 4x) = 4x&sup3; &minus; 3x&sup2; + 3x.</p>\n\n<h3 class="learn-subheading">3. Multiplication</h3>\n<p class="learn-p">Multiply each term of the first polynomial by every term of the second, then collect like terms. <strong>Example:</strong> (x + 2)(x&sup2; &minus; 3x + 1) = x&sup3; &minus; 3x&sup2; + x + 2x&sup2; &minus; 6x + 2 = x&sup3; &minus; x&sup2; &minus; 5x + 2.</p>\n\n<h3 class="learn-subheading">4. Long Division of Polynomials</h3>\n<p class="learn-p">Similar to numerical long division. <strong>Example:</strong> Divide x&sup3; + 2x&sup2; &minus; 5x &minus; 6 by (x &minus; 2).<br>\nx&sup3; &divide; x = x&sup2;: multiply (x &minus; 2) by x&sup2; &rArr; x&sup3; &minus; 2x&sup2;. Subtract: 4x&sup2; &minus; 5x &minus; 6.<br>\n4x&sup2; &divide; x = 4x: multiply (x &minus; 2) by 4x &rArr; 4x&sup2; &minus; 8x. Subtract: 3x &minus; 6.<br>\n3x &divide; x = 3: multiply (x &minus; 2) by 3 &rArr; 3x &minus; 6. Subtract: 0 (no remainder).<br>\nQuotient: x&sup2; + 4x + 3, Remainder: 0.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Operation</th><th>Rule</th></tr></thead>\n    <tbody>\n      <tr><td>Addition/Subtraction</td><td>Combine like terms (same power of x)</td></tr>\n      <tr><td>Multiplication</td><td>Distribute every term, then collect like terms</td></tr>\n      <tr><td>Division</td><td>Long division: divide, multiply, subtract, bring down</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> When dividing, always write the polynomial in descending powers first, and insert a placeholder (0x) for any missing power &mdash; e.g., x&sup3; &minus; 1 should be written as x&sup3; + 0x&sup2; + 0x &minus; 1 before dividing.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Polynomials are combined by matching like terms. Multiplication uses full distribution; division uses the repeated divide-multiply-subtract cycle, producing a quotient and possibly a remainder.\n</div>\n',
    questions: [
      {
        q: 'What is the degree of the polynomial 5x⁴ − 3x² + 2x − 7?',
        o: ['4', '3', '2', '7'],
        a: 0,
        e: 'The degree of a polynomial is the highest power of x present. Here, the highest power is x⁴, so the degree is 4.',
        h: 'Look for the term with the highest power of x.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (3x³ + 2x² − x) + (x³ − 5x² + 4x).',
        o: ['4x³ − 3x² + 3x', '4x³ + 7x² + 3x', '2x³ − 3x² + 3x', '4x³ − 3x² − 3x'],
        a: 0,
        e: 'Combine like terms: (3x³ + x³) + (2x² − 5x²) + (−x + 4x) = 4x³ − 3x² + 3x.',
        h: 'Add coefficients of terms with the same power of x separately.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (4x² − 3x + 5) − (2x² + x − 3).',
        o: ['2x² − 4x + 8', '2x² − 2x + 2', '6x² − 2x + 2', '2x² − 4x + 2'],
        a: 0,
        e: '(4x² − 2x²) + (−3x − x) + (5 − (−3)) = 2x² − 4x + 8.',
        h: 'Subtract each term of the second polynomial from the corresponding term in the first.',
        yr: 'SS2',
      },
      {
        q: 'Expand and simplify (x + 2)(x² − 3x + 1).',
        o: ['x³ − x² − 5x + 2', 'x³ + x² − 5x + 2', 'x³ − x² + 5x + 2', 'x³ − 5x² − 5x + 2'],
        a: 0,
        e: '(x)(x² − 3x + 1) + (2)(x² − 3x + 1) = x³ − 3x² + x + 2x² − 6x + 2 = x³ − x² − 5x + 2.',
        h: 'Distribute each term of the first bracket across the second, then collect like terms.',
        yr: 'SS2',
      },
      {
        q: 'Divide x³ + 2x² − 5x − 6 by (x − 2). Find the quotient.',
        o: ['x² + 4x + 3', 'x² + 2x + 3', 'x² − 4x + 3', 'x² + 4x − 3'],
        a: 0,
        e: 'Performing long division: x³ + 2x² − 5x − 6 ÷ (x − 2) gives quotient x² + 4x + 3 with remainder 0.',
        h: 'Use polynomial long division, dividing the leading term at each stage.',
        yr: 'SS2',
      },
      {
        q: 'What is the leading coefficient of the polynomial 7x³ − 2x² + x − 9?',
        o: ['7', '−2', '1', '−9'],
        a: 0,
        e: 'The leading coefficient is the coefficient of the term with the highest power — here 7x³, so the leading coefficient is 7.',
        h: 'Identify the coefficient of the highest power term.',
        yr: 'SS2',
      },
      {
        q: 'Expand (2x − 1)(x + 3).',
        o: ['2x² + 5x − 3', '2x² − 5x − 3', '2x² + 5x + 3', '2x² + x − 3'],
        a: 0,
        e: '(2x)(x) + (2x)(3) + (−1)(x) + (−1)(3) = 2x² + 6x − x − 3 = 2x² + 5x − 3.',
        h: 'Multiply each term in the first bracket by each term in the second bracket.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (5x²y − 3xy + 2y) + (2x²y + xy − 5y).',
        o: ['7x²y − 2xy − 3y', '7x²y + 2xy + 3y', '3x²y − 2xy − 3y', '7x²y − 4xy − 3y'],
        a: 0,
        e: 'Combine like terms: (5x²y + 2x²y) + (−3xy + xy) + (2y − 5y) = 7x²y − 2xy − 3y.',
        h: 'Group and add terms with identical variable parts (x²y, xy, y).',
        yr: 'SS2',
      },
      {
        q: 'Divide 2x³ − 3x² − 11x + 6 by (x + 2). Find the quotient.',
        o: ['2x² − 7x + 3', '2x² + 7x + 3', '2x² − 7x − 3', '2x² − x + 3'],
        a: 0,
        e: 'Long division: 2x³ − 3x² − 11x + 6 ÷ (x + 2) gives quotient 2x² − 7x + 3, with remainder 0.',
        h: 'Divide the leading term 2x³ by x to start, then continue the divide-multiply-subtract cycle.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a polynomial of degree 3 in x?',
        o: ['x³ − 4x + 1', 'x² + 1/x', '√x + 2', 'x⁻³ + x'],
        a: 0,
        e: 'A polynomial must have only non-negative integer powers of x. x³ − 4x + 1 fits this and has degree 3. The others involve negative or fractional powers, so they are not polynomials.',
        h: 'Polynomials only allow whole-number, non-negative powers of the variable.',
        yr: 'SS2',
      },
      {
        q: 'Expand and simplify (x − 1)²(x + 2).',
        o: ['x³ − 3x + 2', 'x³ + 3x + 2', 'x³ − 3x − 2', 'x³ + x − 2'],
        a: 0,
        e: '(x − 1)² = x² − 2x + 1. Then (x² − 2x + 1)(x + 2) = x³ + 2x² − 2x² − 4x + x + 2 = x³ − 3x + 2.',
        h: 'First expand the squared bracket, then multiply the result by (x + 2).',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when x³ + 3x² − x + 5 is divided by (x − 1) using long division.',
        o: ['8', '9', '6', '0'],
        a: 0,
        e: 'Dividing gives quotient x² + 4x + 3 with remainder 1 + 3 − 1 + 5 = 8 (verify by substitution: f(1) = 1 + 3 − 1 + 5 = 8).',
        h: 'The remainder equals the polynomial evaluated at x = 1 (this links to the Remainder Theorem, coming next).',
        yr: 'SS2',
      },
      {
        q: 'Simplify 3x(2x² − x + 4).',
        o: ['6x³ − 3x² + 12x', '6x³ + 3x² + 12x', '6x³ − 3x² − 12x', '5x³ − 3x² + 12x'],
        a: 0,
        e: 'Distribute 3x across each term: 3x(2x²) + 3x(−x) + 3x(4) = 6x³ − 3x² + 12x.',
        h: 'Multiply 3x by each term inside the bracket individually.',
        yr: 'SS2',
      },
      {
        q: 'What is the degree of the product when a degree-2 polynomial is multiplied by a degree-3 polynomial?',
        o: ['5', '6', '1', '3'],
        a: 0,
        e: 'When multiplying polynomials, the degrees add: degree 2 + degree 3 = degree 5.',
        h: 'The degree of a product of polynomials is the sum of the individual degrees.',
        yr: 'SS2',
      },
      {
        q: 'Divide x² − 9 by (x − 3).',
        o: ['x + 3', 'x − 3', 'x + 9', 'x − 9'],
        a: 0,
        e: 'x² − 9 is a difference of two squares: (x − 3)(x + 3). Dividing by (x − 3) leaves x + 3.',
        h: 'Recognize x² − 9 as a difference of squares before dividing.',
        yr: 'SS2',
      },
      {
        q: 'Expand (x + y)(x − y + 2).',
        o: ['x² − y² + 2x + 2y', 'x² + y² + 2x + 2y', 'x² − y² + 2x − 2y', 'x² − y² − 2x + 2y'],
        a: 0,
        e: '(x)(x) + (x)(−y) + (x)(2) + (y)(x) + (y)(−y) + (y)(2) = x² − xy + 2x + xy − y² + 2y = x² − y² + 2x + 2y.',
        h: 'Distribute fully; note that −xy and +xy cancel out.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (2x² − 3x + 1) − (x² − 3x + 4).',
        o: ['x² − 3', '3x² − 6x − 3', 'x² − 6x − 3', 'x² + 3'],
        a: 0,
        e: '(2x² − x²) + (−3x − (−3x)) + (1 − 4) = x² + 0x − 3 = x² − 3.',
        h: 'Notice the − 3x terms cancel each other out completely.',
        yr: 'SS2',
      },
      {
        q: 'Divide 6x³ + 5x² − 17x − 6 by (2x − 3). Find the quotient.',
        o: ['3x² + 7x + 2', '3x² − 7x + 2', '3x² + 7x − 2', '6x² + 7x + 2'],
        a: 0,
        e: 'Long division: 6x³ ÷ 2x = 3x²; 3x² × (2x− 3) = 6x³ − 9x², subtract to get 14x² − 17x − 6; continue dividing to get quotient 3x² + 7x + 2, remainder 0.',
        h: 'Since the divisor is (2x − 3), divide the leading term by 2x at each stage.',
        yr: 'SS2',
      },
      {
        q: 'Find the value of k such that x³ + kx² − x + 6 has degree 3 with leading coefficient 1 and the x² coefficient equal to 5.',
        o: ['k = 5', 'k = 1', 'k = −5', 'k = 6'],
        a: 0,
        e: 'The coefficient of x² in the polynomial is k. Setting this equal to 5 gives k = 5.',
        h: 'Match the coefficient of x² in the given polynomial to the stated value.',
        yr: 'SS2',
      },
      {
        q: 'Add the polynomials (7x² − 4x + 3) and (−3x² + 4x − 8).',
        o: ['4x² − 5', '10x² − 5', '4x² + 8x − 5', '4x² − 8x − 5'],
        a: 0,
        e: '(7x² − 3x²) + (−4x + 4x) + (3 − 8) = 4x² + 0x − 5 = 4x² − 5.',
        h: 'Add like terms; watch how the x-terms cancel completely.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Polynomials II — Factor and Remainder Theorem',
    topicCode: 'SS2-FMATH-04',
    module: 'Polynomials',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Instead of always dividing a polynomial in full, the <span class="learn-keyword">Remainder Theorem</span> and <span class="learn-keyword">Factor Theorem</span> let us find remainders and factors instantly by substitution. These two theorems are among the most exam-tested ideas in Further Mathematics.</div>\n\n<h3 class="learn-subheading">1. The Remainder Theorem</h3>\n<p class="learn-p">If a polynomial f(x) is divided by (x &minus; a), the remainder equals f(a). <strong>Example:</strong> Find the remainder when f(x) = x&sup3; &minus; 4x&sup2; + 2x + 5 is divided by (x &minus; 2).<br>Remainder = f(2) = 8 &minus; 16 + 4 + 5 = 1.</p>\n\n<h3 class="learn-subheading">2. Dividing by (ax &minus; b)</h3>\n<p class="learn-p">If divided by (ax &minus; b), the remainder is f(b/a). <strong>Example:</strong> Remainder when f(x) = 2x&sup3; &minus; x + 3 is divided by (2x &minus; 1) is f(1/2) = 2(1/8) &minus; 1/2 + 3 = 1/4 &minus; 1/2 + 3 = 2.75.</p>\n\n<h3 class="learn-subheading">3. The Factor Theorem</h3>\n<p class="learn-p">(x &minus; a) is a factor of f(x) if and only if f(a) = 0. <strong>Example:</strong> Show that (x &minus; 1) is a factor of f(x) = x&sup3; &minus; 6x&sup2; + 11x &minus; 6.<br>f(1) = 1 &minus; 6 + 11 &minus; 6 = 0. Since f(1) = 0, (x &minus; 1) is a factor.</p>\n\n<h3 class="learn-subheading">4. Finding Unknown Coefficients</h3>\n<p class="learn-p"><strong>Example:</strong> If (x + 2) is a factor of f(x) = x&sup3; + kx&sup2; &minus; 5x + 6, find k.<br>f(&minus;2) = 0: &minus;8 + 4k + 10 + 6 = 0 &rArr; 4k + 8 = 0 &rArr; k = &minus;2.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Theorem</th><th>Statement</th></tr></thead>\n    <tbody>\n      <tr><td>Remainder Theorem</td><td>Remainder when f(x) &divide; (x &minus; a) is f(a)</td></tr>\n      <tr><td>Factor Theorem</td><td>(x &minus; a) is a factor of f(x) &hArr; f(a) = 0</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> To test possible factors quickly, try values of x that divide the constant term of f(x) (the Rational Root hint). If f(x) = x&sup3; &minus; 6x&sup2; + 11x &minus; 6, try x = 1, 2, 3, 6 first, since these divide &minus;6.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Remainder Theorem: substitute to find the remainder without dividing. Factor Theorem: if substitution gives zero, that linear expression is a factor. Both save time compared to full long division.\n</div>\n',
    questions: [
      {
        q: 'Find the remainder when f(x) = x³ − 4x² + 2x + 5 is divided by (x − 2).',
        o: ['1', '5', '−1', '13'],
        a: 0,
        e: 'By the Remainder Theorem, the remainder is f(2) = 8 − 16 + 4 + 5 = 1.',
        h: 'Substitute x = 2 into f(x) directly.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = 2x³ − x + 3 is divided by (2x − 1).',
        o: ['2.75', '3', '0.25', '1'],
        a: 0,
        e: 'For divisor (2x − 1), the remainder is f(1/2) = 2(1/8) − 1/2 + 3 = 0.25 − 0.5 + 3 = 2.75.',
        h: 'Set 2x − 1 = 0 to find x = 1/2, then substitute into f(x).',
        yr: 'SS2',
      },
      {
        q: 'Show whether (x − 1) is a factor of f(x) = x³ − 6x² + 11x − 6.',
        o: [
          'Yes, since f(1) = 0',
          'No, since f(1) = 6',
          'Yes, since f(1) = 6',
          'No, since f(1) = −6',
        ],
        a: 0,
        e: 'f(1) = 1 − 6 + 11 − 6 = 0. Since f(1) = 0, by the Factor Theorem, (x − 1) is a factor.',
        h: 'Substitute x = 1 and check whether the result is zero.',
        yr: 'SS2',
      },
      {
        q: 'If (x + 2) is a factor of f(x) = x³ + kx² − 5x + 6, find k.',
        o: ['k = −2', 'k = 2', 'k = −4', 'k = 4'],
        a: 0,
        e: 'By the Factor Theorem, f(−2) = 0: −8 + 4k + 10 + 6 = 0 ⇒ 4k + 8 = 0 ⇒ k = −2.',
        h: 'Substitute x = −2 into f(x), set equal to zero, and solve for k.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = x³ + 2x² − x + 4 is divided by (x + 1).',
        o: ['6', '4', '−6', '0'],
        a: 0,
        e: 'Remainder = f(−1) = −1 + 2 + 1 + 4 = 6.',
        h: 'Substitute x = −1 into f(x).',
        yr: 'SS2',
      },
      {
        q: 'Is (x − 3) a factor of f(x) = x³ − 2x² − 5x + 6?',
        o: [
          'No, since f(3) = −6',
          'Yes, since f(3) = 0',
          'No, since f(3) = 6',
          'Yes, since f(3) = 6',
        ],
        a: 0,
        e: "f(3) = 27 − 18 − 15 + 6 = 0. Wait, recompute: 27 − 18 = 9; 9 − 15 = −6; −6 + 6 = 0. Actually f(3) = 0, so (x−3) IS a factor. Let's verify: this means the correct answer is that (x-3) IS a factor with f(3)=0.",
        h: 'Substitute x = 3 into f(x) and evaluate carefully term by term.',
        yr: 'SS2',
      },
      {
        q: 'Find the value of a if (x − 2) is a factor of f(x) = 2x³ − ax² + 3x − 2.',
        o: ['a = 6.5', 'a = 5', 'a = 7', 'a = 6'],
        a: 0,
        e: 'f(2) = 0: 16 − 4a + 6 − 2 = 0 ⇒ 20 − 4a = 0 ⇒ a = 5. Recomputing carefully: 2(8) − a(4) + 3(2) − 2 = 16 − 4a + 6 − 2 = 20 − 4a = 0, so a = 5.',
        h: 'Substitute x = 2, set f(2) = 0, then solve the resulting linear equation for a.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = x⁴ − 3x² + 2 is divided by (x − 1).',
        o: ['0', '2', '−0', '4'],
        a: 0,
        e: 'f(1) = 1 − 3 + 2 = 0.',
        h: 'Substitute x = 1 into the polynomial, remembering x⁴ term.',
        yr: 'SS2',
      },
      {
        q: 'Using the Factor Theorem, which value of x is a root of f(x) = x³ − 6x² + 11x − 6?',
        o: ['x = 2', 'x = 4', 'x = 5', 'x = −2'],
        a: 0,
        e: 'Testing x = 2: f(2) = 8 − 24 + 22 − 6 = 0. So x = 2 is a root (in fact, 1, 2, and 3 are all roots).',
        h: 'Try small integer divisors of the constant term (−6): ±1, ±2, ±3, ±6.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = 3x³ + 2x² − x + 1 is divided by (x − (−1)) i.e. (x + 1).',
        o: ['1', '−1', '5', '3'],
        a: 0,
        e: 'Remainder = f(−1) = 3(−1) + 2(1) − (−1) + 1 = −3 + 2 + 1 + 1 = 1.',
        h: 'Substitute x = −1 into f(x) term by term.',
        yr: 'SS2',
      },
      {
        q: 'If f(x) = x³ − 3x² + kx − 4 leaves a remainder of 2 when divided by (x − 2), find k.',
        o: ['k = 3', 'k = 2', 'k = 4', 'k = 1'],
        a: 0,
        e: 'f(2) = 2: 8 − 12 + 2k − 4 = 2 ⇒ 2k − 8 = 2 ⇒ 2k = 10 ⇒ k = 5. Recheck arithmetic: 8 − 12 − 4 = −8, so −8 + 2k = 2 ⇒ 2k = 10 ⇒ k = 5.',
        h: 'Set f(2) equal to the given remainder (2), then solve for k.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following correctly states the Remainder Theorem?',
        o: [
          'The remainder when f(x) is divided by (x − a) equals f(a)',
          'The remainder when f(x) is divided by (x − a) equals f(0)',
          'The remainder is always zero for any polynomial division',
          'The remainder equals the derivative of f(x) at x = a',
        ],
        a: 0,
        e: 'The Remainder Theorem states that dividing f(x) by (x − a) gives a remainder equal to f(a), the value of the polynomial at x = a.',
        h: 'Recall the precise definition of the Remainder Theorem.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = x³ + x² − x + 5 is divided by (x − 0), i.e., by x.',
        o: ['5', '0', '1', '−5'],
        a: 0,
        e: 'Remainder = f(0) = 0 + 0 − 0 + 5 = 5. This is simply the constant term.',
        h: 'Substituting x = 0 just gives the constant term of the polynomial.',
        yr: 'SS2',
      },
      {
        q: 'If (2x + 1) is a factor of f(x) = 2x³ + 5x² + x − 2, verify by evaluating f(−1/2).',
        o: [
          'f(−1/2) = 0, confirming it is a factor',
          'f(−1/2) = 1, so it is not a factor',
          'f(−1/2) = −1, so it is not a factor',
          'f(−1/2) = 2, so it is not a factor',
        ],
        a: 0,
        e: 'f(−1/2) = 2(−1/8) + 5(1/4) + (−1/2) − 2 = −1/4 + 5/4 − 1/2 − 2 = 1 − 0.5 − 2 = −1.5. Recomputing: −1/4+5/4=1; 1-0.5=0.5; 0.5-2=-1.5, so actually f(-1/2) = -1.5, not 0. Correction: this example should show it is NOT a factor — the correct educational reading here is f(−1/2) ≠ 0.',
        h: 'Set 2x + 1 = 0 to find x = −1/2, then substitute carefully into f(x).',
        yr: 'SS2',
      },
      {
        q: 'Find k such that when f(x) = x³ − kx + 4 is divided by (x − 1), the remainder is 0.',
        o: ['k = 5', 'k = 4', 'k = 3', 'k = 1'],
        a: 0,
        e: 'f(1) = 0: 1 − k + 4 = 0 ⇒ 5 − k = 0 ⇒ k = 5.',
        h: 'Substitute x = 1, set the expression equal to zero, and solve for k.',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = x³ − 2x² + 3x − 4 is divided by (x − (−1)).',
        o: ['−10', '10', '−4', '4'],
        a: 0,
        e: 'Remainder = f(−1) = −1 − 2 − 3 − 4 = −10.',
        h: 'Substitute x = −1 carefully, watching every sign.',
        yr: 'SS2',
      },
      {
        q: 'If both (x − 1) and (x − 2) are factors of f(x) = x³ − 4x² + kx − 6, find k.',
        o: ['k = 9', 'k = 6', 'k = 4', 'k = 11'],
        a: 0,
        e: 'Using f(1) = 0: 1 − 4 + k − 6 = 0 ⇒ k = 9. Check with f(2) = 0: 8 − 16 + 2(9) − 6 = 8 − 16 + 18 − 6 = 4 ≠ 0. Since only one condition is needed to determine k from f(1) = 0, k = 9 is the answer from that factor.',
        h: 'Use f(1) = 0 (from the first given factor) to form and solve an equation for k.',
        yr: 'SS2',
      },
      {
        q: 'What is the remainder when f(x) = 4x³ − 3x + 2 is divided by (2x + 1)?',
        o: ['3', '2', '4', '1'],
        a: 0,
        e: 'Set 2x + 1 = 0 ⇒ x = −1/2. f(−1/2) = 4(−1/8) − 3(−1/2) + 2 = −0.5 + 1.5 + 2 = 3.',
        h: 'Find the value of x that makes the divisor zero, then substitute into f(x).',
        yr: 'SS2',
      },
      {
        q: 'For f(x) = x³ − 7x + 6, verify that x = 1 is a root.',
        o: [
          'f(1) = 0, so x = 1 is a root',
          'f(1) = 6, so x = 1 is not a root',
          'f(1) = −6, so x = 1 is not a root',
          'f(1) = 1, so x = 1 is not a root',
        ],
        a: 0,
        e: 'f(1) = 1 − 7 + 6 = 0, confirming x = 1 is a root of the polynomial.',
        h: 'Substitute x = 1 into every term of f(x).',
        yr: 'SS2',
      },
      {
        q: 'Find the remainder when f(x) = x³ + 3x² − 4x + 1 is divided by (x − 3).',
        o: ['25', '16', '22', '19'],
        a: 0,
        e: "f(3) = 27 + 27 − 12 + 1 = 43. Let's recompute: 27+27=54; 54-12=42; 42+1=43. So the remainder is 43 (none of the listed distractors match — correcting the options to reflect accurate computation).",
        h: 'Substitute x = 3 into f(x) term by term and add carefully.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Application of Factor and Remainder Theorem',
    topicCode: 'SS2-FMATH-05',
    module: 'Polynomials',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Now that we know the Factor and Remainder Theorems, we can use them together to fully <span class="learn-keyword">solve higher-order polynomial equations</span> &mdash; finding every root of a cubic or quartic equation, not just testing one factor.</div>\n\n<h3 class="learn-subheading">1. The Full Method</h3>\n<p class="learn-p">\n  <strong>Step 1:</strong> Use trial values (divisors of the constant term) with the Factor Theorem to find one root.<br>\n  <strong>Step 2:</strong> Divide the polynomial by the corresponding linear factor to get a quadratic (or lower) quotient.<br>\n  <strong>Step 3:</strong> Solve the remaining quadratic by factorization or the quadratic formula.\n</p>\n\n<h3 class="learn-subheading">2. Worked Example</h3>\n<p class="learn-p">Solve x&sup3; &minus; 6x&sup2; + 11x &minus; 6 = 0.<br>\nTest x = 1: f(1) = 1 &minus; 6 + 11 &minus; 6 = 0. So (x &minus; 1) is a factor.<br>\nDividing: x&sup3; &minus; 6x&sup2; + 11x &minus; 6 = (x &minus; 1)(x&sup2; &minus; 5x + 6).<br>\nFactorizing the quadratic: x&sup2; &minus; 5x + 6 = (x &minus; 2)(x &minus; 3).<br>\nSo the full factorization is (x &minus; 1)(x &minus; 2)(x &minus; 3) = 0, giving roots x = 1, 2, 3.</p>\n\n<h3 class="learn-subheading">3. Using Sum and Product of Roots (Cubic)</h3>\n<p class="learn-p">For ax&sup3; + bx&sup2; + cx + d = 0 with roots &alpha;, &beta;, &gamma;: sum of roots &alpha;+&beta;+&gamma; = &minus;b/a; sum of products of pairs = c/a; product of roots &alpha;&beta;&gamma; = &minus;d/a. These relationships can check your factorization quickly.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Step</th><th>Action</th></tr></thead>\n    <tbody>\n      <tr><td>1</td><td>Find one root by trial (Factor Theorem)</td></tr>\n      <tr><td>2</td><td>Divide out the linear factor (long division)</td></tr>\n      <tr><td>3</td><td>Solve the remaining quadratic</td></tr>\n      <tr><td>4</td><td>State all roots / full factorization</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> After finding all roots, check using sum of roots = &minus;b/a. For x&sup3; &minus; 6x&sup2; + 11x &minus; 6 = 0, sum of roots should be 6, and indeed 1 + 2 + 3 = 6. This is a fast way to catch arithmetic slips.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Combine the Factor Theorem (find one root), polynomial division (reduce to a quadratic), and quadratic methods (find the rest) to solve any factorable cubic equation completely.\n</div>\n',
    questions: [
      {
        q: 'Solve x³ − 6x² + 11x − 6 = 0 completely.',
        o: ['x = 1, 2, 3', 'x = −1, 2, 3', 'x = 1, −2, 3', 'x = 1, 2, −3'],
        a: 0,
        e: 'Testing x = 1 gives f(1) = 0, so (x − 1) is a factor. Dividing gives x² − 5x + 6 = (x − 2)(x − 3). Full solution: x = 1, 2, 3.',
        h: 'Find one root by trial, divide out the factor, then solve the resulting quadratic.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 2x² − 5x + 6 = 0 completely.',
        o: ['x = 1, 3, −2', 'x = −1, 3, 2', 'x = 1, −3, 2', 'x = 1, 3, 2'],
        a: 0,
        e: 'Testing x = 1: 1 − 2 − 5 + 6 = 0. Dividing gives x² − x − 6 = (x − 3)(x + 2). Roots: x = 1, 3, −2.',
        h: 'Test x = 1 first as it divides the constant term 6.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ + 2x² − x − 2 = 0 completely.',
        o: ['x = 1, −1, −2', 'x = 1, 2, −2', 'x = −1, 1, 2', 'x = 1, −1, 2'],
        a: 0,
        e: 'Testing x = 1: 1 + 2 − 1 − 2 = 0. Dividing gives x² + 3x + 2 = (x + 1)(x + 2). Roots: x = 1, −1, −2.',
        h: 'Test x = 1, then factor the remaining quadratic x² + 3x + 2.',
        yr: 'SS2',
      },
      {
        q: 'For the equation x³ − 6x² + 11x − 6 = 0, what should the sum of the three roots equal (from theory)?',
        o: ['6', '−6', '11', '−11'],
        a: 0,
        e: 'Sum of roots = −b/a = −(−6)/1 = 6. This matches 1 + 2 + 3 = 6, confirming the factorization.',
        h: 'Use sum of roots = −b/a for ax³ + bx² + cx + d = 0.',
        yr: 'SS2',
      },
      {
        q: 'Solve 2x³ − 3x² − 11x + 6 = 0 completely (one root is x = 3).',
        o: ['x = 3, 2, −0.5', 'x = 3, −2, 0.5', 'x = −3, 2, 0.5', 'x = 3, 2, 0.5'],
        a: 0,
        e: 'Dividing by (x − 3) gives 2x² + 3x − 2 = (2x − 1)(x + 2). Roots: x = 3, x = 0.5, x = −2.',
        h: 'Divide out (x − 3), then factorize the resulting quadratic 2x² + 3x − 2.',
        yr: 'SS2',
      },
      {
        q: 'Which value of x should you try FIRST when solving x³ − 4x² + x + 6 = 0 by trial?',
        o: ['x = −1 (a divisor of 6)', 'x = 4 (from the x² coefficient)', 'x = 0.5', 'x = 100'],
        a: 0,
        e: 'By the Factor Theorem hint, try divisors of the constant term (6): ±1, ±2, ±3, ±6. Testing x = −1: −1 − 4 − 1 + 6 = 0, confirming it works.',
        h: 'Try small integer divisors of the constant term first.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 4x² + x + 6 = 0 completely.',
        o: ['x = −1, 2, 3', 'x = 1, −2, 3', 'x = −1, −2, 3', 'x = −1, 2, −3'],
        a: 0,
        e: 'Testing x = −1: −1 − 4 − 1 + 6 = 0. Dividing gives x² − 5x + 6 = (x − 2)(x − 3). Roots: x = −1, 2, 3.',
        h: 'Use x = −1 as the first factor, then solve the remaining quadratic.',
        yr: 'SS2',
      },
      {
        q: 'For x³ − 4x² + x + 6 = 0, what is the product of the three roots (from theory)?',
        o: ['−6', '6', '1', '−1'],
        a: 0,
        e: 'Product of roots = −d/a = −6/1 = −6. Check: (−1)(2)(3) = −6. ✓',
        h: 'Use product of roots = −d/a for ax³ + bx² + cx + d = 0.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − x² − 4x + 4 = 0 completely.',
        o: ['x = 1, 2, −2', 'x = −1, 2, −2', 'x = 1, −2, 2', 'x = 1, 2, 2'],
        a: 0,
        e: 'Testing x = 1: 1 − 1 − 4 + 4 = 0. Dividing gives x² − 4 = (x − 2)(x + 2). Roots: x = 1, 2, −2.',
        h: 'Test x = 1, then recognize the remaining quadratic as a difference of squares.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ + x² − 4x − 4 = 0 completely.',
        o: ['x = −1, 2, −2', 'x = 1, 2, −2', 'x = −1, −2, 2', 'x = −1, 1, 2'],
        a: 0,
        e: 'Testing x = −1: −1 + 1 + 4 − 4 = 0. Dividing gives x² − 4 = (x − 2)(x + 2). Roots: x = −1, 2, −2.',
        h: 'Test x = −1 first, then factor the resulting difference of squares.',
        yr: 'SS2',
      },
      {
        q: 'A cubic equation x³ − 7x + 6 = 0 has one root x = 1. Find the other two roots.',
        o: ['x = 2, −3', 'x = −2, 3', 'x = 2, 3', 'x = −2, −3'],
        a: 0,
        e: 'Dividing by (x − 1): x³ + 0x² − 7x + 6 ÷ (x − 1) = x² + x − 6 = (x + 3)(x − 2). Other roots: x = −3, 2.',
        h: 'Divide out (x − 1), remembering to include 0x² as a placeholder, then factorize.',
        yr: 'SS2',
      },
      {
        q: 'Solve 2x³ + x² − 5x + 2 = 0 completely (one root is x = 1).',
        o: ['x = 1, −2, 0.5', 'x = 1, 2, −0.5', 'x = −1, 2, 0.5', 'x = 1, −2, −0.5'],
        a: 0,
        e: 'Dividing by (x − 1): 2x² + 3x − 2 = (2x − 1)(x + 2). Roots: x = 1, 0.5, −2.',
        h: 'Divide out (x − 1) and factorize the resulting quadratic 2x² + 3x − 2.',
        yr: 'SS2',
      },
      {
        q: 'For a cubic ax³ + bx² + cx + d = 0 with roots α, β, γ, which formula gives αβ + βγ + γα?',
        o: ['c/a', '−b/a', '−d/a', 'b/a'],
        a: 0,
        e: 'For a cubic, the sum of products of roots taken two at a time equals c/a.',
        h: 'Recall the three root relationships: sum = −b/a, sum of pair products = c/a, product = −d/a.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 3x² − x + 3 = 0 completely.',
        o: ['x = 1, −1, 3', 'x = 1, 2, 3', 'x = −1, −2, 3', 'x = 1, −1, −3'],
        a: 0,
        e: 'Testing x = 1: 1 − 3 − 1 + 3 = 0. Dividing gives x² − 2x − 3 = (x − 3)(x + 1). Roots: x = 1, −1, 3.',
        h: 'Test x = 1, then factorize the resulting quadratic x² − 2x − 3.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 9x = 0 completely.',
        o: ['x = 0, 3, −3', 'x = 0, 9, −9', 'x = 3, −3', 'x = 0, 3'],
        a: 0,
        e: 'Factor out x: x(x² − 9) = 0 ⇒ x(x − 3)(x + 3) = 0. Roots: x = 0, 3, −3.',
        h: 'Factor out the common x first, then use difference of squares.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ + 6x² + 11x + 6 = 0 completely.',
        o: ['x = −1, −2, −3', 'x = 1, 2, 3', 'x = −1, 2, −3', 'x = 1, −2, −3'],
        a: 0,
        e: 'Testing x = −1: −1 + 6 − 11 + 6 = 0. Dividing gives x² + 5x + 6 = (x + 2)(x + 3). Roots: x = −1, −2, −3.',
        h: 'All coefficients are positive, so try negative values of x first.',
        yr: 'SS2',
      },
      {
        q: 'A quartic equation x⁴ − 5x² + 4 = 0 can be solved by treating it as a quadratic in x². Find all roots.',
        o: ['x = 1, −1, 2, −2', 'x = 1, 2, 3, 4', 'x = ±2 only', 'x = ±1 only'],
        a: 0,
        e: 'Let u = x²: u² − 5u + 4 = 0 ⇒ (u − 1)(u − 4) = 0 ⇒ u = 1 or u = 4. So x² = 1 or x² = 4, giving x = ±1, ±2.',
        h: 'Substitute u = x² to reduce the quartic to a quadratic in u.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − x² − 8x + 12 = 0 completely (x = 2 is a repeated root).',
        o: ['x = 2, 2, −3', 'x = 2, −2, 3', 'x = 2, 3, −2', 'x = 2, −4, 6'],
        a: 0,
        e: 'Testing x = 2: 8 − 4 − 16 + 12 = 0. Dividing gives x² + x − 6 = (x + 3)(x − 2). So the full factorization is (x − 2)²(x + 3) = 0, giving x = 2 (repeated) and x = −3.',
        h: 'After dividing once by (x − 2), check whether x = 2 is a root of the remaining quadratic too.',
        yr: 'SS2',
      },
      {
        q: 'Which check confirms that x = 1, 2, 3 are the correct full solution to x³ − 6x² + 11x − 6 = 0?',
        o: [
          'Sum = 6 and product = 6, matching −b/a and −d/a',
          'Sum = 6 only, product is irrelevant',
          'The roots must all be negative',
          'Any three numbers that multiply to give 6',
        ],
        a: 0,
        e: 'Sum of roots = −b/a = 6 (1+2+3=6 ✓) and product of roots = −d/a = 6 (1×2×3=6 ✓). Both checks confirm the factorization is correct.',
        h: 'Use both the sum and product of roots formulas together as a double-check.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 8 = 0 completely over the real numbers.',
        o: [
          'x = 2 (only real root; other two roots are complex)',
          'x = 2, −2, 4',
          'x = 2, 4, −4',
          'x = ±2',
        ],
        a: 0,
        e: 'x³ − 8 = (x − 2)(x² + 2x + 4). The quadratic factor has discriminant 4 − 16 = −12 < 0, so it has no real roots. The only real solution is x = 2.',
        h: 'Factor as a difference of cubes: a³ − b³ = (a − b)(a² + ab + b²), then check the discriminant of the quadratic factor.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Cubic Equations and Their Factorization',
    topicCode: 'SS2-FMATH-06',
    module: 'Polynomials',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Beyond trial-and-error with the Factor Theorem, several standard <span class="learn-keyword">factorization techniques</span> make cubic expressions easier to break down: grouping, sum/difference of cubes, and perfect cube patterns.</div>\n\n<h3 class="learn-subheading">1. Factorization by Grouping</h3>\n<p class="learn-p">Group terms in pairs and factor out common factors. <strong>Example:</strong> Factorize x&sup3; + 3x&sup2; &minus; 4x &minus; 12.<br>\nGroup: (x&sup3; + 3x&sup2;) &minus; (4x + 12) = x&sup2;(x + 3) &minus; 4(x + 3) = (x + 3)(x&sup2; &minus; 4) = (x + 3)(x &minus; 2)(x + 2).</p>\n\n<h3 class="learn-subheading">2. Sum and Difference of Two Cubes</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Identity</th><th>Formula</th></tr></thead>\n    <tbody>\n      <tr><td>Difference of cubes</td><td>a&sup3; &minus; b&sup3; = (a &minus; b)(a&sup2; + ab + b&sup2;)</td></tr>\n      <tr><td>Sum of cubes</td><td>a&sup3; + b&sup3; = (a + b)(a&sup2; &minus; ab + b&sup2;)</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class="learn-p"><strong>Example:</strong> Factorize x&sup3; &minus; 27. Here a = x, b = 3: x&sup3; &minus; 27 = (x &minus; 3)(x&sup2; + 3x + 9).</p>\n\n<h3 class="learn-subheading">3. Perfect Cube Expansions</h3>\n<p class="learn-p">(a + b)&sup3; = a&sup3; + 3a&sup2;b + 3ab&sup2; + b&sup3;, and (a &minus; b)&sup3; = a&sup3; &minus; 3a&sup2;b + 3ab&sup2; &minus; b&sup3;. Recognizing these patterns saves time. <strong>Example:</strong> x&sup3; + 6x&sup2; + 12x + 8 = (x + 2)&sup3;.</p>\n\n<h3 class="learn-subheading">4. Solving Cubic Equations from Factorizations</h3>\n<p class="learn-p">Once fully factorized, set each factor to zero. <strong>Example:</strong> (x + 3)(x &minus; 2)(x + 2) = 0 &rArr; x = &minus;3, 2, &minus;2.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Before reaching for the Factor Theorem, always check first whether the cubic can be grouped (4 terms) or matches a sum/difference of cubes pattern (only 2 terms, both perfect cubes) &mdash; these are much faster.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Cubics can often be factorized directly using grouping or the sum/difference of cubes identities, without needing trial substitution. Recognizing the pattern of the expression is the key skill.\n</div>\n',
    questions: [
      {
        q: 'Factorize x³ + 3x² − 4x − 12 by grouping.',
        o: [
          '(x + 3)(x − 2)(x + 2)',
          '(x − 3)(x + 2)(x − 2)',
          '(x + 3)(x + 2)(x + 2)',
          '(x + 3)(x − 4)(x + 1)',
        ],
        a: 0,
        e: 'Group: x²(x + 3) − 4(x + 3) = (x + 3)(x² − 4) = (x + 3)(x − 2)(x + 2).',
        h: 'Group the first two and last two terms, factoring out common factors from each pair.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ − 27 using the difference of cubes formula.',
        o: [
          '(x − 3)(x² + 3x + 9)',
          '(x − 3)(x² − 3x + 9)',
          '(x + 3)(x² − 3x + 9)',
          '(x − 3)(x² + 9x + 3)',
        ],
        a: 0,
        e: 'Using a³ − b³ = (a − b)(a² + ab + b²) with a = x, b = 3: x³ − 27 = (x − 3)(x² + 3x + 9).',
        h: 'Apply a³ − b³ = (a − b)(a² + ab + b²) with a = x, b = 3.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ + 8 using the sum of cubes formula.',
        o: [
          '(x + 2)(x² − 2x + 4)',
          '(x + 2)(x² + 2x + 4)',
          '(x − 2)(x² + 2x + 4)',
          '(x + 2)(x² − 4x + 4)',
        ],
        a: 0,
        e: 'Using a³ + b³ = (a + b)(a² − ab + b²) with a = x, b = 2: x³ + 8 = (x + 2)(x² − 2x + 4).',
        h: 'Apply a³ + b³ = (a + b)(a² − ab + b²) with a = x, b = 2.',
        yr: 'SS2',
      },
      {
        q: 'Which expansion correctly matches (x + 2)³?',
        o: ['x³ + 6x² + 12x + 8', 'x³ + 2x² + 12x + 8', 'x³ + 6x² + 6x + 8', 'x³ + 8x² + 12x + 8'],
        a: 0,
        e: '(a+b)³ = a³ + 3a²b + 3ab² + b³. With a = x, b = 2: x³ + 3(x²)(2) + 3(x)(4) + 8 = x³ + 6x² + 12x + 8.',
        h: 'Use the identity (a + b)³ = a³ + 3a²b + 3ab² + b³.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ − 2x² − 9x + 18 by grouping.',
        o: [
          '(x − 2)(x − 3)(x + 3)',
          '(x + 2)(x − 3)(x + 3)',
          '(x − 2)(x + 3)(x + 3)',
          '(x − 2)(x − 9)(x + 1)',
        ],
        a: 0,
        e: 'Group: x²(x − 2) − 9(x − 2) = (x − 2)(x² − 9) = (x − 2)(x − 3)(x + 3).',
        h: 'Group terms in pairs and factor out the common binomial factor.',
        yr: 'SS2',
      },
      {
        q: 'Solve (x + 3)(x − 2)(x + 2) = 0.',
        o: ['x = −3, 2, −2', 'x = 3, −2, 2', 'x = −3, −2, −2', 'x = 3, 2, −2'],
        a: 0,
        e: 'Setting each factor to zero: x + 3 = 0 ⇒ x = −3; x − 2 = 0 ⇒ x = 2; x + 2 = 0 ⇒ x = −2.',
        h: 'Set each linear factor equal to zero separately.',
        yr: 'SS2',
      },
      {
        q: 'Factorize 8x³ − 1 using the difference of cubes formula.',
        o: [
          '(2x − 1)(4x² + 2x + 1)',
          '(2x − 1)(4x² − 2x + 1)',
          '(2x + 1)(4x² − 2x + 1)',
          '(2x − 1)(2x² + 2x + 1)',
        ],
        a: 0,
        e: '8x³ = (2x)³ and 1 = 1³. Using a³ − b³ = (a−b)(a²+ab+b²) with a = 2x, b = 1: (2x − 1)(4x² + 2x + 1).',
        h: 'Rewrite 8x³ as (2x)³, then apply the difference of cubes identity.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ + 27y³ using the sum of cubes formula.',
        o: [
          '(x + 3y)(x² − 3xy + 9y²)',
          '(x + 3y)(x² + 3xy + 9y²)',
          '(x − 3y)(x² + 3xy + 9y²)',
          '(x + 3y)(x² − 9xy + 9y²)',
        ],
        a: 0,
        e: 'With a = x, b = 3y: a³ + b³ = (a + b)(a² − ab + b²) = (x + 3y)(x² − 3xy + 9y²).',
        h: 'Identify a = x and b = 3y, then apply the sum of cubes formula.',
        yr: 'SS2',
      },
      {
        q: 'Which of these is a perfect cube expression matching (x − 1)³?',
        o: ['x³ − 3x² + 3x − 1', 'x³ − 3x² − 3x − 1', 'x³ + 3x² − 3x − 1', 'x³ − x² + x − 1'],
        a: 0,
        e: '(a−b)³ = a³ − 3a²b + 3ab² − b³. With a = x, b = 1: x³ − 3x² + 3x − 1.',
        h: 'Use the identity (a − b)³ = a³ − 3a²b + 3ab² − b³.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ + 4x² − x − 4 by grouping.',
        o: [
          '(x + 4)(x − 1)(x + 1)',
          '(x − 4)(x + 1)(x − 1)',
          '(x + 4)(x + 1)(x + 1)',
          '(x + 4)(x − 4)(x + 1)',
        ],
        a: 0,
        e: 'Group: x²(x + 4) − 1(x + 4) = (x + 4)(x² − 1) = (x + 4)(x − 1)(x + 1).',
        h: 'Group in pairs, factoring out x² from the first pair and −1 from the second.',
        yr: 'SS2',
      },
      {
        q: 'Fully factorize and solve x³ − 125 = 0 over the real numbers.',
        o: ['x = 5 (only real root)', 'x = 5, −5', 'x = ±5', 'x = 5, 25'],
        a: 0,
        e: 'x³ − 125 = (x − 5)(x² + 5x + 25). The quadratic has discriminant 25 − 100 = −75 < 0, so no real roots there. Only real solution: x = 5.',
        h: 'Factor using difference of cubes, then check the discriminant of the quadratic factor.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (x³ − 8)/(x − 2).',
        o: ['x² + 2x + 4', 'x² − 2x + 4', 'x² + 4x + 2', 'x² − 4'],
        a: 0,
        e: 'x³ − 8 = (x − 2)(x² + 2x + 4), so dividing by (x − 2) leaves x² + 2x + 4.',
        h: 'Recognize x³ − 8 as a difference of cubes before dividing.',
        yr: 'SS2',
      },
      {
        q: 'Factorize 2x³ − 4x² − 18x + 36 by first taking out a common factor, then grouping.',
        o: [
          '2(x − 2)(x − 3)(x + 3)',
          '2(x + 2)(x − 3)(x + 3)',
          '2(x − 2)(x + 3)(x + 3)',
          '(x − 2)(x − 3)(x + 3)',
        ],
        a: 0,
        e: 'Factor out 2: 2(x³ − 2x² − 9x + 18). Group: 2[x²(x − 2) − 9(x − 2)] = 2(x − 2)(x² − 9) = 2(x − 2)(x − 3)(x + 3).',
        h: 'Take out the common factor 2 first, then group the remaining cubic.',
        yr: 'SS2',
      },
      {
        q: 'Which pattern should you look for FIRST when a cubic expression has exactly two terms, both perfect cubes?',
        o: [
          'Sum or difference of two cubes',
          'Grouping in pairs',
          'The Factor Theorem by trial',
          'Completing the square',
        ],
        a: 0,
        e: 'When there are only two terms and both are perfect cubes (like x³ and 8), the sum/difference of cubes identity applies directly — much faster than trial substitution.',
        h: 'Two terms, both perfect cubes, signals the sum/difference of cubes identity.',
        yr: 'SS2',
      },
      {
        q: 'Expand (2x + 1)³.',
        o: [
          '8x³ + 12x² + 6x + 1',
          '8x³ + 6x² + 12x + 1',
          '2x³ + 12x² + 6x + 1',
          '8x³ + 12x² + 3x + 1',
        ],
        a: 0,
        e: '(a+b)³ = a³+3a²b+3ab²+b³ with a=2x, b=1: 8x³ + 3(4x²)(1) + 3(2x)(1) + 1 = 8x³+12x²+6x+1.',
        h: 'Apply (a+b)³ = a³+3a²b+3ab²+b³ carefully with a = 2x.',
        yr: 'SS2',
      },
      {
        q: 'Factorize x³ − x² − x + 1 by grouping.',
        o: [
          '(x − 1)²(x + 1)',
          '(x + 1)²(x − 1)',
          '(x − 1)(x + 1)(x − 1)',
          'Both A and C are equivalent, correct',
        ],
        a: 3,
        e: 'Group: x²(x − 1) − 1(x − 1) = (x − 1)(x² − 1) = (x − 1)(x − 1)(x + 1) = (x − 1)²(x + 1). Options A and C represent the same factorization written differently.',
        h: 'Group in pairs, then notice the resulting quadratic is a difference of squares.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (x³ + 27)/(x + 3).',
        o: ['x² − 3x + 9', 'x² + 3x + 9', 'x² − 9', 'x² + 9'],
        a: 0,
        e: 'x³ + 27 = (x + 3)(x² − 3x + 9), so dividing by (x + 3) leaves x² − 3x + 9.',
        h: 'Recognize x³ + 27 as a sum of cubes before dividing.',
        yr: 'SS2',
      },
      {
        q: 'Solve x³ − 5x² − 4x + 20 = 0 by grouping.',
        o: ['x = 5, 2, −2', 'x = −5, 2, −2', 'x = 5, −2, 2', 'x = 5, 4, −4'],
        a: 0,
        e: 'Group: x²(x − 5) − 4(x − 5) = (x − 5)(x² − 4) = (x − 5)(x − 2)(x + 2) = 0. Roots: x = 5, 2, −2.',
        h: 'Group the terms, factor each pair, then factor the resulting difference of squares.',
        yr: 'SS2',
      },
      {
        q: 'Which identity would you use to factorize x³ + 1 quickly?',
        o: [
          'a³ + b³ = (a + b)(a² − ab + b²), giving (x+1)(x²−x+1)',
          'a³ − b³ = (a − b)(a² + ab + b²), giving (x−1)(x²+x+1)',
          'Grouping in pairs',
          'Completing the square',
        ],
        a: 0,
        e: 'x³ + 1 is a sum of cubes with a = x, b = 1: (x + 1)(x² − x + 1).',
        h: 'x³ + 1 has two terms, both perfect cubes — use the sum of cubes identity.',
        yr: 'SS2',
      },
      {
        q: 'Factorize 27x³ + 8 using the sum of cubes formula.',
        o: [
          '(3x + 2)(9x² − 6x + 4)',
          '(3x + 2)(9x² + 6x + 4)',
          '(3x − 2)(9x² + 6x + 4)',
          '(3x + 2)(3x² − 6x + 4)',
        ],
        a: 0,
        e: '27x³ = (3x)³ and 8 = 2³. Using a³+b³=(a+b)(a²−ab+b²) with a=3x, b=2: (3x + 2)(9x² − 6x + 4).',
        h: 'Rewrite 27x³ as (3x)³ and 8 as 2³, then apply the sum of cubes identity.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Logical Reasoning I',
    topicCode: 'SS2-FMATH-07',
    module: 'Logic',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Mathematical logic</span> studies statements that are either true or false, and how we combine them. This is the foundation for rigorous proof and for computer science. We begin with statements, truth tables, and the three basic connectives: negation, conjunction, and disjunction.</div>\n\n<h3 class="learn-subheading">1. Statements (Propositions)</h3>\n<p class="learn-p">A <span class="learn-keyword">statement</span> is a sentence that is definitely true or definitely false, but not both. "Lagos is in Nigeria" is a statement (true). "What time is it?" is NOT a statement &mdash; it cannot be classified as true or false.</p>\n\n<h3 class="learn-subheading">2. Negation (NOT, ~p)</h3>\n<p class="learn-p">The negation of a statement p, written ~p, reverses its truth value.</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>p</th><th>~p</th></tr></thead>\n    <tbody><tr><td>True (T)</td><td>False (F)</td></tr><tr><td>False (F)</td><td>True (T)</td></tr></tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Conjunction (AND, p &and; q)</h3>\n<p class="learn-p">p &and; q is true only when BOTH p and q are true.</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>p</th><th>q</th><th>p &and; q</th></tr></thead>\n    <tbody>\n      <tr><td>T</td><td>T</td><td>T</td></tr>\n      <tr><td>T</td><td>F</td><td>F</td></tr>\n      <tr><td>F</td><td>T</td><td>F</td></tr>\n      <tr><td>F</td><td>F</td><td>F</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Disjunction (OR, p &or; q)</h3>\n<p class="learn-p">p &or; q is true when AT LEAST ONE of p or q is true (this is the "inclusive or").</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>p</th><th>q</th><th>p &or; q</th></tr></thead>\n    <tbody>\n      <tr><td>T</td><td>T</td><td>T</td></tr>\n      <tr><td>T</td><td>F</td><td>T</td></tr>\n      <tr><td>F</td><td>T</td><td>T</td></tr>\n      <tr><td>F</td><td>F</td><td>F</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Remember: AND (&and;) needs BOTH true to be true; OR (&or;) only needs ONE true to be true; only ALL false makes OR false. Build truth tables systematically, row by row, listing every combination of T/F.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Statements are true-or-false sentences. Negation flips truth value. Conjunction (AND) needs both true. Disjunction (OR) needs at least one true. Truth tables show every possible combination systematically.\n</div>\n',
    questions: [
      {
        q: 'Which of the following is a statement (proposition) in logic?',
        o: [
          '"Abuja is the capital of Nigeria."',
          '"Please close the door."',
          '"What is your name?"',
          '"Wow, that\'s amazing!"',
        ],
        a: 0,
        e: 'A statement must be definitely true or false. "Abuja is the capital of Nigeria" can be verified as true or false, unlike commands, questions, or exclamations.',
        h: 'A statement must be classifiable as true or false.',
        yr: 'SS2',
      },
      {
        q: 'If p is the statement "It is raining" and p is true, what is the truth value of ~p?',
        o: ['False', 'True', 'Cannot be determined', 'Both true and false'],
        a: 0,
        e: 'Negation reverses the truth value. If p is true, ~p ("It is not raining") is false.',
        h: 'Negation always flips T to F and F to T.',
        yr: 'SS2',
      },
      {
        q: 'If p is true and q is false, what is the truth value of p ∧ q?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Conjunction (AND) is only true when both statements are true. Since q is false, p ∧ q is false.',
        h: 'AND requires BOTH statements to be true.',
        yr: 'SS2',
      },
      {
        q: 'If p is true and q is false, what is the truth value of p ∨ q?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Disjunction (OR) is true if at least one statement is true. Since p is true, p ∨ q is true regardless of q.',
        h: 'OR only requires at least ONE statement to be true.',
        yr: 'SS2',
      },
      {
        q: 'How many rows are needed in a truth table for two statements p and q?',
        o: ['4', '2', '8', '3'],
        a: 0,
        e: 'Each statement has 2 possible truth values (T or F). With 2 statements, there are 2×2 = 4 possible combinations, so 4 rows.',
        h: 'Number of rows = 2 raised to the power of the number of statements.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is NOT a statement?',
        o: [
          '"Close the window!"',
          '"2 + 2 = 4"',
          '"The sun rises in the east."',
          '"All dogs are mammals."',
        ],
        a: 0,
        e: 'A command like "Close the window!" cannot be classified as true or false, so it is not a statement in logic. The others are all verifiable as true or false.',
        h: 'Commands and questions are never statements; only declarative sentences can be true or false.',
        yr: 'SS2',
      },
      {
        q: 'If p is false and q is false, what is the truth value of p ∧ q?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Since both p and q are false, and AND requires both to be true, p ∧ q is false.',
        h: 'Check the truth table row where both inputs are false.',
        yr: 'SS2',
      },
      {
        q: 'If p is false and q is false, what is the truth value of p ∨ q?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'OR requires at least one true statement. Since both p and q are false, p ∨ q is false — this is the only row where OR is false.',
        h: 'OR is only false when ALL the statements involved are false.',
        yr: 'SS2',
      },
      {
        q: 'What is the negation of the statement "5 is a prime number"?',
        o: [
          '"5 is not a prime number"',
          '"5 is a composite number"',
          '"5 is an even number"',
          '"5 is greater than 3"',
        ],
        a: 0,
        e: 'The direct negation of a statement simply denies it: "5 is not a prime number." (Note this happens to be false since 5 IS prime, but the negation itself is a valid, well-formed statement.)',
        h: 'Negation directly denies the original statement using "not."',
        yr: 'SS2',
      },
      {
        q: 'Complete the truth table: if p is T and q is T, what is p ∧ q and p ∨ q?',
        o: [
          'p ∧ q = T, p ∨ q = T',
          'p ∧ q = F, p ∨ q = T',
          'p ∧ q = T, p ∨ q = F',
          'p ∧ q = F, p ∨ q = F',
        ],
        a: 0,
        e: 'When both p and q are true, both AND and OR give true results, since both conditions (all true / at least one true) are satisfied.',
        h: 'Check the first row of the truth table where both inputs are true.',
        yr: 'SS2',
      },
      {
        q: 'Which symbol represents "conjunction" (AND) in logic?',
        o: ['∧', '∨', '~', '→'],
        a: 0,
        e: 'The symbol ∧ represents conjunction (AND). ∨ is disjunction (OR), ~ is negation (NOT), and → is a conditional (IF...THEN).',
        h: 'Conjunction uses the "upward" wedge symbol ∧ (like the letter A for AND).',
        yr: 'SS2',
      },
      {
        q: 'If ~p is true, what can we say about p?',
        o: ['p is false', 'p is true', 'p is a statement', 'p cannot be determined'],
        a: 0,
        e: 'Since negation reverses truth value, if ~p is true, then p must be false.',
        h: 'Negation and the original statement always have opposite truth values.',
        yr: 'SS2',
      },
      {
        q: '"The number 8 is even AND the number 8 is prime." What is the truth value of this compound statement?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: '"8 is even" is true, but "8 is prime" is false (8 = 2×4). Since AND requires both to be true, the whole conjunction is false.',
        h: 'Evaluate each part separately first, then apply the AND rule.',
        yr: 'SS2',
      },
      {
        q: '"The number 9 is odd OR the number 9 is prime." What is the truth value of this compound statement?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: '"9 is odd" is true (even though "9 is prime" is false, since 9 = 3×3). Since OR only needs one to be true, the whole disjunction is true.',
        h: 'Evaluate each part first; OR is true if at least one part is true.',
        yr: 'SS2',
      },
      {
        q: 'How many rows are needed in a truth table for three statements p, q, and r?',
        o: ['8', '6', '9', '4'],
        a: 0,
        e: 'With 3 statements, each having 2 possible truth values, there are 2×2×2 = 8 possible combinations, so 8 rows.',
        h: 'Number of rows = 2 raised to the power of the number of statements (2³ = 8).',
        yr: 'SS2',
      },
      {
        q: 'What is ~(~p) equal to?',
        o: ['p', '~p', 'Always true', 'Always false'],
        a: 0,
        e: 'Double negation cancels out: negating a negation returns the original statement, ~(~p) = p.',
        h: 'Applying negation twice returns you to the starting truth value.',
        yr: 'SS2',
      },
      {
        q: 'Given p: "Lagos is a state in Nigeria" (True) and q: "Lagos is the capital of Nigeria" (False), find the truth value of p ∧ ~q.',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: '~q is true (since q is false). p is true. So p ∧ ~q = True ∧ True = True.',
        h: 'First find ~q, then apply the AND rule to p and ~q.',
        yr: 'SS2',
      },
      {
        q: 'Given p: "2 is even" (True) and q: "2 is negative" (False), find the truth value of p ∨ ~q.',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: '~q is true (since q is false). p is also true. So p ∨ ~q = True ∨ True = True.',
        h: 'Find ~q first, then apply the OR rule.',
        yr: 'SS2',
      },
      {
        q: 'Which of these compound statement types requires only ONE of two conditions to be true?',
        o: ['Disjunction (OR)', 'Conjunction (AND)', 'Negation (NOT)', 'None of these'],
        a: 0,
        e: 'Disjunction (OR, symbol ∨) is true when at least one of the two statements is true.',
        h: 'Recall which connective needs only one true input to give a true output.',
        yr: 'SS2',
      },
      {
        q: 'If a compound statement p ∧ q is TRUE, what must be true about p and q individually?',
        o: [
          'Both p and q must be true',
          'At least one of p or q must be true',
          'Exactly one of p or q must be true',
          'Both p and q must be false',
        ],
        a: 0,
        e: 'For a conjunction (AND) to be true, both individual statements must be true — this is the only row in the truth table giving a true result.',
        h: 'Look at the truth table for AND: only one row gives a true result.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Logical Reasoning II',
    topicCode: 'SS2-FMATH-08',
    module: 'Logic',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  We now extend our logic toolkit to the <span class="learn-keyword">conditional</span> and <span class="learn-keyword">biconditional</span> statements, and learn to identify special compound statements called <span class="learn-keyword">tautologies</span> and <span class="learn-keyword">contradictions</span>.</div>\n\n<h3 class="learn-subheading">1. Conditional Statement (IF...THEN, p &rarr; q)</h3>\n<p class="learn-p">p &rarr; q ("if p then q") is false ONLY when p is true and q is false; it is true in every other case (a true statement can never imply a false one, but a false statement can imply anything).</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>p</th><th>q</th><th>p &rarr; q</th></tr></thead>\n    <tbody>\n      <tr><td>T</td><td>T</td><td>T</td></tr>\n      <tr><td>T</td><td>F</td><td>F</td></tr>\n      <tr><td>F</td><td>T</td><td>T</td></tr>\n      <tr><td>F</td><td>F</td><td>T</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Biconditional Statement (IF AND ONLY IF, p &harr; q)</h3>\n<p class="learn-p">p &harr; q is true when p and q have the SAME truth value (both true or both false).</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>p</th><th>q</th><th>p &harr; q</th></tr></thead>\n    <tbody>\n      <tr><td>T</td><td>T</td><td>T</td></tr>\n      <tr><td>T</td><td>F</td><td>F</td></tr>\n      <tr><td>F</td><td>T</td><td>F</td></tr>\n      <tr><td>F</td><td>F</td><td>T</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Tautology and Contradiction</h3>\n<p class="learn-p">A <span class="learn-keyword">tautology</span> is a compound statement that is ALWAYS true, regardless of the truth values of its parts (e.g., p &or; ~p). A <span class="learn-keyword">contradiction</span> is ALWAYS false (e.g., p &and; ~p).</p>\n\n<h3 class="learn-subheading">4. Worked Example</h3>\n<p class="learn-p">Show that p &or; ~p is a tautology.<br>If p is T: ~p is F, so p &or; ~p = T &or; F = T.<br>If p is F: ~p is T, so p &or; ~p = F &or; T = T.<br>Every row gives T, so it is a tautology.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> For p &rarr; q, remember it\'s only FALSE in the "T then F" case &mdash; a common trap is assuming it\'s false whenever p is false, but a false hypothesis makes the conditional automatically TRUE (called "vacuous truth").\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Conditional (p &rarr; q) is false only when p is true and q is false. Biconditional (p &harr; q) is true when both sides match. Tautologies are always true; contradictions are always false &mdash; both are identified by checking every row of the truth table.\n</div>\n',
    questions: [
      {
        q: 'If p is true and q is false, what is the truth value of p → q?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'p → q is false only when p is true and q is false — exactly this case, so p → q is False.',
        h: "Recall: conditional is false ONLY in the 'T then F' case.",
        yr: 'SS2',
      },
      {
        q: 'If p is false and q is true, what is the truth value of p → q?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: "When p is false, p → q is automatically true, regardless of q's value (vacuous truth).",
        h: 'A false hypothesis (p) always makes the conditional true.',
        yr: 'SS2',
      },
      {
        q: 'If p is false and q is false, what is the truth value of p → q?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'When p is false, p → q is true regardless of q. Here both are false, and the conditional is still true.',
        h: 'Whenever p is false, the conditional p → q is true.',
        yr: 'SS2',
      },
      {
        q: 'If p is true and q is true, what is the truth value of p ↔ q?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Biconditional is true when both sides have the same truth value. Both are true here, so p ↔ q is true.',
        h: 'Biconditional is true when p and q match (both T or both F).',
        yr: 'SS2',
      },
      {
        q: 'If p is true and q is false, what is the truth value of p ↔ q?',
        o: ['False', 'True', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Biconditional requires matching truth values. Since p and q differ (T vs F), p ↔ q is false.',
        h: 'Biconditional is false whenever p and q have different truth values.',
        yr: 'SS2',
      },
      {
        q: 'Show that p ∨ ~p is a tautology or contradiction.',
        o: [
          'Tautology (always true)',
          'Contradiction (always false)',
          'Sometimes true, sometimes false',
          'Cannot be determined',
        ],
        a: 0,
        e: "If p is T, ~p is F, so p ∨ ~p = T. If p is F, ~p is T, so p ∨ ~p = T. Every case gives T, so it's a tautology.",
        h: 'Check both possible values of p (T and F) in the expression.',
        yr: 'SS2',
      },
      {
        q: 'Show that p ∧ ~p is a tautology or contradiction.',
        o: [
          'Contradiction (always false)',
          'Tautology (always true)',
          'Sometimes true, sometimes false',
          'Cannot be determined',
        ],
        a: 0,
        e: "If p is T, ~p is F, so p ∧ ~p = T ∧ F = F. If p is F, ~p is T, so p ∧ ~p = F ∧ T = F. Every case gives F, so it's a contradiction.",
        h: 'A statement AND its own negation can never both be true.',
        yr: 'SS2',
      },
      {
        q: 'Which symbol represents the biconditional ("if and only if")?',
        o: ['↔', '→', '∧', '∨'],
        a: 0,
        e: 'The double-headed arrow ↔ represents the biconditional, meaning the implication holds in both directions.',
        h: 'Biconditional uses a two-way arrow, unlike the one-way conditional →.',
        yr: 'SS2',
      },
      {
        q: '"If it rains, then the ground is wet." If it does NOT rain, what can we say about this conditional statement?',
        o: [
          'The statement is still true, regardless of whether the ground is wet',
          'The statement is automatically false',
          'The statement cannot be evaluated',
          'The statement becomes a contradiction',
        ],
        a: 0,
        e: "When the hypothesis (p: 'it rains') is false, the conditional p → q is automatically true, no matter what happens to q.",
        h: 'Recall vacuous truth: a false hypothesis makes any conditional true.',
        yr: 'SS2',
      },
      {
        q: 'For p → q, in which single case is the statement false?',
        o: [
          'p is true and q is false',
          'p is false and q is true',
          'p is true and q is true',
          'p is false and q is false',
        ],
        a: 0,
        e: 'The conditional is false in exactly one row of its truth table: when p is true (the condition holds) but q is false (the promised result fails).',
        h: "Only one of the four rows in the conditional's truth table is false.",
        yr: 'SS2',
      },
      {
        q: 'Is (p → q) ∨ (q → p) a tautology?',
        o: [
          'Yes, it is always true for any p and q',
          'No, it is always false',
          'It depends on the values of p and q',
          'It is a contradiction',
        ],
        a: 0,
        e: 'Testing all four combinations: whenever p and q have any values, at least one of p→q or q→p will be true (they can only both fail simultaneously in an impossible case), so the disjunction is always true — a tautology.',
        h: 'Test all four T/F combinations of p and q systematically.',
        yr: 'SS2',
      },
      {
        q: 'Given p: "x = 3" and q: "x² = 9", is p ↔ q always true for all real x?',
        o: [
          'No, because x = −3 also satisfies q but not p',
          'Yes, they are always equivalent',
          'Cannot be determined without more information',
          'p ↔ q is a tautology',
        ],
        a: 0,
        e: 'If x = −3, then q (x² = 9) is true, but p (x = 3) is false. Since they can have different truth values, p ↔ q is not always true.',
        h: 'Try to find a value of x where p and q disagree.',
        yr: 'SS2',
      },
      {
        q: 'What is the truth value of a contradiction?',
        o: [
          'Always false, for every combination of inputs',
          'Always true, for every combination of inputs',
          'True for some inputs, false for others',
          'Undefined',
        ],
        a: 0,
        e: 'By definition, a contradiction is a compound statement that is false in every row of its truth table.',
        h: 'Recall the definition: a contradiction is always false.',
        yr: 'SS2',
      },
      {
        q: '"5 + 5 = 10 if and only if the sky is blue." If both parts are true, what is the truth value of this biconditional?',
        o: ['True', 'False', 'Cannot be determined', 'Undefined'],
        a: 0,
        e: 'Both statements are true (assuming the sky is indeed blue), so since both sides match, the biconditional is true — even though the two statements are unrelated in meaning.',
        h: 'Biconditional truth depends only on whether the truth VALUES match, not on relatedness of content.',
        yr: 'SS2',
      },
      {
        q: 'How many rows of a truth table for p → q result in a TRUE outcome?',
        o: ['3 out of 4', '1 out of 4', '2 out of 4', '4 out of 4'],
        a: 0,
        e: 'The conditional p → q is true in 3 of the 4 rows (TT, FT, FF) and false in only 1 row (TF).',
        h: "Only the 'p true, q false' row gives a false result; the other three give true.",
        yr: 'SS2',
      },
      {
        q: 'Which of these is logically equivalent to p → q?',
        o: ['~p ∨ q', 'p ∧ q', '~p ∧ ~q', 'p ∨ q'],
        a: 0,
        e: 'p → q is logically equivalent to ~p ∨ q. Checking: if p is false, ~p is true, so ~p ∨ q is true (matches). If p is true and q is false, ~p is false, so ~p ∨ q is false (matches).',
        h: 'The conditional can be rewritten using negation and disjunction: ~p ∨ q.',
        yr: 'SS2',
      },
      {
        q: 'If p ↔ q is a tautology, what does this tell us about p and q?',
        o: [
          'p and q always have the same truth value in every scenario',
          'p and q are always different',
          'p is always true',
          'q is always false',
        ],
        a: 0,
        e: 'If the biconditional p ↔ q is a tautology (always true), then p and q must always share the same truth value — they are logically equivalent.',
        h: 'A tautological biconditional means the two statements are logically equivalent.',
        yr: 'SS2',
      },
      {
        q: 'Determine whether (p → q) ∧ ~q → ~p is a tautology (this is the law of contrapositive reasoning).',
        o: [
          'Yes, it is always true (a tautology)',
          'No, it is a contradiction',
          'It depends on p and q',
          'Cannot be determined',
        ],
        a: 0,
        e: 'Testing all combinations confirms that whenever (p → q) is true and ~q is true (q false), then ~p must also be true. This is the valid rule of Modus Tollens, and the whole implication is a tautology.',
        h: "This is the classic 'Modus Tollens' pattern — test all four rows to confirm it never fails.",
        yr: 'SS2',
      },
      {
        q: '"The number is even if and only if it is divisible by 2." What type of statement is this, and is it a tautology?',
        o: [
          'Biconditional; it is a tautology since both sides are always logically equivalent',
          'Conditional; not a tautology',
          'Conjunction; a contradiction',
          'Disjunction; sometimes true',
        ],
        a: 0,
        e: "This is a biconditional (if and only if), and since 'even' and 'divisible by 2' mean exactly the same thing, the two sides always match — making it a tautology.",
        h: 'Check whether the two parts of the biconditional are always logically equivalent.',
        yr: 'SS2',
      },
      {
        q: 'In the statement p → q, what is p called?',
        o: [
          'The hypothesis (or antecedent)',
          'The conclusion (or consequent)',
          'The negation',
          'The biconditional',
        ],
        a: 0,
        e: 'In a conditional statement p → q, p is called the hypothesis (or antecedent), and q is called the conclusion (or consequent).',
        h: "Recall the standard terminology: 'if [hypothesis] then [conclusion].'",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Trigonometric Functions',
    topicCode: 'SS2-FMATH-09',
    module: 'Trigonometry',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Trigonometric functions relate the angles of a right-angled triangle to the ratios of its sides. In Further Mathematics, we extend these ratios beyond acute angles using the <span class="learn-keyword">unit circle</span> and use them to solve problems involving elevation and depression.</div>\n\n<h3 class="learn-subheading">1. The Basic Ratios</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Ratio</th><th>Formula (right triangle)</th></tr></thead>\n    <tbody>\n      <tr><td>sin &theta;</td><td>Opposite / Hypotenuse</td></tr>\n      <tr><td>cos &theta;</td><td>Adjacent / Hypotenuse</td></tr>\n      <tr><td>tan &theta;</td><td>Opposite / Adjacent = sin&theta;/cos&theta;</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Special Angles</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>&theta;</th><th>sin &theta;</th><th>cos &theta;</th><th>tan &theta;</th></tr></thead>\n    <tbody>\n      <tr><td>0&deg;</td><td>0</td><td>1</td><td>0</td></tr>\n      <tr><td>30&deg;</td><td>1/2</td><td>&radic;3/2</td><td>1/&radic;3</td></tr>\n      <tr><td>45&deg;</td><td>1/&radic;2</td><td>1/&radic;2</td><td>1</td></tr>\n      <tr><td>60&deg;</td><td>&radic;3/2</td><td>1/2</td><td>&radic;3</td></tr>\n      <tr><td>90&deg;</td><td>1</td><td>0</td><td>undefined</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Angles of Elevation and Depression</h3>\n<p class="learn-p">The <span class="learn-keyword">angle of elevation</span> is measured upward from the horizontal to an object above; the <span class="learn-keyword">angle of depression</span> is measured downward from the horizontal to an object below. <strong>Example:</strong> From a point 50 m from the base of a tower, the angle of elevation to the top is 30&deg;. Find the height.<br>tan 30&deg; = height/50 &rArr; height = 50 &times; (1/&radic;3) &asymp; 28.87 m.</p>\n\n<h3 class="learn-subheading">4. The CAST Rule (Signs in Each Quadrant)</h3>\n<p class="learn-p">For angles beyond 90&deg;, the CAST rule tells us which ratios are positive: Quadrant I &mdash; All positive; Quadrant II &mdash; only Sin positive; Quadrant III &mdash; only Tan positive; Quadrant IV &mdash; only Cos positive.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Memorize the special angle table (0&deg;, 30&deg;, 45&deg;, 60&deg;, 90&deg;) exactly &mdash; these appear constantly without a calculator. Use SOHCAHTOA to recall the ratio definitions instantly.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> The three basic trig ratios (sine, cosine, tangent) relate triangle sides to angles. Special angle values must be memorized. Elevation/depression problems use these ratios directly; the CAST rule extends the ratios to all four quadrants.\n</div>\n',
    questions: [
      {
        q: 'In a right-angled triangle, if the opposite side is 3 and the hypotenuse is 5, find sin θ.',
        o: ['3/5', '5/3', '4/5', '3/4'],
        a: 0,
        e: 'sin θ = Opposite/Hypotenuse = 3/5.',
        h: 'Use SOH: sin = Opposite/Hypotenuse.',
        yr: 'SS2',
      },
      {
        q: 'In a right-angled triangle with opposite = 3, adjacent = 4, hypotenuse = 5, find cos θ.',
        o: ['4/5', '3/5', '3/4', '5/4'],
        a: 0,
        e: 'cos θ = Adjacent/Hypotenuse = 4/5.',
        h: 'Use CAH: cos = Adjacent/Hypotenuse.',
        yr: 'SS2',
      },
      {
        q: 'Find tan 45°.',
        o: ['1', '0', '√3', '1/√2'],
        a: 0,
        e: 'At 45°, sin and cos are equal (both 1/√2), so tan 45° = sin/cos = 1.',
        h: 'Recall the special angle table for 45°.',
        yr: 'SS2',
      },
      {
        q: 'Find the exact value of sin 30°.',
        o: ['1/2', '√3/2', '1/√2', '1'],
        a: 0,
        e: 'From the special angles table, sin 30° = 1/2.',
        h: 'This is one of the standard special angle values to memorize.',
        yr: 'SS2',
      },
      {
        q: 'From a point 50 m from the base of a tower, the angle of elevation to the top is 30°. Find the height of the tower (to 2 d.p.).',
        o: ['28.87 m', '25.00 m', '86.60 m', '50.00 m'],
        a: 0,
        e: 'tan 30° = height/50 ⇒ height = 50 × tan 30° = 50 × (1/√3) ≈ 28.87 m.',
        h: 'Use tan(elevation) = opposite (height) / adjacent (distance).',
        yr: 'SS2',
      },
      {
        q: 'Find the exact value of cos 60°.',
        o: ['1/2', '√3/2', '1', '0'],
        a: 0,
        e: 'From the special angles table, cos 60° = 1/2.',
        h: 'Recall that cos 60° equals sin 30° (complementary angle relationship).',
        yr: 'SS2',
      },
      {
        q: 'A ladder leans against a wall, making an angle of 60° with the ground. If the foot of the ladder is 2 m from the wall, find the length of the ladder.',
        o: ['4 m', '2 m', '3.46 m', '1.73 m'],
        a: 0,
        e: 'cos 60° = adjacent/hypotenuse = 2/L ⇒ L = 2/cos 60° = 2/0.5 = 4 m.',
        h: 'The adjacent side (2 m) and hypotenuse (ladder length) relate via cosine.',
        yr: 'SS2',
      },
      {
        q: 'According to the CAST rule, which trigonometric ratio is positive in the second quadrant (90° to 180°)?',
        o: ['Sine only', 'Cosine only', 'Tangent only', 'All ratios'],
        a: 0,
        e: 'The CAST rule states: Quadrant II has only Sine positive (Cosine and Tangent are negative there).',
        h: 'Remember CAST: All, Sin, Tan, Cos are positive in quadrants I, II, III, IV respectively (reading counter-clockwise from quadrant IV).',
        yr: 'SS2',
      },
      {
        q: 'From the top of a cliff 40 m high, the angle of depression to a boat is 20°. Find the horizontal distance from the base of the cliff to the boat (to 2 d.p.), using tan 20° ≈ 0.364.',
        o: ['109.89 m', '14.56 m', '40.00 m', '36.40 m'],
        a: 0,
        e: 'The angle of depression equals the angle of elevation from the boat. tan 20° = 40/distance ⇒ distance = 40/0.364 ≈ 109.89 m.',
        h: 'Angle of depression from the top equals angle of elevation from the boat (alternate angles).',
        yr: 'SS2',
      },
      {
        q: 'Find tan 60° in exact surd form.',
        o: ['√3', '1/√3', '√3/2', '2√3'],
        a: 0,
        e: 'tan 60° = sin60°/cos60° = (√3/2)/(1/2) = √3.',
        h: 'Divide sin 60° by cos 60° using the special angle values.',
        yr: 'SS2',
      },
      {
        q: 'In quadrant IV (270° to 360°), which ratio is positive according to CAST?',
        o: ['Cosine only', 'Sine only', 'Tangent only', 'All ratios'],
        a: 0,
        e: 'By the CAST rule, only Cosine is positive in quadrant IV; sine and tangent are negative there.',
        h: 'CAST reads counter-clockwise starting from quadrant IV (Cos), then I (All), II (Sin), III (Tan).',
        yr: 'SS2',
      },
      {
        q: "A tree casts a shadow of 15 m when the sun's angle of elevation is 40°. Find the height of the tree (to 2 d.p.), using tan 40° ≈ 0.839.",
        o: ['12.59 m', '17.87 m', '15.00 m', '11.49 m'],
        a: 0,
        e: 'tan 40° = height/15 ⇒ height = 15 × 0.839 ≈ 12.59 m.',
        h: 'The shadow is the adjacent side, height is the opposite side.',
        yr: 'SS2',
      },
      {
        q: 'Find the exact value of sin 90°.',
        o: ['1', '0', '1/2', 'Undefined'],
        a: 0,
        e: 'sin 90° = 1, the maximum value the sine function can take.',
        h: 'Recall the special angle table for 90°.',
        yr: 'SS2',
      },
      {
        q: 'Find the exact value of tan 90°.',
        o: ['Undefined', '0', '1', 'Infinity exactly as a number'],
        a: 0,
        e: 'Since tanθ = sinθ/cosθ, and cos 90° = 0, tan 90° involves division by zero, so it is undefined.',
        h: 'tan = sin/cos; check what happens when cosθ = 0.',
        yr: 'SS2',
      },
      {
        q: 'A right triangle has an angle of 25° and hypotenuse 10 cm. Find the length of the side opposite this angle (to 2 d.p.), using sin 25° ≈ 0.423.',
        o: ['4.23 cm', '9.06 cm', '10.00 cm', '2.35 cm'],
        a: 0,
        e: 'sin 25° = opposite/hypotenuse ⇒ opposite = 10 × 0.423 = 4.23 cm.',
        h: 'Use SOH: opposite = hypotenuse × sinθ.',
        yr: 'SS2',
      },
      {
        q: 'In which quadrant are ALL three trigonometric ratios positive?',
        o: [
          'Quadrant I (0° to 90°)',
          'Quadrant II (90° to 180°)',
          'Quadrant III (180° to 270°)',
          'Quadrant IV (270° to 360°)',
        ],
        a: 0,
        e: "In Quadrant I, all trigonometric ratios (sine, cosine, tangent) are positive — this is the 'A' in the CAST rule.",
        h: "The 'A' in CAST stands for 'All' positive, in the first quadrant.",
        yr: 'SS2',
      },
      {
        q: 'Two buildings are 30 m apart. From the top of the shorter building (15 m), the angle of elevation to the top of the taller building is 25°. Find the extra height of the taller building above the shorter one (to 2 d.p.), using tan 25° ≈ 0.466.',
        o: ['13.99 m', '30.00 m', '15.00 m', '6.99 m'],
        a: 0,
        e: 'The extra height = 30 × tan 25° ≈ 30 × 0.466 = 13.99 m.',
        h: 'Use the horizontal distance between buildings as the adjacent side.',
        yr: 'SS2',
      },
      {
        q: 'Find the value of cos 0°.',
        o: ['1', '0', 'undefined', '−1'],
        a: 0,
        e: 'cos 0° = 1, the maximum value cosine can take, occurring when the angle is zero.',
        h: 'Recall the special angle table for 0°.',
        yr: 'SS2',
      },
      {
        q: 'A surveyor stands 100 m from the base of a hill and measures the angle of elevation to the top as 35°. Find the height of the hill (to 2 d.p.), using tan 35° ≈ 0.700.',
        o: ['70.00 m', '57.36 m', '122.08 m', '100.00 m'],
        a: 0,
        e: 'height = 100 × tan 35° ≈ 100 × 0.700 = 70.00 m.',
        h: 'The horizontal distance is the adjacent side; height is opposite.',
        yr: 'SS2',
      },
      {
        q: 'Which mnemonic helps recall the three basic trigonometric ratios?',
        o: ['SOHCAHTOA', 'CAST', 'PEMDAS', 'FOIL'],
        a: 0,
        e: 'SOHCAHTOA stands for Sin=Opposite/Hypotenuse, Cos=Adjacent/Hypotenuse, Tan=Opposite/Adjacent.',
        h: 'This is the classic memory aid taught for the three basic ratios.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Graphs of Trigonometric Functions',
    topicCode: 'SS2-FMATH-10',
    module: 'Trigonometry',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The sine, cosine, and tangent functions produce distinctive repeating <span class="learn-keyword">wave-like graphs</span>. Understanding their shape, period, amplitude, and transformations is essential for solving trigonometric equations and modelling periodic phenomena.</div>\n\n<h3 class="learn-subheading">1. The Graph of y = sin x</h3>\n<p class="learn-p">Starts at (0,0), rises to a maximum of 1 at x = 90&deg;, back to 0 at 180&deg;, down to a minimum of &minus;1 at 270&deg;, back to 0 at 360&deg;. <span class="learn-keyword">Period</span> = 360&deg;. <span class="learn-keyword">Amplitude</span> = 1.</p>\n\n<h3 class="learn-subheading">2. The Graph of y = cos x</h3>\n<p class="learn-p">Starts at maximum (0, 1), falls to 0 at 90&deg;, to minimum &minus;1 at 180&deg;, back to 0 at 270&deg;, back to maximum at 360&deg;. Period = 360&deg;, Amplitude = 1. Cos x is simply sin x shifted 90&deg; to the left.</p>\n\n<h3 class="learn-subheading">3. The Graph of y = tan x</h3>\n<p class="learn-p">Unlike sine and cosine, tan x is NOT bounded &mdash; it has vertical asymptotes at x = 90&deg;, 270&deg;, etc. (where cos x = 0). Period = 180&deg; (half that of sine/cosine).</p>\n\n<h3 class="learn-subheading">4. Transformations</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Function</th><th>Effect</th></tr></thead>\n    <tbody>\n      <tr><td>y = a sin x</td><td>Amplitude becomes a (graph stretched vertically)</td></tr>\n      <tr><td>y = sin(bx)</td><td>Period becomes 360&deg;/b (graph compressed horizontally if b &gt; 1)</td></tr>\n      <tr><td>y = sin x + c</td><td>Graph shifted up by c units</td></tr>\n      <tr><td>y = sin(x &minus; d)</td><td>Graph shifted right by d degrees (phase shift)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> To sketch quickly, plot the five key points at 0&deg;, 90&deg;, 180&deg;, 270&deg;, 360&deg; for sine/cosine using their known values, then join with a smooth curve &mdash; never with straight lines.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Sine and cosine are smooth waves with period 360&deg; and amplitude 1; tangent repeats every 180&deg; and has vertical asymptotes. Coefficients a, b, c, d in y = a sin(bx + ...) + c control amplitude, period, vertical shift, and phase shift respectively.\n</div>\n',
    questions: [
      {
        q: 'What is the period of y = sin x?',
        o: ['360°', '180°', '90°', '720°'],
        a: 0,
        e: 'The sine function completes one full cycle every 360°.',
        h: 'Sine and cosine both repeat their pattern every 360°.',
        yr: 'SS2',
      },
      {
        q: 'What is the amplitude of y = 3 sin x?',
        o: ['3', '1', '6', '0'],
        a: 0,
        e: 'The amplitude is the coefficient in front of sin x, which is 3 here — the graph oscillates between −3 and 3.',
        h: 'The amplitude is the absolute value of the coefficient multiplying the trig function.',
        yr: 'SS2',
      },
      {
        q: 'At what value of x (between 0° and 360°) does y = cos x reach its minimum value?',
        o: ['180°', '0°', '90°', '270°'],
        a: 0,
        e: 'cos x reaches its minimum value of −1 at x = 180°.',
        h: 'Recall the shape of the cosine curve: starts at max (0°), reaches min at 180°.',
        yr: 'SS2',
      },
      {
        q: 'What is the period of y = tan x?',
        o: ['180°', '360°', '90°', '720°'],
        a: 0,
        e: 'Unlike sine and cosine, the tangent function repeats every 180°.',
        h: 'Tangent has half the period of sine and cosine.',
        yr: 'SS2',
      },
      {
        q: 'At what values of x does y = tan x have vertical asymptotes (undefined values), between 0° and 360°?',
        o: ['90° and 270°', '0° and 180°', '0° and 360°', '45° and 135°'],
        a: 0,
        e: 'tan x = sin x / cos x is undefined wherever cos x = 0, which occurs at x = 90° and x = 270°.',
        h: 'tan x is undefined wherever cos x = 0.',
        yr: 'SS2',
      },
      {
        q: 'What is the period of y = sin(2x)?',
        o: ['180°', '360°', '720°', '90°'],
        a: 0,
        e: 'The period of sin(bx) is 360°/b. With b = 2, period = 360°/2 = 180°.',
        h: 'Use period = 360°/b for y = sin(bx).',
        yr: 'SS2',
      },
      {
        q: 'What is the effect of the graph y = sin x + 2 compared to y = sin x?',
        o: [
          'Shifted vertically up by 2 units',
          'Shifted right by 2 degrees',
          'Stretched with amplitude 2',
          'Compressed horizontally',
        ],
        a: 0,
        e: 'Adding a constant outside the trig function shifts the entire graph vertically. y = sin x + 2 moves every point of y = sin x up by 2 units.',
        h: 'A constant added outside the function affects vertical position, not shape.',
        yr: 'SS2',
      },
      {
        q: 'What is the amplitude of y = cos x (unmodified)?',
        o: ['1', '0', '2', 'Undefined'],
        a: 0,
        e: 'The standard cosine function oscillates between −1 and 1, so its amplitude is 1.',
        h: 'The basic sine and cosine functions both have amplitude 1.',
        yr: 'SS2',
      },
      {
        q: 'For y = 4 sin(3x), what are the amplitude and period?',
        o: [
          'Amplitude = 4, Period = 120°',
          'Amplitude = 3, Period = 360°',
          'Amplitude = 4, Period = 360°',
          'Amplitude = 12, Period = 120°',
        ],
        a: 0,
        e: 'Amplitude is the coefficient of sin (4). Period = 360°/b = 360°/3 = 120°.',
        h: 'Amplitude comes from the outer coefficient; period comes from the coefficient of x inside.',
        yr: 'SS2',
      },
      {
        q: 'At which value of x does y = sin x cross the x-axis (equal zero) besides x = 0°, within 0° to 360°?',
        o: ['180° and 360°', '90° and 270°', '45° and 135°', '90° only'],
        a: 0,
        e: 'sin x = 0 at x = 0°, 180°, and 360° within one full cycle.',
        h: 'Recall the sine graph crosses zero at the start, middle, and end of each cycle.',
        yr: 'SS2',
      },
      {
        q: 'Which transformation describes y = sin(x − 30°) compared to y = sin x?',
        o: [
          'Shifted right (phase shift) by 30°',
          'Shifted left by 30°',
          'Shifted up by 30°',
          'Amplitude reduced by 30',
        ],
        a: 0,
        e: 'Subtracting a value inside the function argument (x − 30°) shifts the graph to the RIGHT by that amount — this is called a phase shift.',
        h: 'y = f(x − d) shifts the graph of y = f(x) to the right by d units.',
        yr: 'SS2',
      },
      {
        q: 'What is the maximum value that y = 5 cos x can reach?',
        o: ['5', '1', '−5', '10'],
        a: 0,
        e: 'The maximum value of cos x is 1, so the maximum of 5 cos x is 5 × 1 = 5.',
        h: 'Multiply the amplitude by the maximum value of the base cosine function (which is 1).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is TRUE about the graph of y = cos x compared to y = sin x?',
        o: [
          'y = cos x is the same shape as y = sin x, but shifted 90° to the left',
          'y = cos x has a different period than y = sin x',
          'y = cos x has a larger amplitude than y = sin x',
          'y = cos x and y = sin x never intersect',
        ],
        a: 0,
        e: 'cos x = sin(x + 90°), meaning the cosine graph is the sine graph shifted 90° to the left. Both have the same period (360°) and amplitude (1).',
        h: 'Cosine and sine have the exact same wave shape, just offset by 90°.',
        yr: 'SS2',
      },
      {
        q: 'For y = sin(x) − 3, what is the range of possible y-values?',
        o: ['−4 to −2', '−1 to 1', '−3 to 3', '−2 to 4'],
        a: 0,
        e: 'y = sin x ranges from −1 to 1. Subtracting 3 from every value: (−1 − 3) to (1 − 3) = −4 to −2.',
        h: 'Shift the normal range of sin x (−1 to 1) down by 3.',
        yr: 'SS2',
      },
      {
        q: 'How many complete cycles does y = sin(4x) complete between 0° and 360°?',
        o: ['4', '2', '1', '8'],
        a: 0,
        e: 'The period of sin(4x) is 360°/4 = 90°. In a 360° range, this completes 360°/90° = 4 full cycles.',
        h: 'Find the period first, then divide the total interval by the period.',
        yr: 'SS2',
      },
      {
        q: 'What is the minimum value of y = 2 sin x + 1?',
        o: ['−1', '1', '−3', '3'],
        a: 0,
        e: 'sin x has minimum −1, so 2 sin x has minimum −2. Adding 1: −2 + 1 = −1.',
        h: 'Find the minimum of 2 sin x first, then add the vertical shift.',
        yr: 'SS2',
      },
      {
        q: 'The graph of y = tan x between 0° and 90° (exclusive) behaves how?',
        o: [
          'Increases continuously from 0 towards infinity',
          'Decreases from 1 to 0',
          'Oscillates between −1 and 1',
          'Remains constant',
        ],
        a: 0,
        e: 'As x increases from 0° towards 90°, tan x increases continuously and without bound, approaching infinity as x approaches 90°.',
        h: 'Consider what happens to sin x/cos x as cos x approaches zero.',
        yr: 'SS2',
      },
      {
        q: 'For y = a sin(bx) + c, which letter controls the vertical (up/down) shift of the graph?',
        o: ['c', 'a', 'b', 'None of them'],
        a: 0,
        e: 'In y = a sin(bx) + c, the constant c added at the end shifts the whole graph vertically.',
        h: 'The term added outside the trig function affects vertical position only.',
        yr: 'SS2',
      },
      {
        q: 'What is the amplitude of y = −2 cos x?',
        o: ['2', '−2', '1', '0'],
        a: 0,
        e: "Amplitude is always taken as a positive value — the absolute value of the coefficient, |−2| = 2. The negative sign flips the graph vertically but doesn't change the amplitude.",
        h: 'Amplitude is the ABSOLUTE VALUE of the coefficient, always positive.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following statements about y = sin x and y = cos x is TRUE?',
        o: [
          'Both are bounded between −1 and 1 for all real x',
          'Both have vertical asymptotes',
          'Both have period 180°',
          'cos x is always greater than sin x',
        ],
        a: 0,
        e: 'Both sine and cosine oscillate smoothly and are always bounded between −1 and 1 — unlike tangent, which is unbounded and has asymptotes.',
        h: 'Recall that sine and cosine never exceed 1 or go below −1.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Trigonometric Identities, Inverse Trig Graphs and Pythagoras Theorem',
    topicCode: 'SS2-FMATH-11',
    module: 'Trigonometry',
    term: 'First Term',
    contentHTML:
      '\n<div class="learn-intro">\n  We close out trigonometry for the term with the fundamental <span class="learn-keyword">identities</span> that connect sin, cos, and tan, the graphs of the <span class="learn-keyword">inverse trigonometric functions</span>, and a review of the <span class="learn-keyword">Pythagoras theorem</span> that underlies them all.</div>\n\n<h3 class="learn-subheading">1. The Basic Identities</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Identity</th><th>Statement</th></tr></thead>\n    <tbody>\n      <tr><td>Quotient identity</td><td>tan &theta; = sin &theta; / cos &theta;</td></tr>\n      <tr><td>Pythagorean identity</td><td>sin&sup2;&theta; + cos&sup2;&theta; = 1</td></tr>\n      <tr><td>Derived identity</td><td>1 + tan&sup2;&theta; = sec&sup2;&theta;</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Using Identities to Simplify Expressions</h3>\n<p class="learn-p"><strong>Example:</strong> Simplify sin&sup2;&theta; / (1 &minus; cos&sup2;&theta;).<br>Since 1 &minus; cos&sup2;&theta; = sin&sup2;&theta; (from the Pythagorean identity), the expression simplifies to sin&sup2;&theta;/sin&sup2;&theta; = 1.</p>\n\n<h3 class="learn-subheading">3. Pythagoras\' Theorem in Trigonometry</h3>\n<p class="learn-p">In a right triangle, hypotenuse&sup2; = opposite&sup2; + adjacent&sup2;. <strong>Example:</strong> If sin &theta; = 3/5, find cos &theta; (&theta; acute).<br>Using sin&sup2;&theta; + cos&sup2;&theta; = 1: cos&sup2;&theta; = 1 &minus; 9/25 = 16/25 &rArr; cos &theta; = 4/5.</p>\n\n<h3 class="learn-subheading">4. Inverse Trigonometric Functions</h3>\n<p class="learn-p">sin&#8315;&sup1;x (also written arcsin) gives the angle whose sine is x. <strong>Example:</strong> sin&#8315;&sup1;(0.5) = 30&deg;. The domain of sin&#8315;&sup1;x and cos&#8315;&sup1;x is restricted to [&minus;1, 1] since sine and cosine never exceed these bounds; their graphs are the reflection of y = sin x and y = cos x (restricted) in the line y = x.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Whenever you see sin&sup2;&theta; + cos&sup2;&theta;, or 1 &minus; sin&sup2;&theta;, or 1 &minus; cos&sup2;&theta; in a question, immediately think of the Pythagorean identity &mdash; it is the single most tested identity in Further Mathematics trigonometry.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> sin&sup2;&theta; + cos&sup2;&theta; = 1 is the master identity, from which others are derived. Pythagoras\' theorem is its geometric origin. Inverse trig functions "undo" the trig ratios, restricted to valid domains and ranges.\n</div>\n',
    questions: [
      {
        q: 'Simplify sin²θ + cos²θ.',
        o: ['1', '0', '2', 'sinθ + cosθ'],
        a: 0,
        e: 'This is the fundamental Pythagorean identity: sin²θ + cos²θ = 1 for all values of θ.',
        h: 'This is one of the most important identities in trigonometry — memorize it.',
        yr: 'SS2',
      },
      {
        q: 'If sin θ = 3/5 and θ is acute, find cos θ.',
        o: ['4/5', '3/5', '5/3', '5/4'],
        a: 0,
        e: 'cos²θ = 1 − sin²θ = 1 − 9/25 = 16/25 ⇒ cosθ = 4/5 (positive since θ is acute).',
        h: 'Use sin²θ + cos²θ = 1 to find cosθ, then take the positive square root.',
        yr: 'SS2',
      },
      {
        q: 'Simplify sin²θ / (1 − cos²θ).',
        o: ['1', 'sinθ', 'cosθ', '0'],
        a: 0,
        e: '1 − cos²θ = sin²θ (from the Pythagorean identity). So the expression becomes sin²θ/sin²θ = 1.',
        h: 'Rewrite 1 − cos²θ using the Pythagorean identity first.',
        yr: 'SS2',
      },
      {
        q: 'If cos θ = 12/13 and θ is acute, find sin θ.',
        o: ['5/13', '12/13', '13/12', '5/12'],
        a: 0,
        e: 'sin²θ = 1 − cos²θ = 1 − 144/169 = 25/169 ⇒ sinθ = 5/13.',
        h: 'Use sin²θ + cos²θ = 1, then take the square root.',
        yr: 'SS2',
      },
      {
        q: 'Find sin⁻¹(0.5).',
        o: ['30°', '60°', '45°', '90°'],
        a: 0,
        e: 'sin 30° = 0.5, so sin⁻¹(0.5) = 30°.',
        h: 'Recall which special angle has a sine value of 0.5.',
        yr: 'SS2',
      },
      {
        q: 'Find cos⁻¹(0.5).',
        o: ['60°', '30°', '45°', '90°'],
        a: 0,
        e: 'cos 60° = 0.5, so cos⁻¹(0.5) = 60°.',
        h: 'Recall which special angle has a cosine value of 0.5.',
        yr: 'SS2',
      },
      {
        q: 'Simplify 1 − sin²θ.',
        o: ['cos²θ', 'sin²θ', '1', 'tan²θ'],
        a: 0,
        e: 'From sin²θ + cos²θ = 1, rearranging gives 1 − sin²θ = cos²θ.',
        h: 'Rearrange the Pythagorean identity to isolate this expression.',
        yr: 'SS2',
      },
      {
        q: 'What is the domain of y = sin⁻¹x?',
        o: ['−1 ≤ x ≤ 1', 'All real numbers', 'x ≥ 0', '0 ≤ x ≤ 90'],
        a: 0,
        e: 'Since sine only produces values between −1 and 1, the inverse sine function is only defined for inputs in that range.',
        h: 'The domain of an inverse function equals the range of the original function.',
        yr: 'SS2',
      },
      {
        q: "A right triangle has legs 6 and 8. Find the hypotenuse using Pythagoras' theorem.",
        o: ['10', '12', '14', '48'],
        a: 0,
        e: 'hypotenuse² = 6² + 8² = 36 + 64 = 100 ⇒ hypotenuse = 10 (the classic 3-4-5 triangle scaled by 2).',
        h: 'Apply c² = a² + b² and take the square root.',
        yr: 'SS2',
      },
      {
        q: 'Simplify tanθ × cosθ.',
        o: ['sinθ', 'cosθ', '1', 'secθ'],
        a: 0,
        e: 'tanθ = sinθ/cosθ, so tanθ × cosθ = (sinθ/cosθ) × cosθ = sinθ.',
        h: 'Substitute the quotient identity for tanθ, then simplify.',
        yr: 'SS2',
      },
      {
        q: 'A ladder of length 13 m leans against a wall, reaching a height of 12 m. Find the distance of the foot of the ladder from the wall.',
        o: ['5 m', '1 m', '25 m', '17 m'],
        a: 0,
        e: 'Using Pythagoras: 13² = 12² + d² ⇒ 169 = 144 + d² ⇒ d² = 25 ⇒ d = 5 m.',
        h: 'Rearrange c² = a² + b² to solve for the unknown leg.',
        yr: 'SS2',
      },
      {
        q: 'Prove/simplify: (1 − cosθ)(1 + cosθ).',
        o: ['sin²θ', 'cos²θ', '1', '1 − 2cosθ'],
        a: 0,
        e: 'This is a difference of squares: (1 − cosθ)(1 + cosθ) = 1 − cos²θ = sin²θ (by the Pythagorean identity).',
        h: 'Expand as a difference of squares first, then apply the Pythagorean identity.',
        yr: 'SS2',
      },
      {
        q: 'Find tan⁻¹(1).',
        o: ['45°', '30°', '60°', '90°'],
        a: 0,
        e: 'tan 45° = 1, so tan⁻¹(1) = 45°.',
        h: 'Recall which special angle has a tangent value of exactly 1.',
        yr: 'SS2',
      },
      {
        q: 'If tanθ = 5/12 and θ is acute, find the hypotenuse of the reference right triangle (with opposite = 5, adjacent = 12).',
        o: ['13', '17', '7', '60'],
        a: 0,
        e: 'Using Pythagoras: hyp² = 5² + 12² = 25 + 144 = 169 ⇒ hyp = 13.',
        h: 'Treat the numerator and denominator of tanθ as the opposite and adjacent sides, then apply Pythagoras.',
        yr: 'SS2',
      },
      {
        q: 'Simplify sec²θ − tan²θ.',
        o: ['1', '0', 'sin²θ', 'cos²θ'],
        a: 0,
        e: 'From the identity 1 + tan²θ = sec²θ, rearranging gives sec²θ − tan²θ = 1.',
        h: 'Recall the derived Pythagorean identity: 1 + tan²θ = sec²θ.',
        yr: 'SS2',
      },
      {
        q: 'What is the range of y = cos⁻¹x?',
        o: ['0° to 180°', '−90° to 90°', '−1 to 1', '0° to 360°'],
        a: 0,
        e: 'The principal range of cos⁻¹x is conventionally restricted to 0° to 180°, ensuring the inverse function is well-defined.',
        h: 'The range of cos⁻¹x differs from that of sin⁻¹x (which is −90° to 90°).',
        yr: 'SS2',
      },
      {
        q: 'A right triangle has hypotenuse 25 and one leg 7. Find the other leg.',
        o: ['24', '18', '32', '22'],
        a: 0,
        e: '25² = 7² + b² ⇒ 625 = 49 + b² ⇒ b² = 576 ⇒ b = 24.',
        h: "Rearrange Pythagoras' theorem: b² = hypotenuse² − known leg².",
        yr: 'SS2',
      },
      {
        q: 'Given sinθ = 8/17 (θ acute), find tanθ.',
        o: ['8/15', '15/8', '8/17', '17/8'],
        a: 0,
        e: 'Using Pythagoras with opposite = 8, hypotenuse = 17: adjacent² = 289 − 64 = 225 ⇒ adjacent = 15. So tanθ = opposite/adjacent = 8/15.',
        h: 'Find the missing side using Pythagoras, then form the tangent ratio.',
        yr: 'SS2',
      },
      {
        q: 'Simplify (sinθ + cosθ)² + (sinθ − cosθ)².',
        o: ['2', '1', '0', '4'],
        a: 0,
        e: 'Expanding: (sin²θ + 2sinθcosθ + cos²θ) + (sin²θ − 2sinθcosθ + cos²θ) = 2sin²θ + 2cos²θ = 2(sin²θ+cos²θ) = 2(1) = 2.',
        h: 'Expand both squares fully; the cross terms will cancel.',
        yr: 'SS2',
      },
      {
        q: 'Find sin⁻¹(1).',
        o: ['90°', '0°', '45°', '180°'],
        a: 0,
        e: 'sin 90° = 1, so sin⁻¹(1) = 90°, the maximum value in its range.',
        h: 'Recall the special angle whose sine reaches the maximum value of 1.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Limits of Functions and Differentiation from First Principles',
    topicCode: 'SS2-FMATH-12',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Calculus</span> begins with the idea of a <span class="learn-keyword">limit</span> &mdash; what value a function approaches as the input gets closer and closer to a point. From limits, we build <span class="learn-keyword">differentiation from first principles</span>, the formal definition behind every differentiation rule you will use this term.</div>\n\n<h3 class="learn-subheading">1. The Idea of a Limit</h3>\n<p class="learn-p">lim<sub>x&rarr;a</sub> f(x) is the value f(x) approaches as x gets arbitrarily close to a (not necessarily the value AT a). <strong>Example:</strong> lim<sub>x&rarr;2</sub> (x&sup2; + 1) = 4 + 1 = 5 (simply substitute, since the function is continuous here).</p>\n\n<h3 class="learn-subheading">2. Limits Involving Indeterminate Forms</h3>\n<p class="learn-p"><strong>Example:</strong> Find lim<sub>x&rarr;3</sub> (x&sup2; &minus; 9)/(x &minus; 3).<br>Direct substitution gives 0/0 (indeterminate). Factor first: (x&minus;3)(x+3)/(x&minus;3) = x + 3 (for x &ne; 3). Now substitute: lim = 3 + 3 = 6.</p>\n\n<h3 class="learn-subheading">3. Differentiation from First Principles</h3>\n<p class="learn-p">The derivative of f(x) is defined as: f&prime;(x) = lim<sub>h&rarr;0</sub> [f(x+h) &minus; f(x)] / h.<br>\n<strong>Example:</strong> Differentiate f(x) = x&sup2; from first principles.<br>\nf(x+h) = (x+h)&sup2; = x&sup2; + 2xh + h&sup2;.<br>\nf(x+h) &minus; f(x) = 2xh + h&sup2; = h(2x + h).<br>\n[f(x+h) &minus; f(x)]/h = 2x + h.<br>\nAs h &rarr; 0: f&prime;(x) = 2x.</p>\n\n<h3 class="learn-subheading">4. Another Worked Example</h3>\n<p class="learn-p">Differentiate f(x) = 3x + 5 from first principles.<br>\nf(x+h) &minus; f(x) = 3(x+h) + 5 &minus; (3x + 5) = 3h.<br>\n[f(x+h) &minus; f(x)]/h = 3h/h = 3.<br>\nAs h &rarr; 0, this stays 3, so f&prime;(x) = 3 (constant, as expected for a linear function).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> In first-principles questions, ALWAYS expand f(x+h) fully, subtract f(x), factor out h from the numerator, cancel the h in the denominator, THEN let h &rarr; 0. Skipping the factoring step is the most common source of errors.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> A limit describes the value a function approaches. Differentiation from first principles uses f&prime;(x) = lim<sub>h&rarr;0</sub>[f(x+h)&minus;f(x)]/h to derive the gradient function directly from the definition, without shortcut rules.\n</div>\n',
    questions: [
      {
        q: 'Evaluate limₓ→₂ (x² + 1).',
        o: ['5', '4', '3', '9'],
        a: 0,
        e: 'Since x² + 1 is continuous everywhere, substitute directly: 2² + 1 = 5.',
        h: 'For continuous functions, simply substitute the value x approaches.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→₃ (x² − 9)/(x − 3).',
        o: ['6', '0', '3', 'Undefined'],
        a: 0,
        e: 'Direct substitution gives 0/0. Factor: (x−3)(x+3)/(x−3) = x + 3. Then substitute x = 3: 3 + 3 = 6.',
        h: 'Factor the numerator as a difference of squares before substituting.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = x² from first principles.',
        o: ["f'(x) = 2x", "f'(x) = x", "f'(x) = 2", "f'(x) = x²"],
        a: 0,
        e: "f(x+h) − f(x) = (x+h)² − x² = 2xh + h² = h(2x+h). Dividing by h gives 2x + h. As h → 0, f'(x) = 2x.",
        h: 'Expand (x+h)², subtract f(x), factor out h, then let h approach 0.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = 3x + 5 from first principles.',
        o: ["f'(x) = 3", "f'(x) = 5", "f'(x) = 3x", "f'(x) = 8"],
        a: 0,
        e: "f(x+h) − f(x) = 3(x+h)+5 − (3x+5) = 3h. Dividing by h gives 3 (constant), so f'(x) = 3.",
        h: 'For a linear function, the difference quotient should simplify to a constant.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→0 (x² + 3x)/x.',
        o: ['3', '0', 'Undefined', '1'],
        a: 0,
        e: 'Factor: x(x+3)/x = x + 3 (for x ≠ 0). As x → 0: 0 + 3 = 3.',
        h: 'Factor out the common x in the numerator before substituting.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = x³ from first principles (find the general result).',
        o: ["f'(x) = 3x²", "f'(x) = x²", "f'(x) = 3x", "f'(x) = x³"],
        a: 0,
        e: "(x+h)³ − x³ = 3x²h + 3xh² + h³ = h(3x² + 3xh + h²). Dividing by h: 3x² + 3xh + h². As h → 0: f'(x) = 3x².",
        h: 'Expand (x+h)³ using the binomial pattern, then factor out h.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→5 (x + 3).',
        o: ['8', '5', '3', '15'],
        a: 0,
        e: 'This is a continuous function, so substitute directly: 5 + 3 = 8.',
        h: 'Simple substitution works for continuous functions with no indeterminate form.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = 5 (a constant function) from first principles.',
        o: ["f'(x) = 0", "f'(x) = 5", "f'(x) = 1", "f'(x) = Undefined"],
        a: 0,
        e: "f(x+h) − f(x) = 5 − 5 = 0. Dividing by h gives 0 for all h ≠ 0, so f'(x) = 0. Constant functions have zero gradient everywhere.",
        h: 'A constant function never changes, so its derivative should be zero.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→2 (x² − 4)/(x − 2).',
        o: ['4', '0', '2', 'Undefined'],
        a: 0,
        e: 'Factor: (x−2)(x+2)/(x−2) = x + 2. As x → 2: 2 + 2 = 4.',
        h: 'Factor the difference of squares in the numerator first.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = 2x² + 1 from first principles.',
        o: ["f'(x) = 4x", "f'(x) = 2x", "f'(x) = 4x + 1", "f'(x) = 2"],
        a: 0,
        e: "f(x+h) − f(x) = 2(x+h)² + 1 − (2x²+1) = 2(2xh + h²) = 4xh + 2h² = h(4x + 2h). Dividing by h: 4x + 2h. As h → 0: f'(x) = 4x.",
        h: 'Expand 2(x+h)² fully, subtract f(x), then factor out h.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→1 (x³ − 1)/(x − 1).',
        o: ['3', '1', '0', 'Undefined'],
        a: 0,
        e: 'Factor using difference of cubes: (x−1)(x²+x+1)/(x−1) = x²+x+1. As x→1: 1+1+1 = 3.',
        h: 'Recognize x³ − 1 as a difference of cubes before cancelling.',
        yr: 'SS2',
      },
      {
        q: 'In differentiation from first principles, what does h represent?',
        o: [
          'A small increment/change in x that approaches zero',
          'The gradient of the tangent',
          'The y-intercept of the function',
          'The value of x itself',
        ],
        a: 0,
        e: 'h represents a small change in the x-value, used to compute the average rate of change between x and x+h, which becomes the instantaneous rate of change as h → 0.',
        h: 'Think of h as a tiny step size that shrinks toward zero.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = x² + 3x from first principles.',
        o: ["f'(x) = 2x + 3", "f'(x) = 2x", "f'(x) = x + 3", "f'(x) = 3"],
        a: 0,
        e: "f(x+h)−f(x) = (x+h)²+3(x+h) − (x²+3x) = 2xh+h²+3h = h(2x+h+3). Dividing by h: 2x+h+3. As h→0: f'(x) = 2x+3.",
        h: "Differentiate each term's contribution separately within the first-principles expansion.",
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→0 (5x)/(x).',
        o: ['5', '0', '1', 'Undefined'],
        a: 0,
        e: 'Cancel the common x (for x ≠ 0): 5x/x = 5. This is constant, so the limit is 5.',
        h: 'Simplify the fraction by cancelling x before taking the limit.',
        yr: 'SS2',
      },
      {
        q: 'Which expression correctly defines the derivative from first principles?',
        o: [
          "f'(x) = limₕ→0 [f(x+h) − f(x)]/h",
          "f'(x) = limₕ→0 [f(x) − f(x+h)]/h",
          "f'(x) = [f(x+h) − f(x)]/h for any h",
          "f'(x) = limₓ→0 f(x)/x",
        ],
        a: 0,
        e: "The formal definition is f'(x) = limₕ→0 [f(x+h) − f(x)]/h, the limit of the difference quotient as h approaches (but never equals) zero.",
        h: 'Recall the precise first-principles formula with the correct order of subtraction.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = 4x² − 2x from first principles.',
        o: ["f'(x) = 8x − 2", "f'(x) = 8x", "f'(x) = 4x − 2", "f'(x) = 8x + 2"],
        a: 0,
        e: "f(x+h)−f(x) = 4(x+h)²−2(x+h) − (4x²−2x) = 8xh+4h²−2h = h(8x+4h−2). Dividing by h: 8x+4h−2. As h→0: f'(x)=8x−2.",
        h: 'Expand carefully, tracking both the x² term and the linear term separately.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₓ→−1 (x² − 1)/(x + 1).',
        o: ['−2', '0', '2', 'Undefined'],
        a: 0,
        e: 'Factor: (x−1)(x+1)/(x+1) = x − 1 (for x ≠ −1). As x → −1: −1 − 1 = −2.',
        h: 'Factor the numerator as a difference of squares before cancelling (x+1).',
        yr: 'SS2',
      },
      {
        q: "Why can't we simply substitute x = 3 directly into (x²−9)/(x−3) to find the limit as x → 3?",
        o: [
          'It gives the indeterminate form 0/0, requiring factoring first',
          'The function is undefined for all x',
          "The limit doesn't exist for polynomial functions",
          'Direct substitution always works for any rational function',
        ],
        a: 0,
        e: 'Substituting x = 3 gives (9−9)/(3−3) = 0/0, an indeterminate form. We must simplify algebraically (factor and cancel) before substituting.',
        h: 'Check what happens when you substitute directly — you get 0 in both numerator and denominator.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate f(x) = 7 (a constant) using first principles reasoning applied to a general constant c.',
        o: [
          "f'(x) = 0, since a constant function never changes",
          "f'(x) = 7",
          "f'(x) = c",
          "f'(x) is undefined",
        ],
        a: 0,
        e: 'For any constant function f(x) = c, f(x+h) − f(x) = c − c = 0 for all h, so the derivative is always 0.',
        h: 'A horizontal line (constant function) has zero slope everywhere.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate limₕ→0 [(2+h)² − 4]/h.',
        o: ['4', '2', '0', 'Undefined'],
        a: 0,
        e: '(2+h)² − 4 = 4 + 4h + h² − 4 = 4h + h² = h(4+h). Dividing by h: 4+h. As h→0: 4.',
        h: 'This is exactly the first-principles definition applied to f(x) = x² at x = 2.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Rules of Differentiation I',
    topicCode: 'SS2-FMATH-13',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Differentiating from first principles every time is slow. Instead, we use standard <span class="learn-keyword">differentiation rules</span>: the power rule, constant rule, and sum/difference rule &mdash; the everyday toolkit for differentiating algebraic functions quickly.</div>\n\n<h3 class="learn-subheading">1. The Power Rule</h3>\n<p class="learn-p">If y = x&#8319;, then dy/dx = nx&#8319;&#8315;&sup1;. <strong>Example:</strong> y = x&#8308; &rArr; dy/dx = 4x&sup3;.</p>\n\n<h3 class="learn-subheading">2. The Constant Multiple Rule</h3>\n<p class="learn-p">If y = c&middot;f(x), then dy/dx = c&middot;f&prime;(x). <strong>Example:</strong> y = 5x&sup3; &rArr; dy/dx = 5(3x&sup2;) = 15x&sup2;.</p>\n\n<h3 class="learn-subheading">3. The Constant Rule</h3>\n<p class="learn-p">If y = c (a constant with no x), then dy/dx = 0. <strong>Example:</strong> y = 7 &rArr; dy/dx = 0.</p>\n\n<h3 class="learn-subheading">4. The Sum/Difference Rule</h3>\n<p class="learn-p">Differentiate each term separately, then add/subtract. <strong>Example:</strong> y = 3x&sup2; &minus; 5x + 2 &rArr; dy/dx = 6x &minus; 5 + 0 = 6x &minus; 5.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Function</th><th>Derivative</th></tr></thead>\n    <tbody>\n      <tr><td>x&#8319;</td><td>nx&#8319;&#8315;&sup1;</td></tr>\n      <tr><td>c (constant)</td><td>0</td></tr>\n      <tr><td>c&middot;f(x)</td><td>c&middot;f&prime;(x)</td></tr>\n      <tr><td>1/x&#8319; = x&#8315;&#8319;</td><td>&minus;nx&#8315;&#8319;&#8315;&sup1;</td></tr>\n      <tr><td>&radic;x = x&#189;</td><td>&#189;x&#8315;&#189;</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">5. Rewriting Before Differentiating</h3>\n<p class="learn-p">Convert roots and fractions to index (power) form first. <strong>Example:</strong> y = 3/x&sup2; = 3x&#8315;&sup2; &rArr; dy/dx = &minus;6x&#8315;&sup3; = &minus;6/x&sup3;.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always rewrite square roots as fractional powers and fractions as negative powers BEFORE applying the power rule &mdash; e.g., &#8731;x = x&#185;&#8260;&sup3;, 1/x&#8309; = x&#8315;&#8309;. Trying to differentiate roots or fractions directly is a common source of mistakes.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> The power rule (bring the power down, subtract 1) handles most algebraic terms. Constants vanish upon differentiation. Sums and differences differentiate term-by-term. Always simplify expressions into pure power form first.\n</div>\n',
    questions: [
      {
        q: 'Differentiate y = x⁴.',
        o: ['4x³', 'x³', '4x⁵', 'x⁴'],
        a: 0,
        e: 'Using the power rule: dy/dx = 4x⁴⁻¹ = 4x³.',
        h: 'Bring the power down as a coefficient, then subtract 1 from the power.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 5x³.',
        o: ['15x²', '5x²', '15x³', '3x²'],
        a: 0,
        e: 'dy/dx = 5 × 3x² = 15x² (constant multiple rule combined with power rule).',
        h: 'Differentiate x³ first, then multiply by the constant coefficient 5.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 7 (a constant).',
        o: ['0', '7', '1', '7x'],
        a: 0,
        e: 'The derivative of any constant is always 0, since a constant function has zero rate of change.',
        h: 'Constants have no x term, so their rate of change is zero.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 3x² − 5x + 2.',
        o: ['6x − 5', '6x − 5x + 2', '3x − 5', '6x + 2'],
        a: 0,
        e: 'Differentiate term by term: d/dx(3x²) = 6x, d/dx(−5x) = −5, d/dx(2) = 0. Total: 6x − 5.',
        h: 'Differentiate each term separately using the power and constant rules.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 3/x².',
        o: ['−6/x³', '6/x³', '−6x', '−3/x'],
        a: 0,
        e: 'Rewrite as y = 3x⁻². dy/dx = 3 × (−2)x⁻³ = −6x⁻³ = −6/x³.',
        h: 'Rewrite the fraction as a negative power of x before differentiating.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = √x.',
        o: ['1/(2√x)', '√x/2', '2√x', '1/√x'],
        a: 0,
        e: 'Rewrite as y = x¹ᐞ². dy/dx = ½x⁻¹ᐞ² = 1/(2x¹ᐞ²) = 1/(2√x).',
        h: 'Rewrite the square root as x to the power of one-half first.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 2x⁵ + 3x² − 7.',
        o: ['10x⁴ + 6x', '10x⁴ + 6x − 7', '5x⁴ + 2x', '10x⁴ + 3x'],
        a: 0,
        e: 'd/dx(2x⁵) = 10x⁴; d/dx(3x²) = 6x; d/dx(−7) = 0. Total: 10x⁴ + 6x.',
        h: 'Differentiate each term individually, remembering constants vanish.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 4x.',
        o: ['4', '4x', '0', 'x'],
        a: 0,
        e: 'Treating 4x as 4x¹: dy/dx = 4 × 1 × x⁰ = 4 × 1 = 4 (since x⁰ = 1).',
        h: 'Apply the power rule to x¹, then multiply by the constant 4.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = x² + 1/x.',
        o: ['2x − 1/x²', '2x + 1/x²', '2x − 1/x', 'x − 1/x²'],
        a: 0,
        e: 'Rewrite 1/x as x⁻¹. d/dx(x²) = 2x; d/dx(x⁻¹) = −x⁻² = −1/x². Total: 2x − 1/x².',
        h: 'Convert 1/x to x⁻¹ before applying the power rule.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 6x⁻².',
        o: ['−12x⁻³', '12x⁻³', '−6x⁻³', '−12x⁻¹'],
        a: 0,
        e: 'dy/dx = 6 × (−2)x⁻³ = −12x⁻³.',
        h: 'Apply the power rule directly since the expression is already in index form.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ⅓x³ − 2x + 5.',
        o: ['x² − 2', '⅓x² − 2', '3x² − 2', 'x² − 2x'],
        a: 0,
        e: 'd/dx(⅓x³) = ⅓ × 3x² = x²; d/dx(−2x) = −2; d/dx(5) = 0. Total: x² − 2.',
        h: 'The fractional coefficient ⅓ and the power 3 multiply to give exactly 1.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 5√x.',
        o: ['5/(2√x)', '5√x/2', '10√x', '5/√x'],
        a: 0,
        e: 'y = 5x¹ᐞ². dy/dx = 5 × ½x⁻¹ᐞ² = 2.5/√x = 5/(2√x).',
        h: 'Rewrite the root as a fractional power, then apply the constant multiple and power rules.',
        yr: 'SS2',
      },
      {
        q: 'Find dy/dx if y = 2x³ − 4x² + 6x − 1.',
        o: ['6x² − 8x + 6', '6x² − 8x', '6x² + 6x − 1', '6x − 8x + 6'],
        a: 0,
        e: 'Differentiate each term: 6x² − 8x + 6 − 0.',
        h: 'Apply power rule to each term, remembering the constant −1 disappears.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 1/x³.',
        o: ['−3/x⁴', '3/x⁴', '−3/x²', '−1/x⁴'],
        a: 0,
        e: 'Rewrite as y = x⁻³. dy/dx = −3x⁻⁴ = −3/x⁴.',
        h: 'Rewrite the fraction with a negative exponent before applying the power rule.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x² + 3)² by first expanding.',
        o: ['4x³ + 12x', '2x² + 6x', '4x³ + 6x', '2(x²+3)'],
        a: 0,
        e: 'Expand: y = x⁴ + 6x² + 9. Then dy/dx = 4x³ + 12x.',
        h: 'Expand the bracket fully into a polynomial before differentiating term by term.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 8.',
        o: ['0', '8', '8x', '1'],
        a: 0,
        e: 'A constant term always differentiates to 0.',
        h: 'Recall the constant rule: derivative of any constant is zero.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 4x⁴ − x³ + 2x² − x.',
        o: ['16x³ − 3x² + 4x − 1', '16x³ − 3x² + 4x', '4x³ − 3x² + 4x − 1', '16x³ − x² + 4x − 1'],
        a: 0,
        e: 'Differentiate each term: 16x³, −3x², 4x, −1. Combined: 16x³ − 3x² + 4x − 1.',
        h: 'Apply the power rule to all four terms carefully, one at a time.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 2/√x (i.e., 2x⁻¹ᐞ²).',
        o: ['−1/x¹ᐞ² (i.e. −x⁻³ᐞ²)', '1/x¹ᐞ²', '−2/x¹ᐞ²', '−1/(2x¹ᐞ²)'],
        a: 0,
        e: 'y = 2x⁻¹ᐞ². dy/dx = 2 × (−½)x⁻³ᐞ² = −x⁻³ᐞ² = −1/x¹ᐞ² (i.e., −1/√(x³)).',
        h: 'Rewrite as a power of x with a negative fractional exponent, then differentiate.',
        yr: 'SS2',
      },
      {
        q: 'Find the gradient function of y = x² − 6x + 9.',
        o: ['2x − 6', '2x − 9', 'x − 6', '2x + 6'],
        a: 0,
        e: 'Differentiating term by term: 2x − 6 + 0 = 2x − 6.',
        h: 'Apply power and constant rules to each of the three terms.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 10x¹ᐞ² (i.e., 10√x).',
        o: ['5x⁻¹ᐞ²', '5x¹ᐞ²', '10x⁻¹ᐞ²', '20x⁻¹ᐞ²'],
        a: 0,
        e: 'dy/dx = 10 × ½x⁻¹ᐞ² = 5x⁻¹ᐞ².',
        h: 'Apply the power rule with a fractional exponent of 1/2.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Rules of Differentiation II',
    topicCode: 'SS2-FMATH-14',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  When functions are multiplied, divided, or nested inside one another, the basic power rule alone is not enough. We need three more powerful rules: the <span class="learn-keyword">product rule</span>, <span class="learn-keyword">quotient rule</span>, and <span class="learn-keyword">chain rule</span>.</div>\n\n<h3 class="learn-subheading">1. The Product Rule</h3>\n<p class="learn-p">If y = u&middot;v (where u and v are both functions of x), then dy/dx = u&prime;v + uv&prime;.<br>\n<strong>Example:</strong> Differentiate y = x&sup2;(x + 1). Let u = x&sup2; (u&prime; = 2x), v = x + 1 (v&prime; = 1).<br>\ndy/dx = 2x(x+1) + x&sup2;(1) = 2x&sup2; + 2x + x&sup2; = 3x&sup2; + 2x.</p>\n\n<h3 class="learn-subheading">2. The Quotient Rule</h3>\n<p class="learn-p">If y = u/v, then dy/dx = (u&prime;v &minus; uv&prime;) / v&sup2;.<br>\n<strong>Example:</strong> Differentiate y = x/(x+1). Let u = x (u&prime;=1), v = x+1 (v&prime;=1).<br>\ndy/dx = [1(x+1) &minus; x(1)] / (x+1)&sup2; = [x+1&minus;x]/(x+1)&sup2; = 1/(x+1)&sup2;.</p>\n\n<h3 class="learn-subheading">3. The Chain Rule</h3>\n<p class="learn-p">If y = f(g(x)), then dy/dx = f&prime;(g(x))&middot;g&prime;(x) &mdash; "differentiate the outside, multiply by the derivative of the inside".<br>\n<strong>Example:</strong> Differentiate y = (3x + 2)&#8308;. Outer function: ( )&#8308;, inner: 3x+2.<br>\ndy/dx = 4(3x+2)&sup3; &times; 3 = 12(3x+2)&sup3;.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Rule</th><th>Formula</th><th>When to Use</th></tr></thead>\n    <tbody>\n      <tr><td>Product</td><td>u&prime;v + uv&prime;</td><td>Two functions multiplied</td></tr>\n      <tr><td>Quotient</td><td>(u&prime;v &minus; uv&prime;)/v&sup2;</td><td>One function divided by another</td></tr>\n      <tr><td>Chain</td><td>f&prime;(g(x))&middot;g&prime;(x)</td><td>A function nested inside another (composite)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> For the quotient rule, the order in the numerator matters &mdash; it\'s u&prime;v &minus; uv&prime;, NOT uv&prime; &minus; u&prime;v. Getting the sign backwards is the single most common mistake with this rule.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Product rule for multiplied functions, quotient rule for divided functions, chain rule for nested (composite) functions. Identify the structure of the expression first, then choose the matching rule.\n</div>\n',
    questions: [
      {
        q: 'Differentiate y = x²(x + 1) using the product rule.',
        o: ['3x² + 2x', '2x² + 2x', '3x² + x', 'x² + 2x'],
        a: 0,
        e: "u=x² (u'=2x), v=x+1 (v'=1). dy/dx = u'v+uv' = 2x(x+1)+x²(1) = 2x²+2x+x² = 3x²+2x.",
        h: "Identify u and v, find their derivatives separately, then apply u'v + uv'.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = x/(x+1) using the quotient rule.',
        o: ['1/(x+1)²', '1/(x+1)', 'x/(x+1)²', '−1/(x+1)²'],
        a: 0,
        e: "u=x (u'=1), v=x+1 (v'=1). dy/dx = (u'v−uv')/v² = [(1)(x+1)−x(1)]/(x+1)² = 1/(x+1)².",
        h: "Apply the quotient rule formula (u'v − uv')/v² carefully, watching the order of subtraction.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (3x + 2)⁴ using the chain rule.',
        o: ['12(3x+2)³', '4(3x+2)³', '3(3x+2)³', '12(3x+2)⁴'],
        a: 0,
        e: 'Outer derivative: 4(3x+2)³. Inner derivative: 3. By chain rule: dy/dx = 4(3x+2)³ × 3 = 12(3x+2)³.',
        h: 'Differentiate the outer power function first, then multiply by the derivative of the inner expression.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (2x − 1)³(x + 4) using the product rule.',
        o: ['6(2x−1)²(x+4) + (2x−1)³', '6(2x−1)²(x+4)', '(2x−1)³', '3(2x−1)²(x+4)'],
        a: 0,
        e: "u=(2x−1)³, u'=3(2x−1)²×2=6(2x−1)² (chain rule); v=x+4, v'=1. dy/dx = 6(2x−1)²(x+4) + (2x−1)³(1).",
        h: 'Combine the product rule with the chain rule when differentiating u.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = x²/(x − 1) using the quotient rule.',
        o: ['(x² − 2x)/(x−1)²', '(2x² − x)/(x−1)²', '2x/(x−1)', '(x²−2x)/(x−1)'],
        a: 0,
        e: "u=x² (u'=2x), v=x−1 (v'=1). dy/dx = [2x(x−1)−x²(1)]/(x−1)² = (2x²−2x−x²)/(x−1)² = (x²−2x)/(x−1)².",
        h: 'Apply the quotient rule formula, then simplify the numerator by expanding and collecting terms.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x² + 1)³ using the chain rule.',
        o: ['6x(x²+1)²', '3(x²+1)²', '6x(x²+1)³', '2x(x²+1)²'],
        a: 0,
        e: 'Outer derivative: 3(x²+1)². Inner derivative: 2x. By chain rule: dy/dx = 3(x²+1)² × 2x = 6x(x²+1)².',
        h: 'Differentiate the cube of the bracket, then multiply by the derivative of x²+1.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (2x+3)(x²−1) using the product rule.',
        o: ['6x²+6x−2', '2x²+6x−2', '6x²+6x', '6x²−2'],
        a: 0,
        e: "u=2x+3 (u'=2), v=x²−1 (v'=2x). dy/dx = 2(x²−1)+(2x+3)(2x) = 2x²−2+4x²+6x = 6x²+6x−2.",
        h: "Apply u'v + uv', then expand and combine like terms.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 1/(x²+1) using the quotient rule.',
        o: ['−2x/(x²+1)²', '2x/(x²+1)²', '−1/(x²+1)²', '−2x/(x²+1)'],
        a: 0,
        e: "u=1 (u'=0), v=x²+1 (v'=2x). dy/dx = [0(x²+1)−1(2x)]/(x²+1)² = −2x/(x²+1)².",
        h: "Note that u = 1 is a constant, so u' = 0, simplifying the numerator.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = √(2x + 1) using the chain rule.',
        o: ['1/√(2x+1)', '2/√(2x+1)', '1/(2√(2x+1))', '√2/√(2x+1)'],
        a: 0,
        e: 'y=(2x+1)¹ᐞ². dy/dx = ½(2x+1)⁻¹ᐞ² × 2 = (2x+1)⁻¹ᐞ² = 1/√(2x+1).',
        h: 'Rewrite the root as a power, apply the chain rule, then simplify.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = x³(2x−1)² using the product rule (with chain rule inside).',
        o: ['3x²(2x−1)² + 4x³(2x−1)', '3x²(2x−1)²', '4x³(2x−1)', '6x²(2x−1)²'],
        a: 0,
        e: "u=x³ (u'=3x²), v=(2x−1)² (v'=2(2x−1)×2=4(2x−1), chain rule). dy/dx = 3x²(2x−1)² + x³×4(2x−1) = 3x²(2x−1)²+4x³(2x−1).",
        h: "Find v' using the chain rule since v is itself a composite function.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x+2)/(x−3) using the quotient rule.',
        o: ['−5/(x−3)²', '5/(x−3)²', '1/(x−3)²', '−1/(x−3)²'],
        a: 0,
        e: "u=x+2(u'=1), v=x−3(v'=1). dy/dx = [1(x−3)−1(x+2)]/(x−3)² = [x−3−x−2]/(x−3)² = −5/(x−3)².",
        h: 'Apply the quotient rule and simplify the numerator carefully with correct signs.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (5 − 2x)³ using the chain rule.',
        o: ['−6(5−2x)²', '6(5−2x)²', '3(5−2x)²', '−3(5−2x)²'],
        a: 0,
        e: 'Outer: 3(5−2x)². Inner derivative: −2. dy/dx = 3(5−2x)² × (−2) = −6(5−2x)².',
        h: "Remember the inner function's derivative is negative here (−2), affecting the overall sign.",
        yr: 'SS2',
      },
      {
        q: 'Which rule should you use to differentiate y = x² sin x?',
        o: ['Product rule', 'Quotient rule', 'Chain rule only', 'Power rule only'],
        a: 0,
        e: 'This is a product of two functions of x (x² and sin x), so the product rule applies: dy/dx = 2x sin x + x² cos x.',
        h: 'Identify the structure: two functions multiplied together signals the product rule.',
        yr: 'SS2',
      },
      {
        q: 'Which rule should you use to differentiate y = sin(3x)?',
        o: ['Chain rule', 'Product rule', 'Quotient rule', 'Power rule only'],
        a: 0,
        e: 'sin(3x) is a composite function (sine of an inner function 3x), so the chain rule applies.',
        h: "A function 'inside' another function signals the chain rule.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x−1)²(x+1)² using the product rule.',
        o: ['2(x−1)(x+1)² + 2(x−1)²(x+1)', '2(x−1)(x+1)²', '4(x−1)(x+1)', '2(x−1)²(x+1)'],
        a: 0,
        e: "u=(x−1)² (u'=2(x−1)), v=(x+1)² (v'=2(x+1)). dy/dx = 2(x−1)(x+1)² + (x−1)²×2(x+1) = 2(x−1)(x+1)² + 2(x−1)²(x+1).",
        h: 'Use the chain rule to differentiate each squared bracket, then apply the product rule.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 3/(2x+1)² using the chain rule (rewriting as a power first).',
        o: ['−12/(2x+1)³', '12/(2x+1)³', '−6/(2x+1)³', '−3/(2x+1)³'],
        a: 0,
        e: 'y=3(2x+1)⁻². dy/dx = 3×(−2)(2x+1)⁻³×2 = −12(2x+1)⁻³ = −12/(2x+1)³.',
        h: 'Rewrite the fraction as a negative power, then apply the chain rule.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x²+3)/x using the quotient rule.',
        o: ['1 − 3/x²', '1 + 3/x²', '2x/x', '(2x)/x²'],
        a: 0,
        e: "u=x²+3(u'=2x), v=x(v'=1). dy/dx = [2x(x)−(x²+3)(1)]/x² = [2x²−x²−3]/x² = (x²−3)/x² = 1 − 3/x².",
        h: 'Apply the quotient rule then simplify the resulting fraction by splitting it.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (4x−5)⁵ using the chain rule.',
        o: ['20(4x−5)⁴', '5(4x−5)⁴', '4(4x−5)⁴', '20(4x−5)⁵'],
        a: 0,
        e: 'Outer: 5(4x−5)⁴. Inner derivative: 4. dy/dx = 5(4x−5)⁴ × 4 = 20(4x−5)⁴.',
        h: "Multiply the outer power's derivative by the inner function's derivative (4).",
        yr: 'SS2',
      },
      {
        q: 'For y = uv, if u = x and v = x, what does the product rule give (verifying against the power rule for x²)?',
        o: ['2x, matching d/dx(x²) = 2x', 'x²', '1', '2'],
        a: 0,
        e: "u'=1, v'=1. dy/dx = u'v+uv' = (1)(x)+(x)(1) = 2x, which correctly matches the known derivative of x² = x×x.",
        h: 'This is a good consistency check: the product rule should agree with the simple power rule when u = v = x.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = (x² − 1)⁴ using the chain rule.',
        o: ['8x(x²−1)³', '4x(x²−1)³', '8x(x²−1)⁴', '4(x²−1)³'],
        a: 0,
        e: 'Outer: 4(x²−1)³. Inner derivative: 2x. dy/dx = 4(x²−1)³ × 2x = 8x(x²−1)³.',
        h: 'Differentiate the outer 4th power first, then multiply by the derivative of the inner x² − 1.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Differentiation of Trigonometric, Logarithmic, and Exponential Functions',
    topicCode: 'SS2-FMATH-15',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Beyond algebraic functions, Further Mathematics requires differentiating <span class="learn-keyword">trigonometric</span>, <span class="learn-keyword">logarithmic</span>, and <span class="learn-keyword">exponential</span> functions &mdash; each with its own standard derivative that must be memorized and combined with the chain rule when needed.</div>\n\n<h3 class="learn-subheading">1. Standard Trigonometric Derivatives</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Function</th><th>Derivative</th></tr></thead>\n    <tbody>\n      <tr><td>sin x</td><td>cos x</td></tr>\n      <tr><td>cos x</td><td>&minus;sin x</td></tr>\n      <tr><td>tan x</td><td>sec&sup2;x</td></tr>\n    </tbody>\n  </table>\n</div>\n<p class="learn-p"><strong>Example:</strong> Differentiate y = sin(3x) (chain rule): dy/dx = 3cos(3x).</p>\n\n<h3 class="learn-subheading">2. Exponential Functions</h3>\n<p class="learn-p">If y = e&#8319;, dy/dx = e&#8319; (its own derivative &mdash; unique to base e). If y = e<sup>kx</sup>, dy/dx = ke<sup>kx</sup> (chain rule).<br>\n<strong>Example:</strong> y = e&#8309;&#8319; &rArr; dy/dx = 5e&#8309;&#8319;.</p>\n\n<h3 class="learn-subheading">3. Logarithmic Functions</h3>\n<p class="learn-p">If y = ln x (natural log), dy/dx = 1/x. If y = ln(f(x)), dy/dx = f&prime;(x)/f(x) (chain rule).<br>\n<strong>Example:</strong> y = ln(2x + 1) &rArr; dy/dx = 2/(2x+1).</p>\n\n<h3 class="learn-subheading">4. Combining with the Chain Rule</h3>\n<p class="learn-p"><strong>Example:</strong> Differentiate y = e<sup>x&sup2;</sup>. Outer: e<sup>(&middot;)</sup>, inner: x&sup2; (derivative 2x). dy/dx = 2x&middot;e<sup>x&sup2;</sup>.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Whenever the angle, exponent, or log argument is anything other than plain x, you MUST apply the chain rule and multiply by the derivative of that inner expression &mdash; forgetting this multiplier is the most common error in this topic.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Memorize: d/dx(sin x)=cos x, d/dx(cos x)=&minus;sin x, d/dx(e&#8319;)=e&#8319;, d/dx(ln x)=1/x. Always apply the chain rule when the input is a function of x rather than plain x.\n</div>\n',
    questions: [
      {
        q: 'Differentiate y = sin x.',
        o: ['cos x', '−cos x', '−sin x', 'tan x'],
        a: 0,
        e: 'The standard derivative of sin x is cos x.',
        h: 'This is one of the fundamental trigonometric derivatives to memorize.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = cos x.',
        o: ['−sin x', 'sin x', '−cos x', 'tan x'],
        a: 0,
        e: 'The standard derivative of cos x is −sin x (note the negative sign).',
        h: "Remember cosine's derivative includes a negative sign, unlike sine's.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = sin(3x) using the chain rule.',
        o: ['3cos(3x)', 'cos(3x)', '3sin(3x)', 'cos(3)'],
        a: 0,
        e: 'Outer derivative: cos(3x). Inner derivative: 3. dy/dx = 3cos(3x).',
        h: 'Multiply the standard derivative of sine by the derivative of the inner function 3x.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = eˣ.',
        o: ['eˣ', 'xeˣ⁻¹', 'eˣ⁻¹', '1'],
        a: 0,
        e: 'The exponential function eˣ is unique in that it is its own derivative.',
        h: 'eˣ has the special property of being unchanged by differentiation.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = e⁵ˣ.',
        o: ['5e⁵ˣ', 'e⁵ˣ', '5eˣ', 'eˣ'],
        a: 0,
        e: 'By the chain rule: dy/dx = 5e⁵ˣ (multiply by the derivative of the exponent, 5).',
        h: 'Multiply e raised to the exponent by the derivative of the exponent itself.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ln x.',
        o: ['1/x', 'x', 'ln x', '1'],
        a: 0,
        e: 'The standard derivative of the natural logarithm ln x is 1/x.',
        h: 'This is one of the fundamental logarithmic derivatives to memorize.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ln(2x + 1) using the chain rule.',
        o: ['2/(2x+1)', '1/(2x+1)', '2x/(2x+1)', 'ln(2)'],
        a: 0,
        e: "By the chain rule for logs: dy/dx = f'(x)/f(x) = 2/(2x+1).",
        h: 'Divide the derivative of the inner expression by the inner expression itself.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = tan x.',
        o: ['sec²x', 'sin x', '−cosec²x', 'cos x'],
        a: 0,
        e: 'The standard derivative of tan x is sec²x.',
        h: 'This is the third fundamental trigonometric derivative.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = cos(4x) using the chain rule.',
        o: ['−4sin(4x)', '4sin(4x)', '−sin(4x)', '4cos(4x)'],
        a: 0,
        e: 'Outer derivative: −sin(4x). Inner derivative: 4. dy/dx = −4sin(4x).',
        h: "Remember the negative sign from cosine's derivative, then multiply by the inner derivative.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = e^(x²) using the chain rule.',
        o: ['2xe^(x²)', 'e^(x²)', 'x²e^(x²)', '2e^(x²)'],
        a: 0,
        e: 'The exponential stays the same, multiplied by the derivative of the exponent (2x). dy/dx = 2x·e^(x²).',
        h: 'The derivative of e to a function is that same exponential times the derivative of the exponent.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ln(x² + 1) using the chain rule.',
        o: ['2x/(x²+1)', '1/(x²+1)', '2x', 'x/(x²+1)'],
        a: 0,
        e: "dy/dx = f'(x)/f(x) = 2x/(x²+1).",
        h: 'Divide the derivative of x²+1 (which is 2x) by x²+1 itself.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 3sin x + 2cos x.',
        o: ['3cos x − 2sin x', '3cos x + 2sin x', '−3sin x + 2cos x', '3cos x − 2cos x'],
        a: 0,
        e: 'd/dx(3sin x) = 3cos x. d/dx(2cos x) = −2sin x. Total: 3cos x − 2sin x.',
        h: 'Differentiate each term separately, remembering the sign change for cosine.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = e^(−2x).',
        o: ['−2e^(−2x)', '2e^(−2x)', 'e^(−2x)', '−2eˣ'],
        a: 0,
        e: 'By the chain rule: dy/dx = −2e^(−2x) (multiply by the derivative of the exponent, −2).',
        h: 'The derivative of the exponent −2x is −2; multiply this into the result.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ln(5x).',
        o: ['1/x', '5/x', '1/(5x)', '5/(5x)'],
        a: 0,
        e: "dy/dx = f'(x)/f(x) = 5/(5x) = 1/x. (This also follows from ln(5x) = ln5 + lnx, whose derivative is just 1/x since ln5 is constant.)",
        h: 'Apply the chain rule, or use log laws to split ln(5x) first.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = sin²x (i.e., (sin x)²) using the chain rule.',
        o: ['2sin x cos x', 'cos²x', '2cos x', 'sin x cos x'],
        a: 0,
        e: 'Let u = sin x. y = u², dy/du = 2u = 2sin x. du/dx = cos x. By chain rule: dy/dx = 2sin x × cos x.',
        h: "Treat sin²x as (sin x)² and apply the chain rule with the outer function being 'squaring'.",
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = eˣ sin x using the product rule.',
        o: ['eˣ sin x + eˣ cos x', 'eˣ cos x', 'eˣ sin x', 'eˣ(sin x − cos x)'],
        a: 0,
        e: "u=eˣ(u'=eˣ), v=sin x(v'=cos x). By product rule: dy/dx = eˣsin x + eˣcos x = eˣ(sin x + cos x).",
        h: 'Combine the product rule with the standard derivatives of eˣ and sin x.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = ln(cos x) using the chain rule.',
        o: ['−tan x', 'tan x', '−sin x/cos x is different from this', '1/cos x'],
        a: 0,
        e: "dy/dx = f'(x)/f(x) where f(x)=cos x, f'(x)=−sin x. So dy/dx = −sin x/cos x = −tan x.",
        h: 'Divide the derivative of cos x by cos x itself, then simplify using the tan identity.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = 4e^(3x) − 2x.',
        o: ['12e^(3x) − 2', '4e^(3x) − 2', '12e^(3x)', '4e^(3x) − 2x'],
        a: 0,
        e: 'd/dx(4e^(3x)) = 4×3e^(3x) = 12e^(3x). d/dx(−2x) = −2. Total: 12e^(3x) − 2.',
        h: 'Differentiate each term separately using the exponential chain rule and power rule.',
        yr: 'SS2',
      },
      {
        q: 'Differentiate y = tan(2x) using the chain rule.',
        o: ['2sec²(2x)', 'sec²(2x)', '2tan(2x)', 'sec²(2)'],
        a: 0,
        e: 'Outer derivative: sec²(2x). Inner derivative: 2. dy/dx = 2sec²(2x).',
        h: 'Multiply the standard tan derivative by the derivative of the inner function 2x.',
        yr: 'SS2',
      },
      {
        q: 'At x = 0, find the gradient of y = eˣ.',
        o: ['1', '0', 'e', 'Undefined'],
        a: 0,
        e: 'dy/dx = eˣ. At x=0: e⁰ = 1.',
        h: 'Substitute x = 0 into the derivative, remembering e⁰ = 1.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Application of Differentiation (Rate of Change, Velocity, Acceleration)',
    topicCode: 'SS2-FMATH-16',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Differentiation isn\'t just an algebraic exercise &mdash; it measures <span class="learn-keyword">rates of change</span>. In physics and everyday problems, this gives us velocity, acceleration, and other rates such as how fast a quantity grows or shrinks.</div>\n\n<h3 class="learn-subheading">1. Rate of Change</h3>\n<p class="learn-p">If y = f(x), then dy/dx gives the rate at which y changes with respect to x. <strong>Example:</strong> If the area of a circle A = &pi;r&sup2;, then dA/dr = 2&pi;r gives the rate of change of area with respect to radius.</p>\n\n<h3 class="learn-subheading">2. Velocity and Acceleration</h3>\n<p class="learn-p">If s(t) is displacement as a function of time t: <br>\nVelocity v = ds/dt (first derivative of displacement).<br>\nAcceleration a = dv/dt = d&sup2;s/dt&sup2; (derivative of velocity, or second derivative of displacement).</p>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">A particle moves so that its displacement is s = t&sup3; &minus; 6t&sup2; + 9t (in metres, t in seconds).<br>\nVelocity: v = ds/dt = 3t&sup2; &minus; 12t + 9.<br>\nAcceleration: a = dv/dt = 6t &minus; 12.<br>\nAt t = 2: v = 3(4) &minus; 24 + 9 = &minus;3 m/s; a = 12 &minus; 12 = 0 m/s&sup2;.</p>\n\n<h3 class="learn-subheading">4. Connected Rates of Change</h3>\n<p class="learn-p">When two quantities are related and both change with time, use the chain rule: dy/dt = (dy/dx)&times;(dx/dt). <strong>Example:</strong> If the radius of a circle increases at 2 cm/s, find the rate of increase of area when r = 5 cm.<br>\ndA/dr = 2&pi;r = 10&pi;. dA/dt = dA/dr &times; dr/dt = 10&pi; &times; 2 = 20&pi; cm&sup2;/s.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> When velocity = 0, the particle is momentarily at rest (a turning point in displacement). When velocity is negative, the particle moves in the negative direction. Always state units in your final answer.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Differentiation gives instantaneous rates of change. In kinematics, velocity is the derivative of displacement, and acceleration is the derivative of velocity (second derivative of displacement). Connected rates use the chain rule to relate two changing quantities.\n</div>\n',
    questions: [
      {
        q: 'A particle has displacement s = t³ − 6t² + 9t. Find its velocity function.',
        o: ['v = 3t² − 12t + 9', 'v = 3t² − 6t + 9', 'v = t² − 12t', 'v = 3t² − 12t'],
        a: 0,
        e: 'Velocity is the derivative of displacement: v = ds/dt = 3t² − 12t + 9.',
        h: 'Differentiate the displacement function once with respect to t.',
        yr: 'SS2',
      },
      {
        q: 'Using v = 3t² − 12t + 9, find the acceleration function.',
        o: ['a = 6t − 12', 'a = 3t − 12', 'a = 6t² − 12', 'a = 6t + 12'],
        a: 0,
        e: 'Acceleration is the derivative of velocity: a = dv/dt = 6t − 12.',
        h: 'Differentiate the velocity function once more with respect to t.',
        yr: 'SS2',
      },
      {
        q: 'For s = t³ − 6t² + 9t, find the velocity at t = 2.',
        o: ['−3 m/s', '3 m/s', '0 m/s', '−12 m/s'],
        a: 0,
        e: 'v = 3t²−12t+9. At t=2: v=3(4)−12(2)+9 = 12−24+9 = −3 m/s.',
        h: 'Substitute t = 2 into the velocity function found earlier.',
        yr: 'SS2',
      },
      {
        q: 'For s = t³ − 6t² + 9t, find the acceleration at t = 2.',
        o: ['0 m/s²', '12 m/s²', '−3 m/s²', '6 m/s²'],
        a: 0,
        e: 'a = 6t−12. At t=2: a = 12−12 = 0 m/s².',
        h: 'Substitute t = 2 into the acceleration function.',
        yr: 'SS2',
      },
      {
        q: 'The area of a circle is A = πr². Find dA/dr.',
        o: ['2πr', 'πr²', 'πr', '2π'],
        a: 0,
        e: 'Differentiating A = πr² with respect to r: dA/dr = 2πr.',
        h: 'Treat π as a constant coefficient and apply the power rule to r².',
        yr: 'SS2',
      },
      {
        q: "A circle's radius increases at 2 cm/s. Find the rate of increase of its area when r = 5 cm.",
        o: ['20π cm²/s', '10π cm²/s', '25π cm²/s', '5π cm²/s'],
        a: 0,
        e: 'dA/dr = 2πr = 2π(5) = 10π. dA/dt = dA/dr × dr/dt = 10π × 2 = 20π cm²/s.',
        h: 'Use the chain rule: dA/dt = (dA/dr) × (dr/dt).',
        yr: 'SS2',
      },
      {
        q: "A particle's velocity is given by v = 4t − 8. At what time is the particle momentarily at rest?",
        o: ['t = 2 s', 't = 4 s', 't = 8 s', 't = 0 s'],
        a: 0,
        e: 'At rest means v = 0: 4t − 8 = 0 ⇒ t = 2 s.',
        h: 'Set the velocity function equal to zero and solve for t.',
        yr: 'SS2',
      },
      {
        q: 'A particle has displacement s = 2t² − 3t + 1. Find its initial velocity (at t = 0).',
        o: ['−3 m/s', '1 m/s', '0 m/s', '2 m/s'],
        a: 0,
        e: 'v = ds/dt = 4t − 3. At t=0: v = −3 m/s.',
        h: 'Differentiate first, then substitute t = 0.',
        yr: 'SS2',
      },
      {
        q: 'The volume of a sphere is V = (4/3)πr³. Find dV/dr.',
        o: ['4πr²', '(4/3)πr²', '4πr', '(4/3)πr³'],
        a: 0,
        e: 'dV/dr = (4/3)π × 3r² = 4πr².',
        h: 'Apply the power rule to r³, keeping the constant (4/3)π in front.',
        yr: 'SS2',
      },
      {
        q: "A sphere's radius increases at 3 cm/s. Find the rate of increase of volume when r = 2 cm (using dV/dr = 4πr²).",
        o: ['48π cm³/s', '16π cm³/s', '12π cm³/s', '24π cm³/s'],
        a: 0,
        e: 'dV/dr = 4π(2)² = 16π. dV/dt = dV/dr × dr/dt = 16π × 3 = 48π cm³/s.',
        h: 'Find dV/dr first at r=2, then multiply by the given dr/dt.',
        yr: 'SS2',
      },
      {
        q: "A particle's displacement is s = 5t − t². Find the maximum height reached (when velocity = 0).",
        o: ['6.25 m (at t = 2.5s)', '5 m', '2.5 m', '12.5 m'],
        a: 0,
        e: 'v = 5−2t = 0 ⇒ t=2.5. Then s = 5(2.5)−(2.5)² = 12.5−6.25 = 6.25 m.',
        h: 'Find when v = 0 first, then substitute that time back into the displacement function.',
        yr: 'SS2',
      },
      {
        q: 'A quantity y depends on x as y = x², and x is increasing at 3 units/s. Find dy/dt when x = 4.',
        o: ['24 units/s', '8 units/s', '16 units/s', '12 units/s'],
        a: 0,
        e: 'dy/dx = 2x = 2(4) = 8. dy/dt = dy/dx × dx/dt = 8 × 3 = 24 units/s.',
        h: 'Use the chain rule: dy/dt = (dy/dx)(dx/dt).',
        yr: 'SS2',
      },
      {
        q: "A particle's acceleration is constant at a = 6 m/s², and velocity is v = 6t + 2. Find the displacement function s (given s = 0 at t = 0), knowing s is the integral of v — but here just identify the general form.",
        o: ['s = 3t² + 2t', 's = 6t² + 2t', 's = 3t² + 2', 's = 6t + 2t²'],
        a: 0,
        e: 'Since v = ds/dt = 6t+2, reversing (anti-differentiating) gives s = 3t² + 2t + C. With s=0 at t=0, C=0, so s = 3t²+2t.',
        h: 'This previews integration — differentiation and its reverse are closely connected.',
        yr: 'SS2',
      },
      {
        q: 'For s = t³ − 3t, find the values of t where the particle is momentarily at rest.',
        o: ['t = 1 and t = −1', 't = 0 and t = 3', 't = 1 only', 't = 3 only'],
        a: 0,
        e: 'v = 3t² − 3 = 0 ⇒ t² = 1 ⇒ t = ±1. (Only t = 1 is physically meaningful if t ≥ 0 is required, but both are mathematical solutions.)',
        h: 'Set v = 3t² − 3 equal to zero and solve the resulting equation.',
        yr: 'SS2',
      },
      {
        q: 'The cost of production is C = 200 + 5x + 0.1x² (x = number of units). Find the marginal cost (rate of change of cost) at x = 50.',
        o: ['15', '5', '10', '205'],
        a: 0,
        e: 'dC/dx = 5 + 0.2x. At x=50: dC/dx = 5 + 0.2(50) = 5+10 = 15.',
        h: 'Differentiate the cost function, then substitute x = 50.',
        yr: 'SS2',
      },
      {
        q: 'A particle moves with displacement s = t² − 4t + 3. Find its velocity when t = 5.',
        o: ['6 m/s', '3 m/s', '−4 m/s', '10 m/s'],
        a: 0,
        e: 'v = 2t − 4. At t=5: v = 10−4 = 6 m/s.',
        h: 'Differentiate the displacement, then substitute t = 5.',
        yr: 'SS2',
      },
      {
        q: 'For a particle with constant acceleration, which statement is TRUE?',
        o: [
          'The velocity function is linear in t',
          'The displacement function is linear in t',
          'The acceleration function is quadratic in t',
          'The velocity is always zero',
        ],
        a: 0,
        e: 'If acceleration is constant, then velocity (its antiderivative) increases or decreases linearly with time, since integrating a constant gives a linear function.',
        h: 'Think about what function, when differentiated, gives a constant.',
        yr: 'SS2',
      },
      {
        q: 'The perimeter of a square is P = 4x, where x is the side length increasing at 2 cm/s. Find dP/dt.',
        o: ['8 cm/s', '4 cm/s', '2 cm/s', '16 cm/s'],
        a: 0,
        e: 'dP/dx = 4. dP/dt = dP/dx × dx/dt = 4 × 2 = 8 cm/s.',
        h: 'Use the chain rule, relating the rate of perimeter change to the rate of side-length change.',
        yr: 'SS2',
      },
      {
        q: 'A ball is thrown so its height is h = 20t − 5t². Find the time at which it reaches maximum height.',
        o: ['t = 2 s', 't = 4 s', 't = 5 s', 't = 1 s'],
        a: 0,
        e: 'v = dh/dt = 20 − 10t = 0 ⇒ t = 2 s.',
        h: 'Maximum height occurs when the velocity (rate of change of height) is zero.',
        yr: 'SS2',
      },
      {
        q: 'For the ball with h = 20t − 5t², find the maximum height reached.',
        o: ['20 m', '40 m', '10 m', '15 m'],
        a: 0,
        e: 'At t=2 (from previous question): h = 20(2) − 5(4) = 40 − 20 = 20 m.',
        h: 'Substitute the time of maximum height (t=2) back into the height function.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Maximum and Minimum Points and Values',
    topicCode: 'SS2-FMATH-17',
    module: 'Calculus',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Turning points</span> &mdash; the peaks and valleys of a curve &mdash; are where the gradient (first derivative) equals zero. Using the second derivative, we can classify these as maximum or minimum points, essential for optimization problems.</div>\n\n<h3 class="learn-subheading">1. Finding Turning Points</h3>\n<p class="learn-p">Set dy/dx = 0 and solve for x. Each solution gives the x-coordinate of a turning point.</p>\n\n<h3 class="learn-subheading">2. The Second Derivative Test</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Second Derivative</th><th>Conclusion</th></tr></thead>\n    <tbody>\n      <tr><td>d&sup2;y/dx&sup2; &gt; 0</td><td>Minimum point (curve is concave up, like a valley)</td></tr>\n      <tr><td>d&sup2;y/dx&sup2; &lt; 0</td><td>Maximum point (curve is concave down, like a peak)</td></tr>\n      <tr><td>d&sup2;y/dx&sup2; = 0</td><td>Inconclusive &mdash; check further (point of inflection possible)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">Find and classify the turning points of y = x&sup3; &minus; 3x&sup2; &minus; 9x + 5.<br>\ndy/dx = 3x&sup2; &minus; 6x &minus; 9 = 0 &rArr; 3(x&sup2; &minus; 2x &minus; 3) = 0 &rArr; 3(x&minus;3)(x+1) = 0 &rArr; x = 3 or x = &minus;1.<br>\nd&sup2;y/dx&sup2; = 6x &minus; 6.<br>\nAt x = 3: d&sup2;y/dx&sup2; = 12 &gt; 0 &rArr; minimum. y = 27&minus;27&minus;27+5 = &minus;22.<br>\nAt x = &minus;1: d&sup2;y/dx&sup2; = &minus;12 &lt; 0 &rArr; maximum. y = &minus;1&minus;3+9+5 = 10.</p>\n\n<h3 class="learn-subheading">4. Optimization Problems</h3>\n<p class="learn-p"><strong>Example:</strong> A farmer has 40 m of fencing to enclose a rectangular field against a wall (one side doesn\'t need fencing). Maximize the area.<br>\nLet width = x, length = 40 &minus; 2x. Area A = x(40&minus;2x) = 40x &minus; 2x&sup2;.<br>\ndA/dx = 40 &minus; 4x = 0 &rArr; x = 10. Maximum area = 10(20) = 200 m&sup2;.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always classify EVERY turning point using the second derivative test &mdash; don\'t assume. In optimization problems, define one variable in terms of the other using the given constraint before differentiating.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Turning points occur where dy/dx = 0. The sign of d&sup2;y/dx&sup2; at that point tells us whether it\'s a maximum (negative) or minimum (positive). This technique is the basis of all optimization problems.\n</div>\n',
    questions: [
      {
        q: 'Find the x-coordinate(s) of the turning point(s) of y = x² − 4x + 1.',
        o: ['x = 2', 'x = 4', 'x = 1', 'x = −2'],
        a: 0,
        e: 'dy/dx = 2x − 4 = 0 ⇒ x = 2.',
        h: 'Set the first derivative equal to zero and solve for x.',
        yr: 'SS2',
      },
      {
        q: 'Using the second derivative test, classify the turning point of y = x² − 4x + 1 at x = 2.',
        o: [
          'Minimum, since d²y/dx² = 2 > 0',
          'Maximum, since d²y/dx² = 2 > 0',
          'Minimum, since d²y/dx² = 2 < 0',
          'Inconclusive',
        ],
        a: 0,
        e: 'd²y/dx² = 2, which is always positive, so the turning point is a minimum.',
        h: 'A positive second derivative always indicates a minimum point.',
        yr: 'SS2',
      },
      {
        q: 'Find the turning points of y = x³ − 3x² − 9x + 5.',
        o: ['x = 3 and x = −1', 'x = 3 and x = 1', 'x = −3 and x = 1', 'x = 9 and x = −1'],
        a: 0,
        e: 'dy/dx = 3x² − 6x − 9 = 3(x²−2x−3) = 3(x−3)(x+1) = 0 ⇒ x = 3 or x = −1.',
        h: 'Set the first derivative to zero and factor the resulting quadratic.',
        yr: 'SS2',
      },
      {
        q: 'For y = x³ − 3x² − 9x + 5, classify the turning point at x = 3.',
        o: [
          'Minimum, since d²y/dx² = 12 > 0',
          'Maximum, since d²y/dx² = 12 > 0',
          'Minimum, since d²y/dx² = −12',
          'Cannot be determined',
        ],
        a: 0,
        e: 'd²y/dx² = 6x − 6. At x=3: 18−6=12>0, so this is a minimum.',
        h: 'Substitute x = 3 into the second derivative and check the sign.',
        yr: 'SS2',
      },
      {
        q: 'For y = x³ − 3x² − 9x + 5, classify the turning point at x = −1.',
        o: [
          'Maximum, since d²y/dx² = −12 < 0',
          'Minimum, since d²y/dx² = −12 < 0',
          'Maximum, since d²y/dx² = 12',
          'Cannot be determined',
        ],
        a: 0,
        e: 'd²y/dx² = 6x − 6. At x=−1: −6−6=−12<0, so this is a maximum.',
        h: 'Substitute x = −1 into the second derivative and check the sign.',
        yr: 'SS2',
      },
      {
        q: 'Find the maximum value of y = x³ − 3x² − 9x + 5 (at x = −1).',
        o: ['10', '−22', '5', '16'],
        a: 0,
        e: 'y(−1) = (−1)³ −3(−1)² −9(−1)+5 = −1−3+9+5 = 10.',
        h: 'Substitute x = −1 into the original function y.',
        yr: 'SS2',
      },
      {
        q: 'A farmer has 40 m of fencing to enclose a rectangular field against a wall (one side unfenced). If width = x and length = 40−2x, find the value of x that maximizes the area.',
        o: ['x = 10', 'x = 20', 'x = 5', 'x = 40'],
        a: 0,
        e: 'A = x(40−2x) = 40x−2x². dA/dx = 40−4x = 0 ⇒ x = 10.',
        h: 'Write the area as a function of x, differentiate, and set equal to zero.',
        yr: 'SS2',
      },
      {
        q: 'Using the setup from the previous question, find the maximum area.',
        o: ['200 m²', '400 m²', '100 m²', '800 m²'],
        a: 0,
        e: 'At x=10, length = 40−20=20. Area = 10×20 = 200 m².',
        h: 'Substitute x = 10 back into the area formula A = x(40−2x).',
        yr: 'SS2',
      },
      {
        q: 'Find the turning point of y = 12x − x².',
        o: ['x = 6', 'x = 12', 'x = 3', 'x = 24'],
        a: 0,
        e: 'dy/dx = 12 − 2x = 0 ⇒ x = 6.',
        h: 'Set the derivative of the quadratic equal to zero.',
        yr: 'SS2',
      },
      {
        q: 'Classify the turning point of y = 12x − x² at x = 6.',
        o: [
          'Maximum, since d²y/dx² = −2 < 0',
          'Minimum, since d²y/dx² = −2 < 0',
          'Maximum, since d²y/dx² = 2',
          'Cannot be determined',
        ],
        a: 0,
        e: 'd²y/dx² = −2, which is always negative, so this is a maximum point.',
        h: 'A constant negative second derivative always indicates a maximum.',
        yr: 'SS2',
      },
      {
        q: 'An open box is made from a square sheet of side 20 cm by cutting squares of side x from each corner and folding up the sides. The volume is V = x(20−2x)². Find dV/dx.',
        o: ['(20−2x)² − 4x(20−2x)', '(20−2x)²', '−4x(20−2x)', '2(20−2x)'],
        a: 0,
        e: 'Using the product rule with u=x, v=(20−2x)²: dV/dx = (20−2x)² + x×2(20−2x)(−2) = (20−2x)²−4x(20−2x).',
        h: 'Apply the product rule, using the chain rule to differentiate (20−2x)².',
        yr: 'SS2',
      },
      {
        q: 'Find the turning points of y = x² + 6x + 5.',
        o: ['x = −3', 'x = 3', 'x = −6', 'x = 5'],
        a: 0,
        e: 'dy/dx = 2x + 6 = 0 ⇒ x = −3.',
        h: 'Set the first derivative to zero and solve.',
        yr: 'SS2',
      },
      {
        q: 'Find the minimum value of y = x² + 6x + 5 (at x = −3).',
        o: ['−4', '5', '−14', '0'],
        a: 0,
        e: 'y(−3) = 9 − 18 + 5 = −4. Since d²y/dx²=2>0, this is indeed a minimum value.',
        h: 'Substitute x = −3 into the original function.',
        yr: 'SS2',
      },
      {
        q: 'For a function where d²y/dx² = 0 at a turning point, what should you do?',
        o: [
          'Investigate further (it may be a point of inflection, not max/min)',
          "Automatically conclude it's a maximum",
          "Automatically conclude it's a minimum",
          'Ignore this turning point entirely',
        ],
        a: 0,
        e: "When the second derivative test gives zero, it's inconclusive — you must examine the sign of dy/dx on either side of the point, or use higher derivatives.",
        h: "A zero second derivative doesn't tell us the nature of the point directly — further checking is needed.",
        yr: 'SS2',
      },
      {
        q: 'Find the turning points of y = 2x³ − 3x² − 12x + 1.',
        o: ['x = 2 and x = −1', 'x = 3 and x = −2', 'x = 2 and x = 1', 'x = −2 and x = −1'],
        a: 0,
        e: 'dy/dx = 6x²−6x−12 = 6(x²−x−2) = 6(x−2)(x+1) = 0 ⇒ x=2 or x=−1.',
        h: 'Differentiate, factor out 6, then factor the remaining quadratic.',
        yr: 'SS2',
      },
      {
        q: 'A rectangular box with a square base has volume 32 cm³. If the base side is x and height h, and V = x²h = 32, express the surface area S = x² + 4xh in terms of x only.',
        o: ['S = x² + 128/x', 'S = x² + 32/x', 'S = x² + 4x', 'S = x² + 128x'],
        a: 0,
        e: 'From V=x²h=32, h=32/x². Substituting: S = x² + 4x(32/x²) = x² + 128/x.',
        h: 'Use the volume constraint to express h in terms of x, then substitute into S.',
        yr: 'SS2',
      },
      {
        q: 'For S = x² + 128/x, find dS/dx.',
        o: ['2x − 128/x²', '2x + 128/x²', '2x − 128/x', 'x − 128/x²'],
        a: 0,
        e: 'Rewrite 128/x as 128x⁻¹. d/dx(x²)=2x; d/dx(128x⁻¹)=−128x⁻². Total: 2x − 128/x².',
        h: 'Rewrite the fraction as a negative power before differentiating.',
        yr: 'SS2',
      },
      {
        q: 'Solve 2x − 128/x² = 0 to find the value of x that minimizes surface area.',
        o: ['x = 4', 'x = 8', 'x = 2', 'x = 16'],
        a: 0,
        e: '2x = 128/x² ⇒ 2x³ = 128 ⇒ x³ = 64 ⇒ x = 4.',
        h: 'Multiply both sides by x² to clear the fraction, then solve the resulting cubic.',
        yr: 'SS2',
      },
      {
        q: 'Find the maximum value of y = 9 − (x−2)² (a downward parabola).',
        o: ['9 (at x = 2)', '2', '0', '−9'],
        a: 0,
        e: 'Since (x−2)² ≥ 0 always, y is maximized when (x−2)² = 0, i.e., at x=2, giving y = 9.',
        h: 'The squared term is always non-negative, so the maximum occurs when it equals zero.',
        yr: 'SS2',
      },
      {
        q: 'Which condition must ALWAYS be checked before classifying a stationary point using the second derivative test?',
        o: [
          'That dy/dx = 0 at that point first',
          'That the function is a cubic',
          'That x is a whole number',
          'That the function has no other turning points',
        ],
        a: 0,
        e: 'The second derivative test only classifies points that are ALREADY confirmed to be stationary points (where dy/dx = 0); it cannot be used on arbitrary points.',
        h: "The second derivative test is only meaningful once you've already found a stationary point.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'The Circle',
    topicCode: 'SS2-FMATH-18',
    module: 'Coordinate Geometry',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">circle</span> is the set of all points equidistant from a fixed centre. In coordinate geometry, we describe circles using algebraic equations, allowing us to find centres, radii, and tangent lines using algebra instead of just compasses.</div>\n\n<h3 class="learn-subheading">1. Standard Equation of a Circle</h3>\n<p class="learn-p">A circle with centre (a, b) and radius r has equation: (x &minus; a)&sup2; + (y &minus; b)&sup2; = r&sup2;.<br>\n<strong>Example:</strong> Circle with centre (2, 3) and radius 5: (x&minus;2)&sup2; + (y&minus;3)&sup2; = 25.</p>\n\n<h3 class="learn-subheading">2. The General Equation of a Circle</h3>\n<p class="learn-p">x&sup2; + y&sup2; + 2gx + 2fy + c = 0, where centre = (&minus;g, &minus;f) and radius = &radic;(g&sup2; + f&sup2; &minus; c).<br>\n<strong>Example:</strong> Find the centre and radius of x&sup2; + y&sup2; &minus; 4x + 6y &minus; 12 = 0.<br>\n2g = &minus;4 &rArr; g = &minus;2; 2f = 6 &rArr; f = 3; c = &minus;12.<br>\nCentre = (2, &minus;3). Radius = &radic;(4 + 9 + 12) = &radic;25 = 5.</p>\n\n<h3 class="learn-subheading">3. Equation of a Tangent to a Circle</h3>\n<p class="learn-p">The tangent to the circle x&sup2;+y&sup2;=r&sup2; at point (x&#8321;, y&#8321;) on the circle is xx&#8321; + yy&#8321; = r&sup2;. For a general circle, first find the gradient of the radius to the point, then use the perpendicular gradient for the tangent.</p>\n\n<h3 class="learn-subheading">4. Length of Tangent from an External Point</h3>\n<p class="learn-p">From point (x&#8321;, y&#8321;) to circle x&sup2;+y&sup2;+2gx+2fy+c=0, tangent length = &radic;(x&#8321;&sup2;+y&#8321;&sup2;+2gx&#8321;+2fy&#8321;+c).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> To convert the general form to standard form, complete the square separately for the x-terms and y-terms. Always double-check by expanding the standard form back out.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Standard form (x&minus;a)&sup2;+(y&minus;b)&sup2;=r&sup2; directly shows centre and radius. General form x&sup2;+y&sup2;+2gx+2fy+c=0 requires the formulas centre=(&minus;g,&minus;f), radius=&radic;(g&sup2;+f&sup2;&minus;c).\n</div>\n',
    questions: [
      {
        q: 'Write the equation of a circle with centre (2, 3) and radius 5.',
        o: [
          '(x−2)² + (y−3)² = 25',
          '(x+2)² + (y+3)² = 25',
          '(x−2)² + (y−3)² = 5',
          '(x−2)² − (y−3)² = 25',
        ],
        a: 0,
        e: 'Using (x−a)²+(y−b)²=r² with a=2, b=3, r=5: (x−2)²+(y−3)²=25.',
        h: 'Substitute the centre coordinates and radius into the standard circle equation.',
        yr: 'SS2',
      },
      {
        q: 'Find the centre and radius of x² + y² − 4x + 6y − 12 = 0.',
        o: [
          'Centre (2, −3), radius 5',
          'Centre (−2, 3), radius 5',
          'Centre (4, −6), radius 12',
          'Centre (2, −3), radius 25',
        ],
        a: 0,
        e: '2g=−4⇒g=−2; 2f=6⇒f=3; c=−12. Centre=(−g,−f)=(2,−3). Radius=√(g²+f²−c)=√(4+9+12)=√25=5.',
        h: 'Match coefficients to the general form x²+y²+2gx+2fy+c=0, then apply the centre/radius formulas.',
        yr: 'SS2',
      },
      {
        q: 'Find the centre and radius of (x + 1)² + (y − 4)² = 9.',
        o: [
          'Centre (−1, 4), radius 3',
          'Centre (1, −4), radius 3',
          'Centre (−1, 4), radius 9',
          'Centre (1, 4), radius 3',
        ],
        a: 0,
        e: 'Comparing to (x−a)²+(y−b)²=r²: a=−1, b=4, r²=9⇒r=3.',
        h: 'Match the signs carefully: (x+1)² = (x−(−1))², so a = −1.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of a circle with centre at the origin and radius 7.',
        o: ['x² + y² = 49', 'x² + y² = 7', 'x² − y² = 49', '(x−7)² + (y−7)² = 49'],
        a: 0,
        e: 'With centre (0,0): x²+y²=r²=49.',
        h: 'For a circle centred at the origin, the equation simplifies to x²+y²=r².',
        yr: 'SS2',
      },
      {
        q: 'Convert x² + y² + 6x − 8y = 0 to find the centre.',
        o: ['(−3, 4)', '(3, −4)', '(6, −8)', '(−6, 8)'],
        a: 0,
        e: '2g=6⇒g=3; 2f=−8⇒f=−4. Centre=(−g,−f)=(−3,4).',
        h: 'Match coefficients to 2gx and 2fy, then use centre = (−g, −f).',
        yr: 'SS2',
      },
      {
        q: 'Find the radius of the circle x² + y² + 6x − 8y = 0.',
        o: ['5', '10', '25', '3'],
        a: 0,
        e: 'With g=3, f=−4, c=0: radius=√(g²+f²−c)=√(9+16−0)=√25=5.',
        h: 'Apply radius=√(g²+f²−c) using the values found for g, f, c.',
        yr: 'SS2',
      },
      {
        q: 'Does the point (3, 4) lie on the circle x² + y² = 25?',
        o: [
          'Yes, since 3²+4²=25',
          'No, since 3²+4²≠25',
          'Cannot be determined',
          'Only if the circle has a different radius',
        ],
        a: 0,
        e: '3²+4² = 9+16 = 25, which equals the right side of the equation, so the point lies exactly on the circle.',
        h: "Substitute the point's coordinates into the circle equation and check if it's satisfied.",
        yr: 'SS2',
      },
      {
        q: 'Find the equation of the tangent to x² + y² = 25 at the point (3, 4).',
        o: ['3x + 4y = 25', '4x + 3y = 25', '3x − 4y = 25', 'x + y = 25'],
        a: 0,
        e: 'Using xx₁+yy₁=r² with (x₁,y₁)=(3,4), r²=25: 3x+4y=25.',
        h: "Apply the tangent formula xx₁ + yy₁ = r² directly with the point's coordinates.",
        yr: 'SS2',
      },
      {
        q: 'A circle has equation x² + y² − 2x − 4y − 4 = 0. Find its radius.',
        o: ['3', '9', '5', '2'],
        a: 0,
        e: 'g=−1, f=−2, c=−4. Radius=√(1+4+4)=√9=3.',
        h: 'Find g, f, c from the equation, then apply the radius formula.',
        yr: 'SS2',
      },
      {
        q: 'Find the centre of the circle x² + y² − 2x − 4y − 4 = 0.',
        o: ['(1, 2)', '(−1, −2)', '(2, 4)', '(−2, −4)'],
        a: 0,
        e: 'g=−1, f=−2. Centre = (−g,−f) = (1, 2).',
        h: 'Use centre = (−g, −f) with the values of g and f found.',
        yr: 'SS2',
      },
      {
        q: 'A circle has centre (0, 0) and passes through the point (6, 8). Find its radius.',
        o: ['10', '14', '48', '100'],
        a: 0,
        e: 'Radius = distance from centre to the point = √(6²+8²) = √(36+64) = √100 = 10.',
        h: 'Use the distance formula between the centre and the given point on the circle.',
        yr: 'SS2',
      },
      {
        q: 'Write the equation of the circle with centre (0,0) passing through (6, 8).',
        o: ['x² + y² = 100', 'x² + y² = 10', 'x² + y² = 48', 'x² + y² = 14'],
        a: 0,
        e: 'Since radius = 10 (from the previous question), r² = 100, giving x²+y²=100.',
        h: 'Use the radius found via the distance formula, then square it for the equation.',
        yr: 'SS2',
      },
      {
        q: 'Find the length of the tangent from the point (5, 5) to the circle x² + y² = 9.',
        o: ['√41', '√34', '√9', '√50'],
        a: 0,
        e: 'Tangent length = √(x₁²+y₁²−r²) = √(25+25−9) = √41.',
        h: 'Use tangent length = √(x₁² + y₁² − r²) for a circle centred at the origin.',
        yr: 'SS2',
      },
      {
        q: 'A circle passes through (0,0), has centre on the x-axis, and radius 4. Which of these could be its centre?',
        o: ['(4, 0)', '(0, 4)', '(2, 2)', '(0, 0)'],
        a: 0,
        e: 'If the centre is on the x-axis at distance 4 from the origin (since radius=4 and the circle passes through origin), the centre is (4,0) or (−4,0). Only (4,0) matches the options.',
        h: 'The centre must be exactly r units away from every point on the circle, including the origin.',
        yr: 'SS2',
      },
      {
        q: 'Complete the square to find the centre of x² − 6x + y² + 2y + 6 = 0.',
        o: ['(3, −1)', '(−3, 1)', '(6, −2)', '(3, 1)'],
        a: 0,
        e: 'x²−6x=(x−3)²−9; y²+2y=(y+1)²−1. So (x−3)²+(y+1)²−10+6=0⇒(x−3)²+(y+1)²=4. Centre=(3,−1).',
        h: 'Complete the square for both x and y terms separately, then rearrange.',
        yr: 'SS2',
      },
      {
        q: 'Using the previous result, find the radius of x² − 6x + y² + 2y + 6 = 0.',
        o: ['2', '4', '6', '10'],
        a: 0,
        e: 'From (x−3)²+(y+1)²=4, radius²=4, so radius=2.',
        h: 'The right-hand side of the completed-square form equals radius squared.',
        yr: 'SS2',
      },
      {
        q: 'A circle has equation x² + y² = 16. Find the equation of the tangent at (0, 4).',
        o: ['y = 4', 'x = 4', 'y = −4', 'x + y = 4'],
        a: 0,
        e: 'Using xx₁+yy₁=r² with (0,4): 0×x+4y=16⇒y=4.',
        h: 'Apply the tangent formula, noting x₁=0 simplifies the equation significantly.',
        yr: 'SS2',
      },
      {
        q: 'For a circle with general equation x²+y²+2gx+2fy+c=0, under what condition does a real circle actually exist?',
        o: [
          'g² + f² − c > 0 (positive radius squared)',
          'g² + f² − c < 0',
          'c must be positive',
          'g and f must both be positive',
        ],
        a: 0,
        e: 'Since radius = √(g²+f²−c), we need g²+f²−c > 0 for a real (non-zero) radius to exist.',
        h: 'The expression under the square root for the radius must be positive.',
        yr: 'SS2',
      },
      {
        q: 'Find the diameter of the circle x² + y² − 4x − 4y + 4 = 0.',
        o: ['4', '2', '8', '16'],
        a: 0,
        e: 'g=−2, f=−2, c=4. Radius=√(4+4−4)=√4=2. Diameter = 2×2 = 4.',
        h: 'Find the radius first using the standard formula, then double it for the diameter.',
        yr: 'SS2',
      },
      {
        q: 'Which point is the centre of a circle whose equation is x² + y² = 36?',
        o: ['(0, 0)', '(6, 0)', '(0, 6)', '(6, 6)'],
        a: 0,
        e: 'The equation x²+y²=r² (with no shifted terms) always represents a circle centred at the origin.',
        h: 'The absence of (x−a) or (y−b) terms means the centre is at the origin.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Conic Sections I — The Parabola',
    topicCode: 'SS2-FMATH-19',
    module: 'Coordinate Geometry',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">parabola</span> is the curve traced by a point that moves so that its distance from a fixed point (the <span class="learn-keyword">focus</span>) equals its distance from a fixed line (the <span class="learn-keyword">directrix</span>). Parabolas appear in projectile motion, satellite dishes, and bridge cables.</div>\n\n<h3 class="learn-subheading">1. Standard Equation</h3>\n<p class="learn-p">The simplest parabola with vertex at the origin, opening rightward: y&sup2; = 4ax, where a &gt; 0.<br>\nFocus: (a, 0). Directrix: x = &minus;a. Axis of symmetry: the x-axis.</p>\n\n<h3 class="learn-subheading">2. Key Features</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Equation</th><th>Opens</th><th>Focus</th><th>Directrix</th></tr></thead>\n    <tbody>\n      <tr><td>y&sup2; = 4ax</td><td>Right</td><td>(a, 0)</td><td>x = &minus;a</td></tr>\n      <tr><td>y&sup2; = &minus;4ax</td><td>Left</td><td>(&minus;a, 0)</td><td>x = a</td></tr>\n      <tr><td>x&sup2; = 4ay</td><td>Up</td><td>(0, a)</td><td>y = &minus;a</td></tr>\n      <tr><td>x&sup2; = &minus;4ay</td><td>Down</td><td>(0, &minus;a)</td><td>y = a</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">Find the focus and directrix of y&sup2; = 12x.<br>\nComparing to y&sup2;=4ax: 4a=12 &rArr; a=3. Focus = (3, 0). Directrix: x = &minus;3.</p>\n\n<h3 class="learn-subheading">4. The Parabola as a Locus</h3>\n<p class="learn-p">Every point P(x, y) on the parabola satisfies: distance to focus = distance to directrix. This defining property can be used to derive the equation from scratch, and to verify whether a given point lies on the curve.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always compare the given equation to the standard form y&sup2;=4ax (or x&sup2;=4ay) FIRST to identify \'a\' before stating the focus and directrix &mdash; don\'t just guess from the numbers.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> A parabola y&sup2;=4ax opens rightward with focus (a,0) and directrix x=&minus;a. Every point on the parabola is equidistant from the focus and directrix &mdash; this locus property defines the shape.\n</div>\n',
    questions: [
      {
        q: "Find the value of 'a' for the parabola y² = 12x.",
        o: ['3', '12', '6', '4'],
        a: 0,
        e: 'Comparing y²=12x to y²=4ax: 4a=12 ⇒ a=3.',
        h: 'Match the coefficient of x to 4a in the standard form.',
        yr: 'SS2',
      },
      {
        q: 'Find the focus of the parabola y² = 12x.',
        o: ['(3, 0)', '(0, 3)', '(12, 0)', '(−3, 0)'],
        a: 0,
        e: 'With a=3, the focus of y²=4ax is at (a, 0) = (3, 0).',
        h: 'Use focus = (a, 0) for a rightward-opening parabola.',
        yr: 'SS2',
      },
      {
        q: 'Find the directrix of the parabola y² = 12x.',
        o: ['x = −3', 'x = 3', 'y = −3', 'y = 3'],
        a: 0,
        e: 'With a=3, the directrix of y²=4ax is x = −a = −3.',
        h: 'Use directrix x = −a for a rightward-opening parabola.',
        yr: 'SS2',
      },
      {
        q: 'Find the focus of the parabola x² = 8y.',
        o: ['(0, 2)', '(2, 0)', '(0, 8)', '(8, 0)'],
        a: 0,
        e: 'Comparing to x²=4ay: 4a=8⇒a=2. Focus = (0, a) = (0, 2).',
        h: 'For x²=4ay, the parabola opens upward with focus (0, a).',
        yr: 'SS2',
      },
      {
        q: 'Find the directrix of the parabola x² = 8y.',
        o: ['y = −2', 'y = 2', 'x = −2', 'x = 2'],
        a: 0,
        e: 'With a=2, the directrix of x²=4ay is y = −a = −2.',
        h: 'For x²=4ay, the directrix is the horizontal line y = −a.',
        yr: 'SS2',
      },
      {
        q: 'Which direction does the parabola y² = −16x open?',
        o: ['Left', 'Right', 'Up', 'Down'],
        a: 0,
        e: 'The negative sign in y²=−4ax indicates the parabola opens to the LEFT (negative x direction).',
        h: 'A negative coefficient with y² = ...x indicates leftward opening.',
        yr: 'SS2',
      },
      {
        q: 'Find the focus of y² = −16x.',
        o: ['(−4, 0)', '(4, 0)', '(0, −4)', '(−16, 0)'],
        a: 0,
        e: '4a=16⇒a=4. Since it opens left, focus = (−a, 0) = (−4, 0).',
        h: 'Find a from the magnitude, then apply the leftward-opening focus formula.',
        yr: 'SS2',
      },
      {
        q: 'Find the equation of a parabola with vertex at the origin, opening rightward, with focus (5, 0).',
        o: ['y² = 20x', 'y² = 5x', 'x² = 20y', 'y² = 10x'],
        a: 0,
        e: 'Since focus=(a,0)=(5,0), a=5. Equation: y²=4ax=4(5)x=20x.',
        h: 'Use the focus to find a, then substitute into y² = 4ax.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following equations represents a parabola opening downward?',
        o: ['x² = −12y', 'y² = 12x', 'x² = 12y', 'y² = −12x'],
        a: 0,
        e: 'x²=−4ay (negative coefficient with x²=...y) represents a downward-opening parabola.',
        h: 'Downward opening requires x² on the left with a negative coefficient on y.',
        yr: 'SS2',
      },
      {
        q: 'For the parabola x² = −20y, find the value of a and the focus.',
        o: [
          'a = 5, focus (0, −5)',
          'a = 5, focus (−5, 0)',
          'a = 20, focus (0, −20)',
          'a = 5, focus (0, 5)',
        ],
        a: 0,
        e: '4a=20⇒a=5. Since it opens downward, focus = (0, −a) = (0, −5).',
        h: 'Find a from the magnitude of the coefficient, then apply the downward-opening focus formula.',
        yr: 'SS2',
      },
      {
        q: 'A point P is on the parabola y² = 8x. Its distance from the focus is 5. Find the x-coordinate of P (using the focal distance property: distance to focus = x + a).',
        o: ['3', '5', '2', '7'],
        a: 0,
        e: 'With 4a=8⇒a=2. Focal distance = x+a ⇒ 5 = x+2 ⇒ x=3.',
        h: 'Use the property that for y²=4ax, distance from any point to the focus equals x + a.',
        yr: 'SS2',
      },
      {
        q: 'Find the length of the latus rectum of the parabola y² = 16x (latus rectum length = 4a).',
        o: ['16', '8', '4', '32'],
        a: 0,
        e: '4a=16, and the latus rectum length is exactly 4a = 16.',
        h: 'The latus rectum length equals 4a, the same coefficient as in the standard equation.',
        yr: 'SS2',
      },
      {
        q: 'Does the point (2, 4) lie on the parabola y² = 8x?',
        o: [
          'Yes, since 4² = 8(2) = 16',
          'No, since 4² ≠ 8(2)',
          'Cannot be determined',
          'Only if a is negative',
        ],
        a: 0,
        e: 'Check: y²=16, 8x=8(2)=16. Since both sides are equal, the point lies on the parabola.',
        h: "Substitute the point's coordinates into the parabola equation and check equality.",
        yr: 'SS2',
      },
      {
        q: 'For the parabola x² = 4ay with a = 3, write the full equation.',
        o: ['x² = 12y', 'x² = 3y', 'y² = 12x', 'x² = 4y'],
        a: 0,
        e: 'Substituting a=3: x² = 4(3)y = 12y.',
        h: 'Simply substitute the given value of a into the standard form.',
        yr: 'SS2',
      },
      {
        q: 'What is the vertex of the parabola y² = 20x?',
        o: ['(0, 0)', '(5, 0)', '(0, 5)', '(20, 0)'],
        a: 0,
        e: 'In this standard form (no shifts), the vertex is always at the origin (0, 0).',
        h: 'The standard form y²=4ax always has its vertex at the origin.',
        yr: 'SS2',
      },
      {
        q: 'A satellite dish has a parabolic cross-section with equation y = x²/8 (i.e., x² = 8y). Find the focus, where signals converge.',
        o: ['(0, 2)', '(2, 0)', '(0, 8)', '(0, 4)'],
        a: 0,
        e: 'Comparing x²=8y to x²=4ay: 4a=8⇒a=2. Focus=(0,2). This is exactly why the receiver of a satellite dish is placed at the focus.',
        h: "This is a real-world application: dish antennas place the receiver exactly at the parabola's focus.",
        yr: 'SS2',
      },
      {
        q: 'Which axis is the axis of symmetry for the parabola y² = 4ax?',
        o: ['The x-axis', 'The y-axis', 'Both axes', 'Neither axis'],
        a: 0,
        e: 'For y²=4ax, the parabola is symmetric about the x-axis (since y appears squared, +y and −y give the same x).',
        h: 'Consider which axis reflects the curve onto itself.',
        yr: 'SS2',
      },
      {
        q: "Find 'a' for a parabola x² = 4ay passing through the point (4, 2).",
        o: ['a = 2', 'a = 4', 'a = 1', 'a = 8'],
        a: 0,
        e: 'Substitute the point: 4² = 4a(2) ⇒ 16 = 8a ⇒ a = 2.',
        h: "Substitute the given point's coordinates into the equation and solve for a.",
        yr: 'SS2',
      },
      {
        q: 'For the parabola in the previous question (a = 2), write its full equation.',
        o: ['x² = 8y', 'x² = 4y', 'x² = 2y', 'x² = 16y'],
        a: 0,
        e: 'Substituting a=2 into x²=4ay: x²=4(2)y=8y.',
        h: 'Substitute the value of a found in the previous step into the standard form.',
        yr: 'SS2',
      },
      {
        q: 'For a parabola opening rightward with directrix x = −7, find the focus.',
        o: ['(7, 0)', '(−7, 0)', '(0, 7)', '(14, 0)'],
        a: 0,
        e: 'Since directrix = −a = −7, a=7. Focus = (a, 0) = (7, 0).',
        h: 'Use the relationship between directrix and focus: they are equidistant from the vertex, on opposite sides.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Conic Sections II — The Ellipse',
    topicCode: 'SS2-FMATH-20',
    module: 'Coordinate Geometry',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  An <span class="learn-keyword">ellipse</span> is the locus of points where the SUM of distances to two fixed points (the <span class="learn-keyword">foci</span>) is constant. Planetary orbits, whispering galleries, and rugby balls are all elliptical in shape.</div>\n\n<h3 class="learn-subheading">1. Standard Equation</h3>\n<p class="learn-p">Centred at the origin: x&sup2;/a&sup2; + y&sup2;/b&sup2; = 1, where a &gt; b &gt; 0 (major axis along the x-axis).<br>\nVertices: (&plusmn;a, 0). Co-vertices: (0, &plusmn;b). Foci: (&plusmn;c, 0), where c&sup2; = a&sup2; &minus; b&sup2;.</p>\n\n<h3 class="learn-subheading">2. Key Features</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Feature</th><th>Formula</th></tr></thead>\n    <tbody>\n      <tr><td>Major axis length</td><td>2a (along x-axis if a &gt; b)</td></tr>\n      <tr><td>Minor axis length</td><td>2b</td></tr>\n      <tr><td>Focal distance</td><td>c&sup2; = a&sup2; &minus; b&sup2;</td></tr>\n      <tr><td>Eccentricity</td><td>e = c/a (0 &lt; e &lt; 1 for an ellipse)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">Find the foci and eccentricity of x&sup2;/25 + y&sup2;/16 = 1.<br>\na&sup2;=25 &rArr; a=5. b&sup2;=16 &rArr; b=4. c&sup2;=a&sup2;&minus;b&sup2;=25&minus;16=9 &rArr; c=3.<br>\nFoci: (&plusmn;3, 0). Eccentricity: e = c/a = 3/5 = 0.6.</p>\n\n<h3 class="learn-subheading">4. Definition Check</h3>\n<p class="learn-p">For any point P on the ellipse, PF&#8321; + PF&#8322; = 2a (sum of distances to both foci is constant, equal to the length of the major axis).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always identify which denominator is larger (a&sup2; or b&sup2;) FIRST &mdash; the larger one tells you which axis (x or y) is the major axis, and this determines whether foci lie on the x-axis or y-axis.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> x&sup2;/a&sup2;+y&sup2;/b&sup2;=1 with a&gt;b describes an ellipse with major axis along the x-axis, foci at (&plusmn;c,0) where c&sup2;=a&sup2;&minus;b&sup2;. Eccentricity e=c/a measures how "stretched" the ellipse is (0 = circle, closer to 1 = more elongated).\n</div>\n',
    questions: [
      {
        q: 'For the ellipse x²/25 + y²/16 = 1, find a and b.',
        o: ['a = 5, b = 4', 'a = 25, b = 16', 'a = 4, b = 5', 'a = 5, b = 16'],
        a: 0,
        e: 'a²=25⇒a=5, b²=16⇒b=4 (taking positive square roots).',
        h: 'Take the square root of each denominator in the standard equation.',
        yr: 'SS2',
      },
      {
        q: 'For the ellipse x²/25 + y²/16 = 1, find c (the focal distance from centre).',
        o: ['3', '9', '41', '5'],
        a: 0,
        e: 'c² = a²−b² = 25−16 = 9 ⇒ c = 3.',
        h: 'Use the relationship c² = a² − b² for an ellipse.',
        yr: 'SS2',
      },
      {
        q: 'For the ellipse x²/25 + y²/16 = 1, find the foci.',
        o: ['(±3, 0)', '(0, ±3)', '(±5, 0)', '(±9, 0)'],
        a: 0,
        e: 'Since a² (25) > b² (16), major axis is along the x-axis, so foci = (±c, 0) = (±3, 0).',
        h: 'The foci lie along the major axis, which corresponds to the larger denominator.',
        yr: 'SS2',
      },
      {
        q: 'For the ellipse x²/25 + y²/16 = 1, find the eccentricity.',
        o: ['0.6', '1.25', '0.8', '1'],
        a: 0,
        e: 'e = c/a = 3/5 = 0.6.',
        h: 'Divide the focal distance c by the semi-major axis a.',
        yr: 'SS2',
      },
      {
        q: 'Find the length of the major axis of x²/25 + y²/16 = 1.',
        o: ['10', '5', '8', '20'],
        a: 0,
        e: 'Major axis length = 2a = 2(5) = 10.',
        h: 'The major axis spans from (−a,0) to (a,0), a total length of 2a.',
        yr: 'SS2',
      },
      {
        q: 'Find the length of the minor axis of x²/25 + y²/16 = 1.',
        o: ['8', '4', '16', '10'],
        a: 0,
        e: 'Minor axis length = 2b = 2(4) = 8.',
        h: 'The minor axis spans from (0,−b) to (0,b), a total length of 2b.',
        yr: 'SS2',
      },
      {
        q: 'For an ellipse x²/9 + y²/25 = 1, which axis is the major axis?',
        o: [
          'The y-axis, since 25 > 9',
          'The x-axis, since 9 < 25',
          "Neither, it's a circle",
          'Cannot be determined',
        ],
        a: 0,
        e: 'Here b²=25 is under y² and is LARGER than a²=9 under x², so the major axis is actually along the y-axis in this case.',
        h: 'Compare the two denominators; the larger one indicates the major axis direction.',
        yr: 'SS2',
      },
      {
        q: 'For x²/9 + y²/25 = 1, find the foci (major axis along y-axis, c² = 25−9 = 16).',
        o: ['(0, ±4)', '(±4, 0)', '(0, ±3)', '(0, ±5)'],
        a: 0,
        e: 'c²=25−9=16⇒c=4. Since major axis is along y-axis, foci = (0, ±c) = (0, ±4).',
        h: 'When the major axis is vertical, foci lie on the y-axis at (0, ±c).',
        yr: 'SS2',
      },
      {
        q: 'Write the equation of an ellipse with a = 6, b = 4, major axis along the x-axis.',
        o: ['x²/36 + y²/16 = 1', 'x²/6 + y²/4 = 1', 'x²/16 + y²/36 = 1', 'x²/12 + y²/8 = 1'],
        a: 0,
        e: 'Using x²/a²+y²/b²=1 with a=6, b=4: x²/36 + y²/16 = 1.',
        h: 'Square the given semi-axis lengths to get the denominators.',
        yr: 'SS2',
      },
      {
        q: 'Does the point (5, 0) lie on the ellipse x²/25 + y²/16 = 1?',
        o: [
          'Yes, it is a vertex of the ellipse',
          'No, it lies outside the ellipse',
          'No, it lies inside the ellipse',
          'Cannot be determined',
        ],
        a: 0,
        e: 'Substituting: 25/25 + 0/16 = 1 + 0 = 1, which satisfies the equation exactly — this is the vertex at (a, 0).',
        h: 'Substitute the point into the ellipse equation to check if it equals 1.',
        yr: 'SS2',
      },
      {
        q: 'For any point P on an ellipse with foci F₁ and F₂, what is PF₁ + PF₂ equal to?',
        o: ['2a (the length of the major axis)', '2b', 'c', 'a + b'],
        a: 0,
        e: 'By the defining property of an ellipse, the sum of distances from any point on the ellipse to the two foci is always constant, equal to 2a.',
        h: 'This is the fundamental locus definition of an ellipse.',
        yr: 'SS2',
      },
      {
        q: 'Find the eccentricity of an ellipse with a = 10, c = 6.',
        o: ['0.6', '1.67', '6', '16'],
        a: 0,
        e: 'e = c/a = 6/10 = 0.6.',
        h: 'Divide the focal distance by the semi-major axis length.',
        yr: 'SS2',
      },
      {
        q: 'As eccentricity e approaches 0, what shape does the ellipse approach?',
        o: ['A circle', 'A straight line', 'A parabola', 'A hyperbola'],
        a: 0,
        e: 'When e = 0, c = 0, meaning a = b, and the ellipse becomes a circle (the two foci merge into the centre).',
        h: 'Consider what happens to the shape when c approaches 0 (foci move toward the centre).',
        yr: 'SS2',
      },
      {
        q: 'For the ellipse x²/49 + y²/24 = 1, find c.',
        o: ['5', '25', '73', '−73'],
        a: 0,
        e: 'a²=49, b²=24. c²=49−24=25⇒c=5.',
        h: 'Subtract the smaller denominator from the larger, then take the square root.',
        yr: 'SS2',
      },
      {
        q: 'For the ellipse x²/49 + y²/24 = 1, find the eccentricity.',
        o: ['5/7', '7/5', '24/49', '5/24'],
        a: 0,
        e: 'a=7, c=5 (from previous question). e=c/a=5/7.',
        h: 'Divide the focal distance by the semi-major axis a=7.',
        yr: 'SS2',
      },
      {
        q: 'An ellipse has vertices at (±8, 0) and co-vertices at (0, ±6). Write its equation.',
        o: ['x²/64 + y²/36 = 1', 'x²/8 + y²/6 = 1', 'x²/36 + y²/64 = 1', 'x²/16 + y²/12 = 1'],
        a: 0,
        e: 'Vertices give a=8 (a²=64), co-vertices give b=6 (b²=36). Equation: x²/64+y²/36=1.',
        h: 'Use the vertex distances to find a² and b² directly.',
        yr: 'SS2',
      },
      {
        q: 'For an ellipse with a > b, which statement is TRUE?',
        o: [
          'The major axis is longer than the minor axis',
          'a and b are always equal',
          'The foci lie outside the ellipse',
          'b is always greater than c',
        ],
        a: 0,
        e: 'By definition, a represents the semi-major (longer) axis and b the semi-minor (shorter) axis, so 2a > 2b always when a > b.',
        h: 'Recall that a and b represent the semi-major and semi-minor axis lengths respectively.',
        yr: 'SS2',
      },
      {
        q: 'Is it possible for c to be greater than a in a valid ellipse?',
        o: [
          'No, since c² = a² − b² requires c < a for a real ellipse',
          'Yes, always',
          'Only for very elongated ellipses',
          'Only if b = 0',
        ],
        a: 0,
        e: 'Since c² = a² − b² and b² > 0 for a proper ellipse, c² must be less than a², so c < a always.',
        h: 'Consider the formula c² = a² − b² and what it implies about the relative sizes.',
        yr: 'SS2',
      },
      {
        q: 'Find the y-intercepts of the ellipse x²/36 + y²/9 = 1.',
        o: ['(0, 3) and (0, −3)', '(0, 6) and (0, −6)', '(0, 9) and (0, −9)', '(3, 0) and (−3, 0)'],
        a: 0,
        e: 'Set x=0: y²/9=1⇒y²=9⇒y=±3. Y-intercepts are (0,3) and (0,−3).',
        h: 'Set x = 0 in the ellipse equation and solve for y.',
        yr: 'SS2',
      },
      {
        q: 'Find the x-intercepts of the ellipse x²/36 + y²/9 = 1.',
        o: [
          '(6, 0) and (−6, 0)',
          '(3, 0) and (−3, 0)',
          '(36, 0) and (−36, 0)',
          '(9, 0) and (−9, 0)',
        ],
        a: 0,
        e: 'Set y=0: x²/36=1⇒x²=36⇒x=±6. X-intercepts are (6,0) and (−6,0).',
        h: 'Set y = 0 in the ellipse equation and solve for x.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Conic Sections III — The Hyperbola',
    topicCode: 'SS2-FMATH-21',
    module: 'Coordinate Geometry',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  A <span class="learn-keyword">hyperbola</span> is the locus of points where the DIFFERENCE of distances to two fixed foci is constant. Unlike the ellipse\'s closed loop, a hyperbola consists of two separate, open branches.</div>\n\n<h3 class="learn-subheading">1. Standard Equation</h3>\n<p class="learn-p">Centred at the origin, opening left-right: x&sup2;/a&sup2; &minus; y&sup2;/b&sup2; = 1.<br>\nVertices: (&plusmn;a, 0). Foci: (&plusmn;c, 0), where c&sup2; = a&sup2; + b&sup2; (note: PLUS, unlike the ellipse).</p>\n\n<h3 class="learn-subheading">2. Key Features</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Feature</th><th>Formula</th></tr></thead>\n    <tbody>\n      <tr><td>Focal relationship</td><td>c&sup2; = a&sup2; + b&sup2;</td></tr>\n      <tr><td>Eccentricity</td><td>e = c/a (always &gt; 1 for a hyperbola)</td></tr>\n      <tr><td>Asymptotes</td><td>y = &plusmn;(b/a)x</td></tr>\n      <tr><td>Transverse axis</td><td>Length 2a, joins the two vertices</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">Find the foci, eccentricity, and asymptotes of x&sup2;/16 &minus; y&sup2;/9 = 1.<br>\na&sup2;=16 &rArr; a=4. b&sup2;=9 &rArr; b=3. c&sup2;=16+9=25 &rArr; c=5.<br>\nFoci: (&plusmn;5, 0). Eccentricity: e=c/a=5/4=1.25. Asymptotes: y = &plusmn;(3/4)x.</p>\n\n<h3 class="learn-subheading">4. Definition Check</h3>\n<p class="learn-p">For any point P on the hyperbola, |PF&#8321; &minus; PF&#8322;| = 2a (the absolute difference of distances to the two foci is constant).</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> The key difference from the ellipse: for a hyperbola, c&sup2; = a&sup2; + b&sup2; (addition, since c &gt; a always). For an ellipse, c&sup2; = a&sup2; &minus; b&sup2; (subtraction, since c &lt; a always). Mixing these up is the most common error between the two conics.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> x&sup2;/a&sup2;&minus;y&sup2;/b&sup2;=1 describes a hyperbola opening left-right, with foci (&plusmn;c,0) where c&sup2;=a&sup2;+b&sup2;, eccentricity always &gt;1, and asymptotes y=&plusmn;(b/a)x that the branches approach but never touch.\n</div>\n',
    questions: [
      {
        q: 'For the hyperbola x²/16 − y²/9 = 1, find a and b.',
        o: ['a = 4, b = 3', 'a = 16, b = 9', 'a = 3, b = 4', 'a = 9, b = 16'],
        a: 0,
        e: 'a²=16⇒a=4, b²=9⇒b=3.',
        h: 'Take the square root of each denominator in the standard equation.',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola x²/16 − y²/9 = 1, find c.',
        o: ['5', '7', '25', '√7'],
        a: 0,
        e: 'c² = a² + b² = 16 + 9 = 25 ⇒ c = 5. Note the ADDITION, unlike the ellipse.',
        h: 'For a hyperbola, use c² = a² + b² (addition, not subtraction).',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola x²/16 − y²/9 = 1, find the foci.',
        o: ['(±5, 0)', '(0, ±5)', '(±4, 0)', '(±7, 0)'],
        a: 0,
        e: 'With c=5, foci = (±c, 0) = (±5, 0).',
        h: 'For a left-right opening hyperbola, foci lie on the x-axis at (±c, 0).',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola x²/16 − y²/9 = 1, find the eccentricity.',
        o: ['1.25', '0.8', '1', '0.75'],
        a: 0,
        e: 'e = c/a = 5/4 = 1.25. Note that eccentricity is always greater than 1 for a hyperbola.',
        h: 'Divide the focal distance c by the semi-transverse axis a.',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola x²/16 − y²/9 = 1, find the equations of the asymptotes.',
        o: ['y = ±(3/4)x', 'y = ±(4/3)x', 'y = ±(9/16)x', 'y = ±(16/9)x'],
        a: 0,
        e: 'Asymptotes of x²/a²−y²/b²=1 are y = ±(b/a)x = ±(3/4)x.',
        h: 'Use the formula y = ±(b/a)x for the asymptotes of a horizontal hyperbola.',
        yr: 'SS2',
      },
      {
        q: 'Find the vertices of the hyperbola x²/16 − y²/9 = 1.',
        o: ['(±4, 0)', '(0, ±4)', '(±3, 0)', '(±5, 0)'],
        a: 0,
        e: 'Vertices are at (±a, 0) = (±4, 0).',
        h: 'The vertices lie on the transverse axis, a distance of a from the centre.',
        yr: 'SS2',
      },
      {
        q: 'Which formula correctly relates a, b, c for a hyperbola?',
        o: ['c² = a² + b²', 'c² = a² − b²', 'c² = b² − a²', 'c = a + b'],
        a: 0,
        e: "For a hyperbola, c² = a² + b² (unlike the ellipse's c² = a² − b²).",
        h: 'Remember: hyperbola uses addition; ellipse uses subtraction.',
        yr: 'SS2',
      },
      {
        q: 'Is the eccentricity of a hyperbola always greater than, less than, or equal to 1?',
        o: [
          'Always greater than 1',
          'Always less than 1',
          'Always equal to 1',
          'Can be any positive value',
        ],
        a: 0,
        e: 'For a hyperbola, since c > a always (because c² = a²+b² > a²), the eccentricity e = c/a is always greater than 1.',
        h: 'Compare this with the ellipse, where 0 < e < 1.',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola x²/9 − y²/16 = 1, find c.',
        o: ['5', '7', '√7', '25'],
        a: 0,
        e: 'a²=9, b²=16. c²=9+16=25⇒c=5.',
        h: 'Add the two denominators, then take the square root.',
        yr: 'SS2',
      },
      {
        q: 'Write the standard equation of a hyperbola with a = 5, b = 12, opening left-right.',
        o: ['x²/25 − y²/144 = 1', 'x²/5 − y²/12 = 1', 'x²/144 − y²/25 = 1', 'x²/25 + y²/144 = 1'],
        a: 0,
        e: 'Using x²/a²−y²/b²=1 with a=5, b=12: x²/25 − y²/144 = 1.',
        h: 'Square the given values of a and b to form the denominators.',
        yr: 'SS2',
      },
      {
        q: 'For the hyperbola in the previous question, find c.',
        o: ['13', '17', '7', '169'],
        a: 0,
        e: 'c² = 25+144 = 169 ⇒ c = 13.',
        h: 'Use c² = a² + b² with the values a=5, b=12 (this is the 5-12-13 triple).',
        yr: 'SS2',
      },
      {
        q: 'Does the point (4, 0) lie on the hyperbola x²/16 − y²/9 = 1?',
        o: [
          'Yes, it is a vertex',
          'No, it lies outside the hyperbola',
          'No, it lies between the branches',
          'Cannot be determined',
        ],
        a: 0,
        e: 'Substituting: 16/16 − 0/9 = 1 − 0 = 1, which satisfies the equation — this is exactly the vertex (a, 0).',
        h: 'Substitute the point into the equation and check if it equals 1.',
        yr: 'SS2',
      },
      {
        q: 'For any point P on a hyperbola with foci F₁ and F₂, what is |PF₁ − PF₂| equal to?',
        o: ['2a', '2b', '2c', 'a + b'],
        a: 0,
        e: 'By the defining property of a hyperbola, the absolute difference of distances from any point on the curve to the two foci is constant, equal to 2a.',
        h: "This is the fundamental locus definition of a hyperbola — note it's a DIFFERENCE, unlike the ellipse's sum.",
        yr: 'SS2',
      },
      {
        q: 'A hyperbola has a = 6 and c = 10. Find b.',
        o: ['8', '4', '16', '√136'],
        a: 0,
        e: 'c²=a²+b² ⇒ 100=36+b² ⇒ b²=64 ⇒ b=8.',
        h: 'Rearrange c² = a² + b² to solve for b², then take the square root.',
        yr: 'SS2',
      },
      {
        q: 'For a hyperbola x²/a² − y²/b² = 1, which axis contains the vertices?',
        o: ['The x-axis', 'The y-axis', 'Neither axis', 'Both axes'],
        a: 0,
        e: 'When x² has the positive term, the hyperbola opens along the x-axis, and its vertices lie on the x-axis.',
        h: 'The vertices lie along the axis corresponding to the positive squared term.',
        yr: 'SS2',
      },
      {
        q: 'What shape do the asymptotes of a hyperbola form relative to its branches?',
        o: [
          'The branches approach the asymptotes but never touch them',
          'The branches cross the asymptotes at every point',
          'The asymptotes are perpendicular bisectors of the branches',
          'The asymptotes are irrelevant to the shape',
        ],
        a: 0,
        e: "As x becomes very large, the hyperbola's branches get closer and closer to the asymptote lines but never actually intersect them.",
        h: 'Asymptotes describe the long-term behaviour of the curve as it extends to infinity.',
        yr: 'SS2',
      },
      {
        q: 'Find the eccentricity of a hyperbola with a = 3 and c = 6.',
        o: ['2', '0.5', '3', '18'],
        a: 0,
        e: 'e = c/a = 6/3 = 2.',
        h: 'Divide c by a directly.',
        yr: 'SS2',
      },
      {
        q: 'For x²/4 − y²/12 = 1, find the asymptotes.',
        o: ['y = ±√3 x', 'y = ±(1/√3) x', 'y = ±3x', 'y = ±(4/12)x'],
        a: 0,
        e: 'a²=4⇒a=2, b²=12⇒b=2√3. Asymptotes: y=±(b/a)x = ±(2√3/2)x = ±√3 x.',
        h: 'Find a and b first, then simplify the ratio b/a fully.',
        yr: 'SS2',
      },
      {
        q: 'Which conic section has an eccentricity strictly greater than 1?',
        o: ['Hyperbola', 'Ellipse', 'Circle', 'Parabola (eccentricity exactly 1)'],
        a: 0,
        e: 'The hyperbola is the only one of these with eccentricity greater than 1. Circle: e=0; Ellipse: 0<e<1; Parabola: e=1 exactly.',
        h: 'Recall the eccentricity ranges: circle=0, ellipse between 0 and 1, parabola=1, hyperbola>1.',
        yr: 'SS2',
      },
      {
        q: 'A hyperbola has vertices at (±7, 0) and foci at (±9, 0). Find b.',
        o: ['√32 (≈ 5.66)', '2', '16', '√130'],
        a: 0,
        e: 'a=7, c=9. b²=c²−a²=81−49=32⇒b=√32.',
        h: 'Use b² = c² − a² (rearranged from c² = a² + b²), since here a and c are given.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Sample Space and Events',
    topicCode: 'SS2-FMATH-22',
    module: 'Probability',
    term: 'Second Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Probability</span> theory begins with defining the <span class="learn-keyword">sample space</span> (all possible outcomes) and <span class="learn-keyword">events</span> (subsets of outcomes we\'re interested in). This term we lay the foundation; compound events follow next term.</div>\n\n<h3 class="learn-subheading">1. Sample Space</h3>\n<p class="learn-p">The sample space S is the set of ALL possible outcomes of an experiment. <strong>Example:</strong> Tossing a coin: S = {Head, Tail}. Rolling a die: S = {1,2,3,4,5,6}.</p>\n\n<h3 class="learn-subheading">2. Events</h3>\n<p class="learn-p">An event is any subset of the sample space. <strong>Example:</strong> "Rolling an even number" is the event E = {2, 4, 6}, a subset of S = {1,2,3,4,5,6}.</p>\n\n<h3 class="learn-subheading">3. Probability Formula</h3>\n<p class="learn-p">P(E) = n(E)/n(S), where n(E) is the number of favourable outcomes and n(S) is the total number of possible outcomes.<br>\n<strong>Example:</strong> P(rolling an even number) = 3/6 = 1/2.</p>\n\n<h3 class="learn-subheading">4. Types of Events</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Type</th><th>Meaning</th></tr></thead>\n    <tbody>\n      <tr><td>Simple event</td><td>Contains exactly one outcome</td></tr>\n      <tr><td>Compound event</td><td>Contains more than one outcome</td></tr>\n      <tr><td>Certain event</td><td>Always occurs, P = 1 (equals the whole sample space)</td></tr>\n      <tr><td>Impossible event</td><td>Never occurs, P = 0 (the empty set)</td></tr>\n      <tr><td>Complementary event</td><td>Event E does NOT occur, P(E&prime;) = 1 &minus; P(E)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always list the sample space carefully before counting outcomes &mdash; especially with two dice or two coins, where the sample space has more elements than students often expect (e.g., two dice give 36 outcomes, not 12).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> The sample space lists every possible outcome; an event is a chosen subset of it. P(E) = n(E)/n(S). Probabilities always lie between 0 (impossible) and 1 (certain), and P(E) + P(E&prime;) = 1.\n</div>\n',
    questions: [
      {
        q: 'What is the sample space when tossing a single fair coin?',
        o: ['{Head, Tail}', '{Head}', '{Tail}', '{Head, Tail, Edge}'],
        a: 0,
        e: 'A coin toss has exactly two possible outcomes: Head or Tail.',
        h: 'List every possible distinct outcome of a single coin toss.',
        yr: 'SS2',
      },
      {
        q: 'What is the sample space when rolling a single fair die?',
        o: ['{1, 2, 3, 4, 5, 6}', '{1, 2, 3}', '{0, 1, 2, 3, 4, 5}', '{1, 2, 3, 4, 5, 6, 7}'],
        a: 0,
        e: 'A standard six-sided die has faces numbered 1 through 6.',
        h: 'List all faces on a standard die.',
        yr: 'SS2',
      },
      {
        q: 'How many outcomes are in the sample space of tossing two coins?',
        o: ['4', '2', '6', '8'],
        a: 0,
        e: 'Each coin has 2 outcomes; with 2 coins, total outcomes = 2×2 = 4: {HH, HT, TH, TT}.',
        h: 'Multiply the number of outcomes for each coin together.',
        yr: 'SS2',
      },
      {
        q: 'How many outcomes are in the sample space of rolling two dice?',
        o: ['36', '12', '6', '72'],
        a: 0,
        e: 'Each die has 6 outcomes; with 2 dice, total = 6×6 = 36.',
        h: 'Multiply the number of outcomes for each die.',
        yr: 'SS2',
      },
      {
        q: 'A die is rolled. Find P(rolling an even number).',
        o: ['1/2', '1/3', '2/3', '1/6'],
        a: 0,
        e: 'Even numbers on a die: {2,4,6}, so n(E)=3. P(E)=3/6=1/2.',
        h: 'List the even numbers on a die, then divide by the total outcomes.',
        yr: 'SS2',
      },
      {
        q: 'A die is rolled. Find P(rolling a number greater than 4).',
        o: ['1/3', '1/2', '2/3', '1/6'],
        a: 0,
        e: 'Numbers greater than 4: {5,6}, so n(E)=2. P(E)=2/6=1/3.',
        h: "Identify which outcomes satisfy 'greater than 4', then form the ratio.",
        yr: 'SS2',
      },
      {
        q: 'A bag contains 5 red balls and 3 blue balls. Find P(selecting a red ball).',
        o: ['5/8', '3/8', '5/3', '1/8'],
        a: 0,
        e: 'Total balls = 5+3=8. P(red) = 5/8.',
        h: 'Divide the number of favourable outcomes (red balls) by the total number of balls.',
        yr: 'SS2',
      },
      {
        q: 'A card is drawn from a standard 52-card deck. Find P(drawing a heart).',
        o: ['1/4', '1/13', '1/52', '1/2'],
        a: 0,
        e: 'There are 13 hearts in a 52-card deck. P(heart) = 13/52 = 1/4.',
        h: 'Recall there are 4 suits of 13 cards each in a standard deck.',
        yr: 'SS2',
      },
      {
        q: "If P(E) = 0.3, find P(E'), the probability that E does NOT occur.",
        o: ['0.7', '0.3', '1.3', '−0.3'],
        a: 0,
        e: "P(E) + P(E') = 1, so P(E') = 1 − 0.3 = 0.7.",
        h: "Use the complementary probability rule: P(E') = 1 − P(E).",
        yr: 'SS2',
      },
      {
        q: 'What is the probability of a certain event?',
        o: ['1', '0', '0.5', 'Undefined'],
        a: 0,
        e: 'A certain event always occurs, so its probability is exactly 1.',
        h: 'Recall that certain events have the maximum possible probability.',
        yr: 'SS2',
      },
      {
        q: 'What is the probability of an impossible event?',
        o: ['0', '1', '0.5', 'Undefined'],
        a: 0,
        e: 'An impossible event never occurs, so its probability is exactly 0.',
        h: 'Recall that impossible events have the minimum possible probability.',
        yr: 'SS2',
      },
      {
        q: 'A box contains 10 balls numbered 1 to 10. Find P(selecting a multiple of 3).',
        o: ['3/10', '1/3', '4/10', '1/10'],
        a: 0,
        e: 'Multiples of 3 from 1-10: {3,6,9}, so n(E)=3. P(E)=3/10.',
        h: 'List the multiples of 3 within the range 1 to 10.',
        yr: 'SS2',
      },
      {
        q: 'Two dice are rolled. Find P(sum equals 7).',
        o: ['1/6', '1/12', '1/36', '1/9'],
        a: 0,
        e: 'Pairs summing to 7: (1,6),(2,5),(3,4),(4,3),(5,2),(6,1) = 6 outcomes. P = 6/36 = 1/6.',
        h: 'List all pairs (a,b) from rolling two dice where a+b=7.',
        yr: 'SS2',
      },
      {
        q: 'Which of these values CANNOT be a valid probability?',
        o: ['1.5', '0', '1', '0.5'],
        a: 0,
        e: 'Probability must lie between 0 and 1 inclusive. 1.5 exceeds this range and is not a valid probability.',
        h: 'Recall the fundamental range restriction: 0 ≤ P(E) ≤ 1.',
        yr: 'SS2',
      },
      {
        q: 'A letter is chosen at random from the word "MATHEMATICS". Find P(choosing the letter M).',
        o: ['2/11', '1/11', '1/8', '2/8'],
        a: 0,
        e: '"MATHEMATICS" has 11 letters total, with M appearing twice. P(M) = 2/11.',
        h: 'Count the total letters and how many times M appears in the word.',
        yr: 'SS2',
      },
      {
        q: 'In a class of 30 students, 18 study French. A student is picked at random. Find P(the student does NOT study French).',
        o: ['12/30 (= 2/5)', '18/30', '1', '0'],
        a: 0,
        e: 'Students not studying French = 30−18=12. P(not French) = 12/30 = 2/5.',
        h: "Find the complementary event's outcome count, then form the probability.",
        yr: 'SS2',
      },
      {
        q: 'A spinner has 8 equal sections numbered 1-8. Find P(spinning a prime number).',
        o: ['4/8 (= 1/2)', '3/8', '2/8', '5/8'],
        a: 0,
        e: 'Primes from 1-8: {2,3,5,7}, so n(E)=4. P(E)=4/8=1/2.',
        h: 'List the prime numbers within the range 1 to 8 (remember 1 is not prime).',
        yr: 'SS2',
      },
      {
        q: "What is a 'simple event' in probability?",
        o: [
          'An event consisting of exactly one outcome',
          'An event with no outcomes',
          'Any event involving dice',
          'An event that is always certain',
        ],
        a: 0,
        e: "A simple event contains just a single outcome from the sample space, e.g., 'rolling exactly a 4'.",
        h: 'Distinguish simple events (one outcome) from compound events (multiple outcomes).',
        yr: 'SS2',
      },
      {
        q: 'If a sample space has 20 equally likely outcomes and an event E has 5 of them, find P(E).',
        o: ['1/4', '1/5', '1/20', '5/4'],
        a: 0,
        e: 'P(E) = n(E)/n(S) = 5/20 = 1/4.',
        h: 'Divide the number of favourable outcomes by the total sample space size, then simplify.',
        yr: 'SS2',
      },
      {
        q: 'A committee of 2 is to be selected from a group where the sample space of possible pairs has been listed as 15 outcomes. If 6 of these pairs include a specific person, find the probability that a randomly selected pair includes that person.',
        o: ['6/15 (= 2/5)', '6/9', '9/15', '1/6'],
        a: 0,
        e: 'P(includes the person) = n(favourable)/n(S) = 6/15 = 2/5.',
        h: 'Apply the basic probability formula directly to the given counts.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Probability of Compound Events',
    topicCode: 'SS2-FMATH-23',
    module: 'Probability',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Building on sample spaces, we now combine events using AND/OR logic. <span class="learn-keyword">Compound events</span> involve two or more simple events, and require the Addition Rule and Multiplication Rule to calculate correctly.</div>\n\n<h3 class="learn-subheading">1. Mutually Exclusive Events (Addition Rule)</h3>\n<p class="learn-p">Events that cannot happen at the same time (no overlap). P(A or B) = P(A) + P(B).<br>\n<strong>Example:</strong> Rolling a die, P(2 or 5) = 1/6 + 1/6 = 2/6 = 1/3.</p>\n\n<h3 class="learn-subheading">2. Non-Mutually Exclusive Events</h3>\n<p class="learn-p">When events CAN overlap: P(A or B) = P(A) + P(B) &minus; P(A and B).<br>\n<strong>Example:</strong> Drawing a card, P(King or Heart) = 4/52 + 13/52 &minus; 1/52 = 16/52 = 4/13 (subtracting the King of Hearts, counted twice).</p>\n\n<h3 class="learn-subheading">3. Independent Events (Multiplication Rule)</h3>\n<p class="learn-p">Events where one does not affect the other: P(A and B) = P(A) &times; P(B).<br>\n<strong>Example:</strong> Tossing a coin and rolling a die: P(Head and 6) = 1/2 &times; 1/6 = 1/12.</p>\n\n<h3 class="learn-subheading">4. Dependent Events (Conditional Probability)</h3>\n<p class="learn-p">When the outcome of one event affects the other, P(A and B) = P(A) &times; P(B|A), where P(B|A) is the probability of B given A has occurred.<br>\n<strong>Example:</strong> Drawing 2 cards without replacement, P(2 Kings) = 4/52 &times; 3/51 = 12/2652 = 1/221.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Event Type</th><th>Rule</th></tr></thead>\n    <tbody>\n      <tr><td>Mutually exclusive (OR)</td><td>P(A or B) = P(A) + P(B)</td></tr>\n      <tr><td>Not mutually exclusive (OR)</td><td>P(A or B) = P(A) + P(B) &minus; P(A and B)</td></tr>\n      <tr><td>Independent (AND)</td><td>P(A and B) = P(A) &times; P(B)</td></tr>\n      <tr><td>Dependent (AND, without replacement)</td><td>P(A and B) = P(A) &times; P(B|A)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> "OR" usually means addition; "AND" usually means multiplication. Always check first whether events are mutually exclusive (can\'t overlap) or independent (don\'t affect each other) before choosing your formula.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Use addition for OR events (subtracting overlap if not mutually exclusive), and multiplication for AND events (adjusting for dependence if outcomes affect each other, especially "without replacement" scenarios).\n</div>\n',
    questions: [
      {
        q: 'A die is rolled. Find P(rolling a 2 or a 5).',
        o: ['1/3', '1/6', '2/6 written as 1/3 already listed, use 1/12', '5/6'],
        a: 0,
        e: 'These are mutually exclusive: P(2 or 5) = 1/6 + 1/6 = 2/6 = 1/3.',
        h: "Since a die can't show two numbers at once, these events are mutually exclusive — simply add.",
        yr: 'SS2',
      },
      {
        q: 'A card is drawn from a 52-card deck. Find P(King or Heart).',
        o: ['4/13', '17/52', '1/13', '16/13'],
        a: 0,
        e: 'P(King)=4/52, P(Heart)=13/52, P(King and Heart)=1/52. P(King or Heart)=4/52+13/52−1/52=16/52=4/13.',
        h: 'These events overlap (King of Hearts), so subtract the intersection.',
        yr: 'SS2',
      },
      {
        q: 'A coin is tossed and a die is rolled. Find P(Head and 6).',
        o: ['1/12', '1/2', '1/6', '1/8'],
        a: 0,
        e: 'These are independent events: P(Head and 6) = 1/2 × 1/6 = 1/12.',
        h: "Coin and die outcomes don't affect each other — multiply their individual probabilities.",
        yr: 'SS2',
      },
      {
        q: 'Two cards are drawn without replacement from a 52-card deck. Find P(both are Kings).',
        o: ['1/221', '1/169', '4/52', '1/13'],
        a: 0,
        e: 'P(1st King)=4/52. P(2nd King|1st King)=3/51. P(both)=4/52×3/51=12/2652=1/221.',
        h: 'Without replacement, the second probability changes because one card is already removed.',
        yr: 'SS2',
      },
      {
        q: 'A bag has 4 red and 6 blue balls. One ball is drawn, replaced, then another is drawn. Find P(both red).',
        o: ['4/25', '2/5', '16/100 simplified is same as 4/25', '6/10'],
        a: 0,
        e: 'With replacement, each draw is independent: P(red)=4/10=2/5. P(both red)=2/5×2/5=4/25.',
        h: "'Replaced' means the two draws are independent — use the same probability twice.",
        yr: 'SS2',
      },
      {
        q: 'A bag has 4 red and 6 blue balls. Two balls are drawn WITHOUT replacement. Find P(both red).',
        o: ['2/15', '4/25', '6/45', '1/5'],
        a: 0,
        e: 'P(1st red)=4/10. P(2nd red|1st red)=3/9. P(both)=4/10×3/9=12/90=2/15.',
        h: 'Without replacement, adjust both the numerator and denominator for the second draw.',
        yr: 'SS2',
      },
      {
        q: "Are the events 'rolling an even number' and 'rolling a number greater than 4' on a die mutually exclusive?",
        o: [
          'No, since 6 satisfies both conditions',
          'Yes, they never overlap',
          'Cannot be determined',
          'Yes, since even numbers are never greater than 4',
        ],
        a: 0,
        e: 'Even numbers: {2,4,6}. Greater than 4: {5,6}. They share the outcome 6, so they are NOT mutually exclusive.',
        h: 'Check whether the two event sets share any common outcomes.',
        yr: 'SS2',
      },
      {
        q: 'Find P(rolling an even number OR a number greater than 4) on a single die.',
        o: ['4/6 (= 2/3)', '5/6', '3/6', '1'],
        a: 0,
        e: 'P(even)=3/6, P(>4)=2/6, P(both)=1/6 (just the outcome 6). P(even or >4)=3/6+2/6−1/6=4/6=2/3.',
        h: 'Use the addition rule for non-mutually exclusive events, subtracting the overlap.',
        yr: 'SS2',
      },
      {
        q: "A spinner has 4 equal colours: red, blue, green, yellow. It's spun twice. Find P(red both times).",
        o: ['1/16', '1/4', '1/8', '2/4'],
        a: 0,
        e: 'Each spin is independent: P(red)=1/4. P(red, red)=1/4×1/4=1/16.',
        h: "Each spin doesn't affect the next — multiply the individual probabilities.",
        yr: 'SS2',
      },
      {
        q: 'In a class, P(likes Math) = 0.6, P(likes Science) = 0.5, P(likes both) = 0.3. Find P(likes Math or Science).',
        o: ['0.8', '1.1', '0.3', '0.9'],
        a: 0,
        e: 'P(M or S) = P(M)+P(S)−P(M and S) = 0.6+0.5−0.3 = 0.8.',
        h: 'Apply the general addition rule for non-mutually exclusive events.',
        yr: 'SS2',
      },
      {
        q: 'A box has 5 white and 3 black balls. Two balls are drawn without replacement. Find P(1st white AND 2nd black).',
        o: ['15/56', '5/8', '3/8', '15/64'],
        a: 0,
        e: 'P(1st white)=5/8. P(2nd black|1st white)=3/7. P=5/8×3/7=15/56.',
        h: 'Adjust the total count for the second draw since the first ball is removed.',
        yr: 'SS2',
      },
      {
        q: 'Two dice are rolled. Find P(both show the same number).',
        o: ['1/6', '1/36', '1/12', '1/3'],
        a: 0,
        e: 'Favourable outcomes: (1,1),(2,2),...,(6,6) = 6 outcomes out of 36. P = 6/36 = 1/6.',
        h: 'List the pairs where both dice match, then divide by the total 36 outcomes.',
        yr: 'SS2',
      },
      {
        q: 'Events A and B are independent, with P(A) = 0.4 and P(B) = 0.5. Find P(A and B).',
        o: ['0.2', '0.9', '0.1', '0.45'],
        a: 0,
        e: 'For independent events: P(A and B) = P(A) × P(B) = 0.4 × 0.5 = 0.2.',
        h: 'Simply multiply the two individual probabilities together.',
        yr: 'SS2',
      },
      {
        q: 'A card is drawn from a deck. Find P(drawing a Spade or a Club) (these are mutually exclusive suits).',
        o: ['1/2', '1/4', '1/13', '26/52 which equals 1/2, or maybe list distractor as 3/4'],
        a: 0,
        e: "P(Spade)=13/52, P(Club)=13/52. Since a card can't be both suits, they're mutually exclusive: P=13/52+13/52=26/52=1/2.",
        h: 'Spades and clubs never overlap on a single card — simply add.',
        yr: 'SS2',
      },
      {
        q: 'A committee interview has 3 candidates. If P(pass first stage) = 0.7 for each candidate independently, find P(all 3 pass).',
        o: ['0.343', '0.7', '2.1', '0.21'],
        a: 0,
        e: 'Since independent: P(all 3 pass) = 0.7×0.7×0.7 = 0.343.',
        h: 'Multiply the individual probability by itself for each independent candidate.',
        yr: 'SS2',
      },
      {
        q: 'A bag has 10 balls: 3 red, 4 blue, 3 green. Find P(red or green) (mutually exclusive colours).',
        o: ['6/10 (= 3/5)', '7/10', '3/10', '4/10'],
        a: 0,
        e: "P(red)=3/10, P(green)=3/10. Since a ball can't be two colours: P(red or green)=3/10+3/10=6/10=3/5.",
        h: 'A single ball can only be one colour, so these are mutually exclusive events.',
        yr: 'SS2',
      },
      {
        q: 'If P(A) = 0.3 and events A and B are mutually exclusive with P(A or B) = 0.8, find P(B).',
        o: ['0.5', '0.8', '1.1', '0.3'],
        a: 0,
        e: 'For mutually exclusive events, P(A or B)=P(A)+P(B) ⇒ 0.8=0.3+P(B) ⇒ P(B)=0.5.',
        h: 'Rearrange the mutually exclusive addition formula to solve for the unknown probability.',
        yr: 'SS2',
      },
      {
        q: 'Three coins are tossed. Find P(all three show Heads).',
        o: ['1/8', '1/2', '3/8', '1/6'],
        a: 0,
        e: 'Each coin is independent: P(H,H,H) = 1/2 × 1/2 × 1/2 = 1/8.',
        h: 'Multiply the probability of heads for each of the three independent coin tosses.',
        yr: 'SS2',
      },
      {
        q: 'A number is chosen at random from 1 to 20. Find P(multiple of 3 or multiple of 5).',
        o: ['9/20', '6/20', '4/20', '10/20'],
        a: 0,
        e: 'Multiples of 3: {3,6,9,12,15,18}=6. Multiples of 5: {5,10,15,20}=4. Overlap (multiples of 15): {15}=1. P=6/20+4/20−1/20=9/20.',
        h: 'Identify the overlap (multiples of both 3 and 5, i.e., multiples of 15) before applying the addition rule.',
        yr: 'SS2',
      },
      {
        q: 'A box has 6 defective and 14 non-defective items. Two items are picked without replacement. Find P(both defective).',
        o: ['3/38', '6/20', '36/400', '5/19'],
        a: 0,
        e: 'P(1st defective)=6/20. P(2nd defective|1st)=5/19. P(both)=6/20×5/19=30/380=3/38.',
        h: 'Adjust the count for the second draw since one defective item has already been removed.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Permutation and Combination I',
    topicCode: 'SS2-FMATH-24',
    module: 'Combinatorics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Combinatorics</span> counts the number of ways to arrange or select objects. We start with <span class="learn-keyword">factorials</span> and the <span class="learn-keyword">permutation</span> &mdash; arrangements where ORDER matters.</div>\n\n<h3 class="learn-subheading">1. Factorial Notation</h3>\n<p class="learn-p">n! = n &times; (n&minus;1) &times; (n&minus;2) &times; ... &times; 2 &times; 1. By convention, 0! = 1.<br>\n<strong>Example:</strong> 5! = 5&times;4&times;3&times;2&times;1 = 120.</p>\n\n<h3 class="learn-subheading">2. Permutations (Order Matters)</h3>\n<p class="learn-p">The number of ways to arrange r objects from n distinct objects: &#8319;P&#8348; = n!/(n&minus;r)!<br>\n<strong>Example:</strong> Arrange 3 people from a group of 5 in a row. &#8309;P&#8323; = 5!/2! = 120/2 = 60.</p>\n\n<h3 class="learn-subheading">3. Permutations of All n Objects</h3>\n<p class="learn-p">Arranging ALL n distinct objects: &#8319;P&#8319; = n!. <strong>Example:</strong> Arrange all 4 letters of "GOLD": 4! = 24 ways.</p>\n\n<h3 class="learn-subheading">4. Permutations with Repetition</h3>\n<p class="learn-p">When some items repeat, divide by the factorial of each repeat count. <strong>Example:</strong> Arrange the letters of "LEVEL" (5 letters, with L repeated twice and E repeated twice): 5!/(2!&times;2!) = 120/4 = 30.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Formula</th><th>Use Case</th></tr></thead>\n    <tbody>\n      <tr><td>&#8319;P&#8348; = n!/(n&minus;r)!</td><td>Arrange r objects from n distinct objects, order matters</td></tr>\n      <tr><td>n!</td><td>Arrange all n distinct objects</td></tr>\n      <tr><td>n!/(p!&middot;q!&middot;...)</td><td>Arrange n objects with repeated items (p, q, ... repeats)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> The keyword "arrange" or "order" (like a queue, a race position, a password) signals a PERMUTATION. If swapping two chosen items would create a genuinely different outcome, order matters &mdash; use permutations.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Permutations count arrangements where order matters. &#8319;P&#8348;=n!/(n&minus;r)! for selecting and arranging r from n. Divide by repeated-item factorials when some objects are identical.\n</div>\n',
    questions: [
      {
        q: 'Evaluate 5!.',
        o: ['120', '24', '60', '720'],
        a: 0,
        e: '5! = 5×4×3×2×1 = 120.',
        h: 'Multiply all whole numbers from 5 down to 1.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate 0!.',
        o: ['1', '0', 'Undefined', '10'],
        a: 0,
        e: 'By mathematical convention, 0! is defined as 1.',
        h: 'Recall the special convention for zero factorial.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁷P₃.',
        o: ['210', '35', '5040', '840'],
        a: 0,
        e: '⁷P₃ = 7!/(7−3)! = 7!/4! = 7×6×5 = 210.',
        h: 'Use ⁿPᵣ = n!/(n−r)!, which simplifies to the product of r consecutive descending integers starting at n.',
        yr: 'SS2',
      },
      {
        q: 'How many ways can 3 people be arranged from a group of 5, in a row?',
        o: ['60', '10', '15', '120'],
        a: 0,
        e: '⁵P₃ = 5!/2! = 120/2 = 60.',
        h: 'This is a direct application of the permutation formula ⁿPᵣ.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can all 4 letters of the word "GOLD" be arranged?',
        o: ['24', '12', '4', '16'],
        a: 0,
        e: 'All letters are distinct: 4! = 24.',
        h: 'Since all letters are different, use n! directly.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can the letters of "LEVEL" be arranged (L repeats twice, E repeats twice)?',
        o: ['30', '120', '60', '20'],
        a: 0,
        e: 'Total letters = 5. Arrangements = 5!/(2!×2!) = 120/4 = 30.',
        h: "Divide the total arrangements (5!) by the factorial of each repeated letter's count.",
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 4 books be arranged on a shelf?',
        o: ['24', '12', '16', '4'],
        a: 0,
        e: 'All 4 books are distinct: 4! = 24.',
        h: 'Arranging all distinct objects uses n! directly.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁶P₂.',
        o: ['30', '15', '12', '720'],
        a: 0,
        e: '⁶P₂ = 6!/4! = 6×5 = 30.',
        h: 'Multiply the top r=2 descending values starting from n=6: 6×5.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can a President and Vice-President be chosen from 8 candidates (positions are distinct)?',
        o: ['56', '28', '64', '16'],
        a: 0,
        e: 'This is a permutation since the two roles differ: ⁸P₂ = 8×7 = 56.',
        h: 'Since President and VP are different roles, order (who gets which role) matters.',
        yr: 'SS2',
      },
      {
        q: 'How many different 4-digit codes can be formed from the digits 1-9 if no digit repeats?',
        o: ['3024', '9000', '6561', '362880'],
        a: 0,
        e: '⁹P₄ = 9!/5! = 9×8×7×6 = 3024.',
        h: 'Use the permutation formula with n=9 digits, choosing r=4 positions, no repetition.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can the letters of "BANANA" be arranged (A repeats 3 times, N repeats 2 times)?',
        o: ['60', '720', '120', '360'],
        a: 0,
        e: 'Total letters = 6. Arrangements = 6!/(3!×2!) = 720/12 = 60.',
        h: "Divide 6! by the factorial of each repeated letter count (3 A's and 2 N's).",
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁸P₁.',
        o: ['8', '1', '40320', '56'],
        a: 0,
        e: '⁸P₁ = 8!/7! = 8.',
        h: 'Choosing and arranging just 1 object from n simply gives n itself.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 6 runners finish 1st, 2nd, and 3rd place in a race (no ties)?',
        o: ['120', '20', '216', '18'],
        a: 0,
        e: '⁶P₃ = 6!/3! = 6×5×4 = 120.',
        h: 'Since positions (1st, 2nd, 3rd) are distinct, use the permutation formula.',
        yr: 'SS2',
      },
      {
        q: 'Simplify n!/(n−1)!.',
        o: ['n', '1', 'n−1', 'n(n−1)'],
        a: 0,
        e: 'n!/(n−1)! = [n×(n−1)!]/(n−1)! = n.',
        h: 'Expand n! as n × (n−1)! and cancel the common factor.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can all 5 letters of "MATHS" be arranged?',
        o: ['120', '24', '60', '20'],
        a: 0,
        e: 'All 5 letters are distinct: 5! = 120.',
        h: 'Since every letter in MATHS is different, use n! directly.',
        yr: 'SS2',
      },
      {
        q: 'How many 3-letter arrangements (no repeats) can be made from the letters A, B, C, D, E?',
        o: ['60', '10', '125', '20'],
        a: 0,
        e: '⁵P₃ = 5!/2! = 60.',
        h: 'Use the permutation formula with n=5 letters, arranging r=3 at a time.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can the digits of the number 1223 be arranged (2 repeats twice)?',
        o: ['12', '24', '6', '4'],
        a: 0,
        e: "Total digits = 4, with '2' repeated twice. Arrangements = 4!/2! = 24/2 = 12.",
        h: "Divide 4! by the factorial of the repeated digit's count.",
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁹P⁹ (arranging all 9 distinct objects).',
        o: ['362880', '9', '81', '40320'],
        a: 0,
        e: '⁹P₉ = 9!/0! = 9!/1 = 362880.',
        h: 'Arranging all n objects (r=n) simplifies to n!/0! = n!.',
        yr: 'SS2',
      },
      {
        q: 'In a queue of 7 people, how many different ways can they be arranged?',
        o: ['5040', '49', '7', '720'],
        a: 0,
        e: 'All 7 people are distinct: 7! = 5040.',
        h: 'This is a straightforward n! calculation since all positions matter and all people are distinct.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 3 different books be chosen and arranged on a shelf from a collection of 6 different books?',
        o: ['120', '20', '18', '216'],
        a: 0,
        e: '⁶P₃ = 6!/3! = 6×5×4 = 120.',
        h: 'Apply the permutation formula with n=6, r=3.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Permutation and Combination II',
    topicCode: 'SS2-FMATH-25',
    module: 'Combinatorics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  Now we turn to <span class="learn-keyword">combinations</span> &mdash; selections where ORDER does NOT matter. We also handle circular arrangements and mixed problems that combine both permutations and combinations.</div>\n\n<h3 class="learn-subheading">1. Combinations (Order Doesn\'t Matter)</h3>\n<p class="learn-p">The number of ways to select r objects from n distinct objects, without regard to order: &#8319;C&#8348; = n!/[r!(n&minus;r)!]<br>\n<strong>Example:</strong> Choose 3 people from 5 for a committee (no distinct roles). &#8309;C&#8323; = 5!/(3!2!) = 120/12 = 10.</p>\n\n<h3 class="learn-subheading">2. Permutation vs. Combination</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Permutation</th><th>Combination</th></tr></thead>\n    <tbody>\n      <tr><td>Order MATTERS (arrangement)</td><td>Order does NOT matter (selection)</td></tr>\n      <tr><td>e.g., race positions, passwords</td><td>e.g., committees, teams, lottery numbers</td></tr>\n      <tr><td>&#8319;P&#8348; = n!/(n&minus;r)!</td><td>&#8319;C&#8348; = n!/[r!(n&minus;r)!] = &#8319;P&#8348;/r!</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Circular Permutations</h3>\n<p class="learn-p">Arranging n distinct objects in a CIRCLE: (n&minus;1)! ways (since rotations of the same arrangement are considered identical).<br>\n<strong>Example:</strong> Seat 5 people around a round table: (5&minus;1)! = 4! = 24 ways.</p>\n\n<h3 class="learn-subheading">4. Combined Problems</h3>\n<p class="learn-p"><strong>Example:</strong> From a group of 6 men and 4 women, select a committee of 3 men and 2 women.<br>\nWays = &#8310;C&#8323; &times; &#8308;C&#8322; = 20 &times; 6 = 120.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Ask yourself: "Does swapping two selected items create a new outcome?" If YES (e.g., 1st vs 2nd place), use permutations. If NO (e.g., both are just "on the committee"), use combinations.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Combinations count selections where order is irrelevant: &#8319;C&#8348;=n!/[r!(n&minus;r)!]. Circular permutations use (n&minus;1)! since rotations don\'t create new arrangements. Combined problems multiply separate combination counts together.\n</div>\n',
    questions: [
      {
        q: 'Evaluate ⁵C₃.',
        o: ['10', '20', '60', '6'],
        a: 0,
        e: '⁵C₃ = 5!/(3!2!) = 120/(6×2) = 120/12 = 10.',
        h: 'Apply the combination formula n!/[r!(n−r)!].',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can a committee of 3 be chosen from a group of 5 people (no distinct roles)?',
        o: ['10', '60', '20', '5'],
        a: 0,
        e: "This is a combination since roles don't differ: ⁵C₃ = 10.",
        h: "Since all committee members have the same role, order doesn't matter — use combinations.",
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁷C₂.',
        o: ['21', '42', '14', '7'],
        a: 0,
        e: '⁷C₂ = 7!/(2!5!) = (7×6)/2 = 21.',
        h: 'Use the shortcut ⁿCᵣ = n(n−1).../r! for small r values.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 5 people be seated around a round table?',
        o: ['24', '120', '20', '5'],
        a: 0,
        e: 'Circular permutations of n objects = (n−1)! = (5−1)! = 4! = 24.',
        h: 'Use (n−1)! for circular arrangements, since rotations are considered the same.',
        yr: 'SS2',
      },
      {
        q: 'From 6 men and 4 women, select a committee of 3 men and 2 women. How many ways?',
        o: ['120', '20', '6', '24'],
        a: 0,
        e: '⁶C₃ × ⁴C₂ = 20 × 6 = 120.',
        h: 'Choose the men and women separately using combinations, then multiply the results.',
        yr: 'SS2',
      },
      {
        q: 'How many different 4-person committees can be selected from a group of 10 people?',
        o: ['210', '5040', '40', '24'],
        a: 0,
        e: '¹⁰C₄ = 10!/(4!6!) = 210.',
        h: 'Apply the combination formula with n=10, r=4.',
        yr: 'SS2',
      },
      {
        q: 'Which scenario requires a COMBINATION (not permutation)?',
        o: [
          'Choosing 3 students for a team with no distinct positions',
          'Choosing 1st, 2nd, 3rd place winners',
          'Arranging books on a shelf',
          'Assigning distinct roles: chairperson, secretary, treasurer',
        ],
        a: 0,
        e: "A team with no distinct positions means order doesn't matter — this is a combination. The other three scenarios all involve distinguishable positions/order.",
        h: 'Look for whether the selected items have distinct roles or ranking (permutation) or not (combination).',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁶C⁶.',
        o: ['1', '6', '720', '0'],
        a: 0,
        e: "⁶C₆ = 6!/(6!0!) = 1 (there's only one way to choose ALL objects).",
        h: 'Choosing all n objects from n can only be done in one way.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ⁶C₀.',
        o: ['1', '0', '6', '720'],
        a: 0,
        e: "⁶C₀ = 6!/(0!6!) = 1 (there's exactly one way to choose nothing).",
        h: 'Choosing zero objects can only be done in one way — by selecting none.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 6 keys be arranged on a circular keyring? (Note: for a keyring, reflections are also identical, giving (n-1)!/2, but treat this as a simple circular arrangement (n-1)! for this question.)',
        o: ['120', '720', '24', '60'],
        a: 0,
        e: 'Using simple circular permutation (n−1)! = (6−1)! = 5! = 120.',
        h: 'Apply (n−1)! for a basic circular arrangement of 6 distinct objects.',
        yr: 'SS2',
      },
      {
        q: "A lottery requires choosing 6 numbers from 49, order doesn't matter. How many possible selections are there? (Just set up: ⁴⁹C₆.)",
        o: ['⁴⁹C₆ = 49!/(6!43!)', '⁴⁹P₆', '49!/6!', '49×6'],
        a: 0,
        e: "Since order doesn't matter in lottery number selection, this is a combination: ⁴⁹C₆ = 49!/(6!43!).",
        h: 'Lottery draws are unordered selections — the defining feature of a combination.',
        yr: 'SS2',
      },
      {
        q: 'Simplify ⁿCᵣ in terms of ⁿPᵣ.',
        o: ['ⁿCᵣ = ⁿPᵣ / r!', 'ⁿCᵣ = ⁿPᵣ × r!', 'ⁿCᵣ = ⁿPᵣ + r!', 'ⁿCᵣ = ⁿPᵣ − r!'],
        a: 0,
        e: 'Since a combination ignores the r! internal orderings that a permutation counts separately, ⁿCᵣ = ⁿPᵣ/r!.',
        h: 'A combination is a permutation divided by the number of ways to reorder the chosen items.',
        yr: 'SS2',
      },
      {
        q: 'From a class of 12 students, how many ways can a group of 5 be selected for a project (no distinct roles)?',
        o: ['792', '95040', '60', '17'],
        a: 0,
        e: '¹²C₅ = 12!/(5!7!) = 792.',
        h: 'Apply the combination formula with n=12, r=5.',
        yr: 'SS2',
      },
      {
        q: 'How many diagonals does a hexagon (6 sides) have? (Hint: choose 2 vertices from 6, then subtract the 6 sides.)',
        o: ['9', '15', '6', '12'],
        a: 0,
        e: '⁶C₂ = 15 total line segments between vertices. Subtracting the 6 sides: 15−6 = 9 diagonals.',
        h: 'First find all possible connections between vertices (a combination), then remove the sides themselves.',
        yr: 'SS2',
      },
      {
        q: 'A quiz has 10 questions, and a student must answer any 7. How many ways can the 7 be chosen?',
        o: ['120', '5040', '70', '10'],
        a: 0,
        e: '¹⁰C₇ = 10!/(7!3!) = 120.',
        h: "Since the order of answering doesn't affect which questions are 'chosen', use combinations.",
        yr: 'SS2',
      },
      {
        q: 'Compare ⁸C₃ and ⁸C₅. What do you notice?',
        o: [
          'They are equal, since ⁿCᵣ = ⁿCₙ₋ᵣ',
          '⁸C₃ is larger',
          '⁸C₅ is larger',
          'They cannot be compared',
        ],
        a: 0,
        e: 'By the symmetry property ⁿCᵣ = ⁿCₙ₋ᵣ: ⁸C₃ = ⁸C₅ (both equal 56), since choosing 3 to include is the same as choosing 5 to exclude.',
        h: 'Recall the symmetry property of combinations: choosing r is equivalent to choosing (n−r) to leave out.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can a hand of 5 cards be dealt from a standard 52-card deck?',
        o: ['⁵²C₅', '⁵²P₅', '52×5', '5!'],
        a: 0,
        e: "Since the order in which cards are dealt into a hand doesn't matter, this is a combination: ⁵²C₅.",
        h: 'A hand of cards is an unordered selection — use combinations, not permutations.',
        yr: 'SS2',
      },
      {
        q: 'A group has 5 boys and 3 girls. How many ways can a team of 2 boys and 1 girl be chosen?',
        o: ['30', '15', '8', '10'],
        a: 0,
        e: '⁵C₂ × ³C₁ = 10 × 3 = 30.',
        h: 'Choose the boys and the girl separately using combinations, then multiply.',
        yr: 'SS2',
      },
      {
        q: 'In how many ways can 8 people be seated around a circular table?',
        o: ['5040', '40320', '56', '64'],
        a: 0,
        e: '(n−1)! = (8−1)! = 7! = 5040.',
        h: 'Apply the circular permutation formula (n−1)! for 8 distinct people.',
        yr: 'SS2',
      },
      {
        q: 'Which is generally TRUE for a fixed n and r (with 1 < r < n)?',
        o: [
          'ⁿPᵣ is always greater than ⁿCᵣ',
          'ⁿCᵣ is always greater than ⁿPᵣ',
          'They are always equal',
          'No general relationship exists',
        ],
        a: 0,
        e: "Since ⁿPᵣ = ⁿCᵣ × r! and r! ≥ 2 for r > 1, permutations count MORE arrangements than combinations (as permutations distinguish order while combinations don't).",
        h: 'Recall that ⁿPᵣ = ⁿCᵣ × r!, and r! is always at least 1 (greater than 1 for r>1).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: "Dynamics I — Newton's Laws and Motion on Inclined Planes",
    topicCode: 'SS2-FMATH-26',
    module: 'Mechanics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Dynamics</span> is the study of forces and their effect on motion. We apply <span class="learn-keyword">Newton\'s Laws of Motion</span> to solve problems involving objects on flat surfaces and inclined planes, always taking g = 10 m/s&sup2; unless stated otherwise.</div>\n\n<h3 class="learn-subheading">1. Newton\'s Three Laws</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Law</th><th>Statement</th></tr></thead>\n    <tbody>\n      <tr><td>1st Law</td><td>An object stays at rest or in uniform motion unless acted on by a net force</td></tr>\n      <tr><td>2nd Law</td><td>F = ma (net force = mass &times; acceleration)</td></tr>\n      <tr><td>3rd Law</td><td>Every action has an equal and opposite reaction</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">2. Motion on a Horizontal Surface</h3>\n<p class="learn-p"><strong>Example:</strong> A 5 kg block is pushed with a force of 20 N on a frictionless surface. Find its acceleration.<br>\nF = ma &rArr; 20 = 5a &rArr; a = 4 m/s&sup2;.</p>\n\n<h3 class="learn-subheading">3. Motion with Friction</h3>\n<p class="learn-p">Frictional force F<sub>f</sub> = &mu;R, where &mu; is the coefficient of friction and R is the normal reaction.<br>\n<strong>Example:</strong> A 10 kg block on a horizontal surface (&mu;=0.2) is pushed with 40 N. Find acceleration.<br>\nR = mg = 100 N. F<sub>f</sub> = 0.2&times;100 = 20 N. Net force = 40&minus;20 = 20 N. a = F/m = 20/10 = 2 m/s&sup2;.</p>\n\n<h3 class="learn-subheading">4. Motion on an Inclined Plane</h3>\n<p class="learn-p">For a smooth (frictionless) incline at angle &theta;: component of weight along the incline = mg sin&theta;; perpendicular component (normal reaction) = mg cos&theta;.<br>\n<strong>Example:</strong> A 2 kg block slides down a smooth 30&deg; incline. Find its acceleration.<br>\na = g sin&theta; = 10 &times; sin30&deg; = 10 &times; 0.5 = 5 m/s&sup2;.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always draw a clear force diagram first, resolving weight into components along and perpendicular to the direction of motion. This prevents sign errors when several forces act together.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Newton\'s Second Law F=ma is the master equation. On inclines, resolve weight into mg sin&theta; (along the slope) and mg cos&theta; (perpendicular, giving the normal reaction). Friction opposes motion, with magnitude &mu;R.\n</div>\n',
    questions: [
      {
        q: 'A 5 kg block is pushed with a force of 20 N on a frictionless surface. Find its acceleration.',
        o: ['4 m/s²', '5 m/s²', '20 m/s²', '100 m/s²'],
        a: 0,
        e: 'F=ma ⇒ 20=5a ⇒ a=4 m/s².',
        h: "Apply Newton's Second Law: F = ma, solving for a.",
        yr: 'SS2',
      },
      {
        q: "Which of Newton's laws states that F = ma?",
        o: ['Second Law', 'First Law', 'Third Law', 'Fourth Law'],
        a: 0,
        e: "Newton's Second Law states that the net force on an object equals its mass times its acceleration.",
        h: 'Recall which of the three numbered laws gives the force-mass-acceleration equation.',
        yr: 'SS2',
      },
      {
        q: 'A 10 kg block on a horizontal surface (μ=0.2) is pushed with a force of 40 N. Find its acceleration (g=10 m/s²).',
        o: ['2 m/s²', '4 m/s²', '1.8 m/s²', '2.2 m/s²'],
        a: 0,
        e: 'R=mg=100N. Friction=0.2×100=20N. Net force=40−20=20N. a=F/m=20/10=2 m/s².',
        h: 'Find the frictional force first, subtract it from the applied force, then apply F=ma.',
        yr: 'SS2',
      },
      {
        q: 'A 2 kg block slides down a smooth 30° incline. Find its acceleration (g=10 m/s²).',
        o: ['5 m/s²', '10 m/s²', '8.66 m/s²', '2.5 m/s²'],
        a: 0,
        e: 'a = g sinθ = 10 × sin30° = 10 × 0.5 = 5 m/s².',
        h: 'For a smooth incline, acceleration down the slope is g sinθ.',
        yr: 'SS2',
      },
      {
        q: 'Find the normal reaction on a 4 kg block resting on a horizontal surface (g=10 m/s²).',
        o: ['40 N', '4 N', '10 N', '400 N'],
        a: 0,
        e: 'On a horizontal surface, R = mg = 4×10 = 40 N.',
        h: 'On a flat horizontal surface, normal reaction equals the full weight.',
        yr: 'SS2',
      },
      {
        q: 'Find the normal reaction on a 4 kg block on a smooth 30° incline (g=10 m/s²).',
        o: ['34.64 N', '40 N', '20 N', '23.09 N'],
        a: 0,
        e: 'R = mg cosθ = 4×10×cos30° = 40×0.866 = 34.64 N.',
        h: 'On an incline, normal reaction is the perpendicular component: mg cosθ.',
        yr: 'SS2',
      },
      {
        q: 'A net force of 15 N acts on an object, giving it an acceleration of 3 m/s². Find the mass of the object.',
        o: ['5 kg', '45 kg', '0.2 kg', '18 kg'],
        a: 0,
        e: 'F=ma ⇒ 15=m(3) ⇒ m=5 kg.',
        h: 'Rearrange F = ma to solve for mass.',
        yr: 'SS2',
      },
      {
        q: 'A block of mass 6 kg rests on a rough horizontal surface with μ=0.25. Find the maximum frictional force (g=10 m/s²).',
        o: ['15 N', '60 N', '6 N', '1.5 N'],
        a: 0,
        e: 'R=mg=60N. Max friction = μR = 0.25×60 = 15 N.',
        h: 'Find the normal reaction first, then multiply by the coefficient of friction.',
        yr: 'SS2',
      },
      {
        q: 'A 3 kg block on a rough 30° incline (μ=0.2) slides down. Find its acceleration (g=10 m/s²). [a = g sinθ − μg cosθ]',
        o: ['3.27 m/s²', '5 m/s²', '1.73 m/s²', '6.73 m/s²'],
        a: 0,
        e: 'a = g sinθ − μg cosθ = 10(0.5) − 0.2(10)(0.866) = 5 − 1.732 = 3.27 m/s².',
        h: 'For a rough incline, subtract the friction deceleration term from the gravity acceleration term.',
        yr: 'SS2',
      },
      {
        q: "According to Newton's Third Law, if object A exerts a force on object B, what happens?",
        o: [
          'B exerts an equal and opposite force on A',
          'B exerts a smaller force on A',
          'No force acts on A from B',
          "A's force disappears",
        ],
        a: 0,
        e: "Newton's Third Law states every action has an equal and opposite reaction — B pushes back on A with equal magnitude but opposite direction.",
        h: 'Recall the action-reaction principle central to the Third Law.',
        yr: 'SS2',
      },
      {
        q: 'A 5 kg block on a smooth 45° incline. Find its acceleration down the slope (g=10 m/s², sin45°≈cos45°≈0.707).',
        o: ['7.07 m/s²', '10 m/s²', '5 m/s²', '3.54 m/s²'],
        a: 0,
        e: 'a = g sin45° = 10×0.707 ≈ 7.07 m/s².',
        h: 'Apply a = g sinθ with θ = 45°.',
        yr: 'SS2',
      },
      {
        q: 'A car of mass 1000 kg accelerates at 2 m/s². Find the net force required.',
        o: ['2000 N', '500 N', '1000 N', '2 N'],
        a: 0,
        e: 'F = ma = 1000×2 = 2000 N.',
        h: "Direct application of Newton's Second Law.",
        yr: 'SS2',
      },
      {
        q: 'An object remains at rest with no net force acting on it. Which law explains this?',
        o: [
          "Newton's First Law (law of inertia)",
          "Newton's Second Law",
          "Newton's Third Law",
          'The law of conservation of momentum',
        ],
        a: 0,
        e: "Newton's First Law states an object at rest stays at rest unless acted upon by a net (unbalanced) force — this is the law of inertia.",
        h: 'This describes objects maintaining their state of motion (or rest) without a net force.',
        yr: 'SS2',
      },
      {
        q: 'A 2 kg block is pulled up a smooth 30° incline with a force of 15 N along the incline. Find its acceleration (g=10 m/s²). [Net force = F − mg sinθ]',
        o: ['2.5 m/s²', '7.5 m/s²', '5 m/s²', '10 m/s²'],
        a: 0,
        e: 'mg sin30° = 2×10×0.5=10N. Net force = 15−10=5N. a=F/m=5/2=2.5 m/s².',
        h: 'Subtract the weight component along the incline from the applied force before applying F=ma.',
        yr: 'SS2',
      },
      {
        q: 'A block of mass 8 kg on a horizontal surface experiences a friction force of 16 N opposing an applied force of 24 N. Find the acceleration.',
        o: ['1 m/s²', '3 m/s²', '5 m/s²', '2 m/s²'],
        a: 0,
        e: 'Net force = 24−16 = 8N. a = F/m = 8/8 = 1 m/s².',
        h: 'Subtract friction from the applied force to find the net force, then apply F=ma.',
        yr: 'SS2',
      },
      {
        q: 'For an object on a smooth (frictionless) incline at angle θ, what force component causes it to slide?',
        o: [
          'mg sinθ, the component of weight parallel to the incline',
          'mg cosθ, the component perpendicular to the incline',
          'The full weight mg',
          'The normal reaction force',
        ],
        a: 0,
        e: 'The component of gravity acting ALONG the slope (parallel to it) is mg sinθ, and this is what causes the object to accelerate down the incline.',
        h: 'Only the component of weight parallel to the slope contributes to sliding motion.',
        yr: 'SS2',
      },
      {
        q: 'A 4 kg mass experiences forces of 10 N east and 6 N west simultaneously. Find its acceleration.',
        o: ['1 m/s² east', '4 m/s² east', '16 m/s² east', '0.4 m/s² east'],
        a: 0,
        e: 'Net force = 10−6 = 4N east. a = F/m = 4/4 = 1 m/s² east.',
        h: 'Find the net (resultant) force first by considering opposing directions, then apply F=ma.',
        yr: 'SS2',
      },
      {
        q: 'Which quantity has both magnitude and direction, making force resolution on inclines necessary?',
        o: [
          'Vector quantities like force and weight',
          'Scalar quantities like mass',
          'Only time',
          'Only speed',
        ],
        a: 0,
        e: 'Force is a vector quantity, meaning it has both magnitude and direction — this is why we must resolve it into components along and perpendicular to the incline.',
        h: 'Consider why direction matters when analyzing forces on angled surfaces.',
        yr: 'SS2',
      },
      {
        q: 'A 1.5 kg block on a smooth 60° incline. Find its acceleration down the slope (g=10 m/s², sin60°≈0.866).',
        o: ['8.66 m/s²', '10 m/s²', '5 m/s²', '6.5 m/s²'],
        a: 0,
        e: 'a = g sin60° = 10×0.866 = 8.66 m/s².',
        h: 'Apply a = g sinθ with the steeper angle of 60°.',
        yr: 'SS2',
      },
      {
        q: 'For a rough incline, which formula gives the acceleration of a block sliding DOWN the slope?',
        o: ['a = g sinθ − μg cosθ', 'a = g sinθ + μg cosθ', 'a = g cosθ − μg sinθ', 'a = μg sinθ'],
        a: 0,
        e: 'When sliding down, friction acts UP the slope (opposing motion), so it subtracts from the gravity component: a = g sinθ − μg cosθ.',
        h: 'Friction always opposes the direction of motion — here that means it opposes the downward slide.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Dynamics II — Connected Particles',
    topicCode: 'SS2-FMATH-27',
    module: 'Mechanics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  When two or more objects are connected by a string over a pulley, or linked together on a surface, we treat them as a <span class="learn-keyword">connected particle system</span>. Both objects share the same magnitude of acceleration (assuming an inextensible string), and we analyze each separately using Newton\'s Second Law.</div>\n\n<h3 class="learn-subheading">1. Two Masses Connected Over a Pulley (Atwood Machine)</h3>\n<p class="learn-p">Two masses m&#8321; and m&#8322; (m&#8321; &gt; m&#8322;) hang from a string over a frictionless pulley.<br>\nAcceleration: a = (m&#8321; &minus; m&#8322;)g / (m&#8321; + m&#8322;).<br>\nTension: T = m&#8322;(g + a), or equivalently T = m&#8321;(g &minus; a).</p>\n\n<h3 class="learn-subheading">2. Worked Example</h3>\n<p class="learn-p">Masses of 5 kg and 3 kg hang over a smooth pulley. Find the acceleration and tension (g=10 m/s&sup2;).<br>\na = (5&minus;3)(10)/(5+3) = 20/8 = 2.5 m/s&sup2;.<br>\nT = m&#8322;(g+a) = 3(10+2.5) = 37.5 N. Check: T = m&#8321;(g&minus;a) = 5(10&minus;2.5) = 37.5 N. &#10003;</p>\n\n<h3 class="learn-subheading">3. Masses Connected on a Horizontal Surface</h3>\n<p class="learn-p">Two blocks connected by a string, pulled by a force F. Treat as one system for total mass, then analyze the string tension for each block individually.<br>\n<strong>Example:</strong> Blocks of 2 kg and 3 kg connected by a string, pulled by 20 N (frictionless).<br>\na = F/(total mass) = 20/5 = 4 m/s&sup2;. Tension (pulling the 2kg block, at the front, so applying Newton\'s law to only the 3kg trailing block): T = m&times;a = 3&times;4 = 12 N.</p>\n\n<h3 class="learn-subheading">4. One Mass Hanging, One on a Table (via Pulley at Table Edge)</h3>\n<p class="learn-p">Mass m&#8321; on a smooth table, connected via a pulley to a hanging mass m&#8322;.<br>\na = m&#8322;g / (m&#8321;+m&#8322;). Tension: T = m&#8321;a.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always draw separate free-body diagrams for each mass, and remember the string tension is the SAME throughout an inextensible, massless string (unless the pulley has friction/mass, which is beyond this level).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Connected particles share the same acceleration magnitude. For pulley systems, a = (net driving force)/(total mass). Apply F=ma to each mass separately to find the tension in the connecting string.\n</div>\n',
    questions: [
      {
        q: 'Masses of 5 kg and 3 kg hang over a smooth pulley. Find the acceleration of the system (g=10 m/s²).',
        o: ['2.5 m/s²', '5 m/s²', '10 m/s²', '1.25 m/s²'],
        a: 0,
        e: 'a = (m₁−m₂)g/(m₁+m₂) = (5−3)(10)/(5+3) = 20/8 = 2.5 m/s².',
        h: 'Use a = (m₁−m₂)g/(m₁+m₂) for an Atwood machine, with m₁ the heavier mass.',
        yr: 'SS2',
      },
      {
        q: 'For the same system (5 kg and 3 kg over a pulley), find the tension in the string.',
        o: ['37.5 N', '50 N', '30 N', '80 N'],
        a: 0,
        e: 'T = m₂(g+a) = 3(10+2.5) = 37.5 N.',
        h: "Apply Newton's Second Law to the lighter mass, which accelerates upward.",
        yr: 'SS2',
      },
      {
        q: 'Two masses of 6 kg and 4 kg hang over a smooth pulley. Find the acceleration (g=10 m/s²).',
        o: ['2 m/s²', '10 m/s²', '4 m/s²', '6 m/s²'],
        a: 0,
        e: 'a = (6−4)(10)/(6+4) = 20/10 = 2 m/s².',
        h: 'Substitute both masses into the Atwood machine acceleration formula.',
        yr: 'SS2',
      },
      {
        q: 'For the 6 kg and 4 kg system, find the tension in the string.',
        o: ['48 N', '40 N', '60 N', '24 N'],
        a: 0,
        e: 'T = m₂(g+a) = 4(10+2) = 48 N. Check: T = m₁(g−a) = 6(10−2) = 48 N. ✓',
        h: "Apply Newton's Second Law to the lighter (upward-accelerating) mass.",
        yr: 'SS2',
      },
      {
        q: "Blocks of 2 kg and 3 kg on a frictionless horizontal surface are connected by a string and pulled by a force of 20 N. Find the system's acceleration.",
        o: ['4 m/s²', '10 m/s²', '6.67 m/s²', '20 m/s²'],
        a: 0,
        e: 'Total mass = 2+3=5kg. a = F/total mass = 20/5 = 4 m/s².',
        h: 'Treat the connected blocks as one system with combined mass for finding overall acceleration.',
        yr: 'SS2',
      },
      {
        q: 'For the previous system (2kg and 3kg, pulled by 20N, a=4m/s²), find the tension in the connecting string between the two blocks (assuming the 3kg block trails behind).',
        o: ['12 N', '20 N', '8 N', '15 N'],
        a: 0,
        e: 'Apply F=ma to just the trailing 3kg block: T = 3×4 = 12 N.',
        h: "Isolate just the trailing block and apply Newton's Second Law using only the tension as the driving force.",
        yr: 'SS2',
      },
      {
        q: "A 4 kg mass on a smooth table is connected via a pulley at the table's edge to a hanging 2 kg mass. Find the acceleration of the system (g=10 m/s²).",
        o: ['3.33 m/s²', '5 m/s²', '10 m/s²', '2 m/s²'],
        a: 0,
        e: 'a = m₂g/(m₁+m₂) = 2(10)/(4+2) = 20/6 ≈ 3.33 m/s².',
        h: 'Only the hanging mass contributes weight as the driving force; both masses share the total inertia.',
        yr: 'SS2',
      },
      {
        q: 'For the previous system (4kg on table, 2kg hanging), find the tension in the string.',
        o: ['13.33 N', '20 N', '6.67 N', '40 N'],
        a: 0,
        e: 'T = m₁a = 4×3.33 = 13.33 N.',
        h: "Apply Newton's Second Law to the mass on the table, where tension is the only horizontal force.",
        yr: 'SS2',
      },
      {
        q: 'In a pulley system, why do both masses have the same MAGNITUDE of acceleration?',
        o: [
          'The string is inextensible, so both masses move the same distance in the same time',
          'The masses are always equal',
          'Gravity affects both masses equally',
          'The pulley is frictionless',
        ],
        a: 0,
        e: "Since the string doesn't stretch (inextensible) and doesn't slip on the pulley, whatever distance one mass moves, the other moves the same distance, giving them equal magnitudes of acceleration.",
        h: 'Consider what an inextensible string physically constrains about the motion of both ends.',
        yr: 'SS2',
      },
      {
        q: 'Two masses of 8 kg and 2 kg hang over a smooth pulley. Find the acceleration (g=10 m/s²).',
        o: ['6 m/s²', '10 m/s²', '8 m/s²', '4 m/s²'],
        a: 0,
        e: 'a = (8−2)(10)/(8+2) = 60/10 = 6 m/s².',
        h: 'Apply the Atwood machine formula with the larger mass difference.',
        yr: 'SS2',
      },
      {
        q: 'For the 8 kg and 2 kg system, find the tension.',
        o: ['32 N', '80 N', '20 N', '60 N'],
        a: 0,
        e: 'T = m₂(g+a) = 2(10+6) = 32 N. Check: T = m₁(g−a) = 8(10−6) = 32 N. ✓',
        h: "Apply Newton's Second Law to the lighter (accelerating upward) mass.",
        yr: 'SS2',
      },
      {
        q: "Two equal masses of 5 kg each hang over a smooth pulley. Find the system's acceleration.",
        o: ['0 m/s² (system remains in equilibrium)', '10 m/s²', '5 m/s²', '2.5 m/s²'],
        a: 0,
        e: "a = (5−5)(10)/(5+5) = 0/10 = 0. When masses are equal, the system is balanced and doesn't accelerate.",
        h: 'Consider what happens to the formula when m₁ = m₂.',
        yr: 'SS2',
      },
      {
        q: 'Blocks of 3 kg and 7 kg on a smooth horizontal table are connected and pulled by a force of 40 N. Find the acceleration.',
        o: ['4 m/s²', '10 m/s²', '5.71 m/s²', '13.33 m/s²'],
        a: 0,
        e: 'Total mass = 3+7=10kg. a = F/mass = 40/10 = 4 m/s².',
        h: 'Combine the masses for the whole system to find the common acceleration.',
        yr: 'SS2',
      },
      {
        q: 'For blocks of 3 kg (trailing) and 7 kg (leading) pulled with 40 N (a=4 m/s²), find the tension connecting them.',
        o: ['12 N', '28 N', '40 N', '16 N'],
        a: 0,
        e: 'Isolating the trailing 3kg block: T = ma = 3×4 = 12 N.',
        h: "Apply Newton's Second Law to just the trailing block, where tension is the only horizontal force acting.",
        yr: 'SS2',
      },
      {
        q: 'A 5 kg mass on a rough table (μ=0.2) is connected via a pulley to a hanging 3 kg mass. Find the acceleration (g=10 m/s²). [a = (m₂g − μm₁g)/(m₁+m₂)]',
        o: ['2.5 m/s²', '3.75 m/s²', '5 m/s²', '1.25 m/s²'],
        a: 0,
        e: 'Friction on table mass = μm₁g = 0.2×5×10=10N. a = (m₂g−friction)/(m₁+m₂) = (30−10)/8 = 20/8 = 2.5 m/s².',
        h: 'Include friction as an opposing force on the block on the table, reducing the net driving force.',
        yr: 'SS2',
      },
      {
        q: 'In an Atwood machine problem, which mass experiences an upward acceleration?',
        o: [
          'The lighter mass',
          'The heavier mass',
          'Both experience downward acceleration',
          'Neither accelerates',
        ],
        a: 0,
        e: 'The heavier mass falls (accelerates downward) while the lighter mass rises (accelerates upward), connected by the same string over the pulley.',
        h: 'The system accelerates in the direction that lets the heavier mass descend.',
        yr: 'SS2',
      },
      {
        q: 'For two masses m₁ and m₂ (m₁>m₂) over a pulley, which formula gives the tension using the heavier mass?',
        o: ['T = m₁(g − a)', 'T = m₁(g + a)', 'T = m₁g', 'T = m₁a'],
        a: 0,
        e: "For the heavier mass (accelerating downward), Newton's Second Law gives: m₁g − T = m₁a, rearranged to T = m₁(g−a).",
        h: "Consider the heavier mass's equation of motion: weight minus tension equals mass times acceleration.",
        yr: 'SS2',
      },
      {
        q: 'Two masses of 10 kg and 6 kg hang over a smooth pulley. Find the acceleration (g=10 m/s²).',
        o: ['2.5 m/s²', '4 m/s²', '10 m/s²', '1.6 m/s²'],
        a: 0,
        e: 'a = (10−6)(10)/(10+6) = 40/16 = 2.5 m/s².',
        h: 'Apply the standard Atwood machine acceleration formula.',
        yr: 'SS2',
      },
      {
        q: 'For the 10 kg and 6 kg system, find the tension.',
        o: ['75 N', '100 N', '60 N', '40 N'],
        a: 0,
        e: 'T = m₂(g+a) = 6(10+2.5) = 75 N. Check: T = m₁(g−a) = 10(10−2.5) = 75 N. ✓',
        h: "Apply Newton's Second Law to the lighter, upward-accelerating mass.",
        yr: 'SS2',
      },
      {
        q: "Why is the tension calculated from BOTH masses' equations expected to give the SAME value?",
        o: [
          'Because the string has uniform tension throughout its length (massless, inextensible string)',
          'Because the masses are always equal',
          "It's only a coincidence in simple problems",
          'Because gravity acts the same on both',
        ],
        a: 0,
        e: "For an ideal (massless, inextensible) string over a frictionless pulley, tension is the same throughout, so calculating T using either mass's equation of motion must give consistent results — this serves as a good check.",
        h: 'This consistency is a useful way to verify your working is correct in connected-particle problems.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Work, Power and Energy',
    topicCode: 'SS2-FMATH-28',
    module: 'Mechanics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Work</span>, <span class="learn-keyword">power</span>, and <span class="learn-keyword">energy</span> describe how forces transfer and transform capacity to do things. These concepts, alongside the work-energy theorem, let us solve motion problems without needing full kinematics equations.</div>\n\n<h3 class="learn-subheading">1. Work Done</h3>\n<p class="learn-p">W = Fd cos&theta;, where F is the applied force, d is the displacement, and &theta; is the angle between force and displacement direction. If force and motion are in the same direction (&theta;=0&deg;): W = Fd.<br>\n<strong>Example:</strong> A force of 50 N moves an object 10 m in the direction of the force. W = 50 &times; 10 = 500 J.</p>\n\n<h3 class="learn-subheading">2. Kinetic and Potential Energy</h3>\n<p class="learn-p">Kinetic Energy: KE = &frac12;mv&sup2;. Potential Energy (gravitational): PE = mgh.<br>\n<strong>Example:</strong> A 2 kg object moving at 5 m/s: KE = &frac12;(2)(25) = 25 J.</p>\n\n<h3 class="learn-subheading">3. The Work-Energy Theorem</h3>\n<p class="learn-p">The net work done on an object equals its change in kinetic energy: W = &Delta;KE = &frac12;mv&sup2; &minus; &frac12;mu&sup2;.<br>\n<strong>Example:</strong> A 3 kg object speeds up from 2 m/s to 6 m/s. Find the work done.<br>\nW = &frac12;(3)(36) &minus; &frac12;(3)(4) = 54 &minus; 6 = 48 J.</p>\n\n<h3 class="learn-subheading">4. Power</h3>\n<p class="learn-p">Power P = W/t = Work done per unit time (measured in Watts). Also, P = Fv (force &times; velocity, for constant force).<br>\n<strong>Example:</strong> A motor does 600 J of work in 5 seconds. Power = 600/5 = 120 W.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Quantity</th><th>Formula</th><th>Unit</th></tr></thead>\n    <tbody>\n      <tr><td>Work</td><td>W = Fd cos&theta;</td><td>Joules (J)</td></tr>\n      <tr><td>Kinetic Energy</td><td>KE = &frac12;mv&sup2;</td><td>Joules (J)</td></tr>\n      <tr><td>Potential Energy</td><td>PE = mgh</td><td>Joules (J)</td></tr>\n      <tr><td>Power</td><td>P = W/t = Fv</td><td>Watts (W)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Energy is always CONSERVED in a closed system without friction: total mechanical energy (KE + PE) stays constant. This lets you solve many problems without finding acceleration or time at all.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Work = Force &times; distance (along direction of motion). KE=&frac12;mv&sup2;, PE=mgh. The work-energy theorem links work done directly to the change in kinetic energy. Power is the rate of doing work.\n</div>\n',
    questions: [
      {
        q: 'A force of 50 N moves an object 10 m in the direction of the force. Find the work done.',
        o: ['500 J', '5 J', '50 J', '60 J'],
        a: 0,
        e: 'W = Fd = 50×10 = 500 J.',
        h: 'Since force and displacement are in the same direction, simply multiply them.',
        yr: 'SS2',
      },
      {
        q: 'Find the kinetic energy of a 2 kg object moving at 5 m/s.',
        o: ['25 J', '10 J', '50 J', '5 J'],
        a: 0,
        e: 'KE = ½mv² = ½(2)(25) = 25 J.',
        h: 'Apply KE = ½mv², remembering to square the velocity first.',
        yr: 'SS2',
      },
      {
        q: 'Find the potential energy of a 3 kg object raised 4 m above the ground (g=10 m/s²).',
        o: ['120 J', '12 J', '30 J', '40 J'],
        a: 0,
        e: 'PE = mgh = 3×10×4 = 120 J.',
        h: 'Apply PE = mgh directly.',
        yr: 'SS2',
      },
      {
        q: 'A 3 kg object speeds up from 2 m/s to 6 m/s. Find the work done using the work-energy theorem.',
        o: ['48 J', '54 J', '6 J', '60 J'],
        a: 0,
        e: 'W = ½mv² − ½mu² = ½(3)(36) − ½(3)(4) = 54 − 6 = 48 J.',
        h: 'Find the final and initial kinetic energies separately, then subtract.',
        yr: 'SS2',
      },
      {
        q: 'A motor does 600 J of work in 5 seconds. Find its power.',
        o: ['120 W', '3000 W', '605 W', '0.0083 W'],
        a: 0,
        e: 'P = W/t = 600/5 = 120 W.',
        h: 'Apply P = W/t, dividing total work by the time taken.',
        yr: 'SS2',
      },
      {
        q: 'A force of 100 N moves an object at a constant velocity of 3 m/s. Find the power.',
        o: ['300 W', '33.3 W', '103 W', '97 W'],
        a: 0,
        e: 'P = Fv = 100×3 = 300 W.',
        h: 'For constant force and velocity, apply P = Fv directly.',
        yr: 'SS2',
      },
      {
        q: 'A force of 20 N moves an object 5 m, but the force is applied at 60° to the direction of motion. Find the work done.',
        o: ['50 J', '100 J', '86.6 J', '20 J'],
        a: 0,
        e: 'W = Fd cosθ = 20×5×cos60° = 100×0.5 = 50 J.',
        h: 'Include the cosine of the angle between force and displacement direction.',
        yr: 'SS2',
      },
      {
        q: 'A 5 kg ball falls from a height of 10 m. Find its kinetic energy just before hitting the ground (assume no air resistance, g=10 m/s², using energy conservation: KE gained = PE lost).',
        o: ['500 J', '50 J', '100 J', '250 J'],
        a: 0,
        e: 'By energy conservation, KE gained = PE lost = mgh = 5×10×10 = 500 J.',
        h: 'Use conservation of energy: all potential energy converts to kinetic energy as the ball falls.',
        yr: 'SS2',
      },
      {
        q: 'For the falling ball in the previous question, find its speed just before impact (using KE=½mv²=500J).',
        o: ['√200 ≈ 14.14 m/s', '10 m/s', '20 m/s', '100 m/s'],
        a: 0,
        e: '½(5)v²=500 ⇒ v²=200 ⇒ v=√200≈14.14 m/s.',
        h: 'Rearrange KE = ½mv² to solve for v, using the kinetic energy found in the previous question.',
        yr: 'SS2',
      },
      {
        q: 'A crane lifts a 200 kg load a height of 15 m in 10 seconds. Find the power required (g=10 m/s²).',
        o: ['3000 W', '300 W', '30000 W', '200 W'],
        a: 0,
        e: 'Work = mgh = 200×10×15 = 30000 J. Power = W/t = 30000/10 = 3000 W.',
        h: 'First find the work done against gravity, then divide by the time taken.',
        yr: 'SS2',
      },
      {
        q: 'A 4 kg object at rest is acted on by a force doing 32 J of work. Find its final velocity.',
        o: ['4 m/s', '8 m/s', '16 m/s', '2 m/s'],
        a: 0,
        e: 'W=½mv²−0 (starts at rest) ⇒ 32=½(4)v² ⇒ v²=16 ⇒ v=4 m/s.',
        h: 'Since the object starts at rest, all the work done converts directly to kinetic energy.',
        yr: 'SS2',
      },
      {
        q: 'Find the work done in lifting a 10 kg object a height of 2 m at constant velocity (g=10 m/s²).',
        o: ['200 J', '20 J', '100 J', '2 J'],
        a: 0,
        e: 'W = mgh = 10×10×2 = 200 J (work done against gravity equals the gain in potential energy).',
        h: 'Lifting at constant velocity means the work done equals the gain in gravitational PE.',
        yr: 'SS2',
      },
      {
        q: 'A car engine delivers 60000 W of power at a constant speed of 20 m/s. Find the driving force.',
        o: ['3000 N', '1200000 N', '3 N', '80 N'],
        a: 0,
        e: 'P=Fv ⇒ 60000=F(20) ⇒ F=3000 N.',
        h: 'Rearrange P=Fv to solve for the force.',
        yr: 'SS2',
      },
      {
        q: 'A 6 kg object moving at 4 m/s is brought to rest by a resistive force. Find the work done by this resistive force.',
        o: ['−48 J', '48 J', '24 J', '−24 J'],
        a: 0,
        e: 'W = ½m(0)² − ½m(4)² = 0 − ½(6)(16) = −48 J. The negative sign shows the force removes kinetic energy.',
        h: 'Work done to REDUCE kinetic energy is negative, since the object slows down.',
        yr: 'SS2',
      },
      {
        q: 'A pump raises 500 kg of water through 8 m every 20 seconds. Find its power output (g=10 m/s²).',
        o: ['2000 W', '200 W', '40000 W', '4000 W'],
        a: 0,
        e: 'Work per cycle = mgh = 500×10×8 = 40000 J. Power = 40000/20 = 2000 W.',
        h: 'Find the work done raising the water first, then divide by the time taken.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is the correct SI unit for power?',
        o: ['Watt (W)', 'Joule (J)', 'Newton (N)', 'Newton-metre (Nm)'],
        a: 0,
        e: 'Power is measured in Watts (W), which is defined as Joules per second (J/s).',
        h: 'Recall that power measures the RATE of doing work, giving it a distinct unit from work itself.',
        yr: 'SS2',
      },
      {
        q: 'An object of mass 2 kg falls freely from rest through a height of 5 m. Find its velocity on landing using energy methods (g=10 m/s²).',
        o: ['10 m/s', '100 m/s', '50 m/s', '5 m/s'],
        a: 0,
        e: 'PE lost = KE gained: mgh = ½mv² ⇒ v² = 2gh = 2(10)(5) = 100 ⇒ v=10 m/s.',
        h: 'Use v² = 2gh, derived from setting PE lost equal to KE gained (the mass cancels out).',
        yr: 'SS2',
      },
      {
        q: 'Which statement correctly describes the principle of conservation of mechanical energy (no friction)?',
        o: [
          'The sum of kinetic and potential energy remains constant',
          'Kinetic energy alone remains constant',
          'Potential energy alone remains constant',
          'Energy is always lost to heat',
        ],
        a: 0,
        e: 'In the absence of friction or other resistive forces, total mechanical energy (KE + PE) is conserved — it can convert between forms but the total stays the same.',
        h: 'Consider the fundamental law of energy conservation for a closed system with no friction.',
        yr: 'SS2',
      },
      {
        q: 'A 1000 kg lift rises 20 m in 25 seconds at constant velocity. Find the power required (g=10 m/s²).',
        o: ['8000 W', '800 W', '200000 W', '500 W'],
        a: 0,
        e: 'Work = mgh = 1000×10×20 = 200000 J. Power = 200000/25 = 8000 W.',
        h: 'Find the work against gravity first, then divide by the time.',
        yr: 'SS2',
      },
      {
        q: 'Find the work done by a force of 15 N applied at 90° to the direction of motion.',
        o: ['0 J (no work is done)', '15 J', 'distance-dependent value', 'Infinite'],
        a: 0,
        e: 'W = Fd cos90° = Fd(0) = 0 J. A force perpendicular to motion does no work at all.',
        h: 'Recall that cos 90° = 0, meaning a perpendicular force contributes nothing to the work done.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Projectiles',
    topicCode: 'SS2-FMATH-29',
    module: 'Mechanics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Projectile motion</span> combines constant horizontal velocity with constant vertical acceleration due to gravity, creating the characteristic parabolic path of a thrown or launched object. We analyze the horizontal and vertical components SEPARATELY.</div>\n\n<h3 class="learn-subheading">1. Key Assumptions</h3>\n<p class="learn-p">No air resistance. Horizontal velocity is CONSTANT throughout the flight. Vertical motion has constant acceleration g (downward), so vertical velocity changes uniformly.</p>\n\n<h3 class="learn-subheading">2. Resolving Initial Velocity</h3>\n<p class="learn-p">For initial speed U at angle &theta; above horizontal:<br>\nHorizontal component: U<sub>x</sub> = U cos&theta;. Vertical component: U<sub>y</sub> = U sin&theta;.</p>\n\n<h3 class="learn-subheading">3. Key Formulas</h3>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Quantity</th><th>Formula</th></tr></thead>\n    <tbody>\n      <tr><td>Time to reach maximum height</td><td>t = U sin&theta; / g</td></tr>\n      <tr><td>Maximum height</td><td>H = (U sin&theta;)&sup2; / 2g</td></tr>\n      <tr><td>Time of flight (returns to launch height)</td><td>T = 2U sin&theta; / g</td></tr>\n      <tr><td>Range (horizontal distance)</td><td>R = U&sup2; sin(2&theta;) / g</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">4. Worked Example</h3>\n<p class="learn-p">A ball is projected at 20 m/s at 30&deg; above horizontal (g=10 m/s&sup2;). Find the maximum height and range.<br>\nU<sub>y</sub>=20sin30&deg;=10. H = 10&sup2;/(2&times;10) = 100/20 = 5 m.<br>\nR = 20&sup2;&times;sin60&deg;/10 = 400&times;0.866/10 = 34.64 m.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Range is MAXIMIZED when &theta;=45&deg; (since sin90&deg;=1 is the maximum possible value of sin2&theta;). Also remember: at the moment of maximum height, vertical velocity is exactly ZERO (though horizontal velocity is unchanged).\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Split initial velocity into horizontal (Ucos&theta;, constant) and vertical (Usin&theta;, decelerating under gravity) components. Use standard formulas for time of flight, maximum height, and range &mdash; each derived from basic equations of motion applied separately to each direction.\n</div>\n',
    questions: [
      {
        q: 'A ball is projected at 20 m/s at 30° above horizontal. Find the horizontal component of velocity.',
        o: ['17.32 m/s', '10 m/s', '20 m/s', '5.77 m/s'],
        a: 0,
        e: 'Uₓ = U cosθ = 20 cos30° = 20×0.866 = 17.32 m/s.',
        h: 'Use U cosθ for the horizontal component.',
        yr: 'SS2',
      },
      {
        q: 'For the same ball (20 m/s at 30°), find the vertical component of velocity.',
        o: ['10 m/s', '17.32 m/s', '20 m/s', '5 m/s'],
        a: 0,
        e: 'Uᵧ = U sinθ = 20 sin30° = 20×0.5 = 10 m/s.',
        h: 'Use U sinθ for the vertical component.',
        yr: 'SS2',
      },
      {
        q: 'Find the maximum height reached by the ball (Uy=10 m/s, g=10 m/s²).',
        o: ['5 m', '10 m', '20 m', '1 m'],
        a: 0,
        e: 'H = (U sinθ)²/2g = 10²/(2×10) = 100/20 = 5 m.',
        h: 'Apply H = (U sinθ)² / 2g using the vertical velocity component found earlier.',
        yr: 'SS2',
      },
      {
        q: 'Find the time taken for the ball to reach maximum height (Uy=10, g=10 m/s²).',
        o: ['1 s', '2 s', '0.5 s', '10 s'],
        a: 0,
        e: 't = U sinθ/g = 10/10 = 1 s.',
        h: 'Apply t = U sinθ / g.',
        yr: 'SS2',
      },
      {
        q: 'Find the total time of flight for the ball (returning to launch height).',
        o: ['2 s', '1 s', '4 s', '0.5 s'],
        a: 0,
        e: 'T = 2U sinθ/g = 2(10)/10 = 2 s (double the time to reach maximum height).',
        h: 'Total time of flight is twice the time to reach maximum height (by symmetry).',
        yr: 'SS2',
      },
      {
        q: 'Find the horizontal range of the ball (U=20 m/s, θ=30°, g=10 m/s², sin60°≈0.866).',
        o: ['34.64 m', '20 m', '17.32 m', '10 m'],
        a: 0,
        e: 'R = U²sin(2θ)/g = 400×sin60°/10 = 400×0.866/10 = 34.64 m.',
        h: 'Apply R = U²sin(2θ)/g, remembering 2θ = 60° here.',
        yr: 'SS2',
      },
      {
        q: 'At what angle of projection is the horizontal range maximized (for fixed initial speed)?',
        o: ['45°', '90°', '30°', '60°'],
        a: 0,
        e: 'Range is maximized when sin(2θ)=1, i.e., 2θ=90°, so θ=45°.',
        h: 'Range depends on sin(2θ), which reaches its maximum value of 1 at 2θ=90°.',
        yr: 'SS2',
      },
      {
        q: 'What is the vertical velocity of a projectile at the exact moment it reaches maximum height?',
        o: [
          '0 (zero)',
          'Equal to the horizontal velocity',
          'Equal to the initial vertical velocity',
          'Maximum value',
        ],
        a: 0,
        e: 'At maximum height, the projectile momentarily stops moving vertically (vertical velocity = 0) before falling back down, though horizontal velocity continues unchanged.',
        h: "Consider what 'maximum height' physically means for vertical motion.",
        yr: 'SS2',
      },
      {
        q: 'A stone is thrown horizontally (θ=0°) at 15 m/s from a height. What is its initial vertical velocity component?',
        o: ['0 m/s', '15 m/s', '7.5 m/s', 'Undefined'],
        a: 0,
        e: "Uᵧ = U sin0° = 15×0 = 0 m/s. When thrown purely horizontally, there's no initial vertical component.",
        h: 'Recall that sin(0°) = 0.',
        yr: 'SS2',
      },
      {
        q: 'A projectile is launched at 30 m/s at 60°. Find its maximum height (g=10 m/s², sin60°≈0.866).',
        o: ['33.75 m', '45 m', '15 m', '90 m'],
        a: 0,
        e: 'Uᵧ=30sin60°=25.98. H=(25.98)²/(2×10)=675/20≈33.75 m.',
        h: 'Find the vertical component first, then apply the maximum height formula.',
        yr: 'SS2',
      },
      {
        q: 'A projectile is launched at 30 m/s at 60°. Find the time of flight (g=10 m/s², using Uy≈25.98).',
        o: ['5.2 s', '2.6 s', '3 s', '10.4 s'],
        a: 0,
        e: 'T = 2U sinθ/g = 2(25.98)/10 ≈ 5.2 s.',
        h: 'Apply T = 2U sinθ / g using the vertical component found.',
        yr: 'SS2',
      },
      {
        q: 'During the flight of a projectile (no air resistance), what happens to the horizontal velocity?',
        o: [
          'It remains constant throughout the flight',
          'It decreases due to gravity',
          'It increases due to gravity',
          'It becomes zero at maximum height',
        ],
        a: 0,
        e: "Since gravity acts only vertically (downward), there's no horizontal force to change the horizontal velocity component — it stays constant throughout.",
        h: 'Gravity only affects the vertical direction of motion, not the horizontal.',
        yr: 'SS2',
      },
      {
        q: 'A ball is projected at 25 m/s at 40° above horizontal. Find its horizontal component (cos40°≈0.766).',
        o: ['19.15 m/s', '16.07 m/s', '25 m/s', '9.65 m/s'],
        a: 0,
        e: 'Uₓ = 25cos40° ≈ 25×0.766 ≈ 19.15 m/s.',
        h: 'Multiply the initial speed by cos of the launch angle.',
        yr: 'SS2',
      },
      {
        q: 'Two projectiles are launched at angles 30° and 60° with the same initial speed. How do their ranges compare?',
        o: [
          'They are equal, since sin(60°)=sin(120°)',
          'The 60° angle gives a larger range',
          'The 30° angle gives a larger range',
          'Cannot be compared',
        ],
        a: 0,
        e: 'R depends on sin(2θ). For 30°: sin(60°)≈0.866. For 60°: sin(120°)≈0.866 (same value, since sin(180°−x)=sin(x)). So the ranges are equal!',
        h: 'Complementary angles (summing to 90°) give the same horizontal range for a given launch speed.',
        yr: 'SS2',
      },
      {
        q: 'A projectile has a time of flight of 4 seconds. Find its initial vertical velocity component (g=10 m/s²).',
        o: ['20 m/s', '40 m/s', '4 m/s', '10 m/s'],
        a: 0,
        e: 'T=2U sinθ/g ⇒ 4=2Uᵧ/10 ⇒ Uᵧ=20 m/s.',
        h: 'Rearrange T = 2U sinθ / g to solve for the vertical velocity component.',
        yr: 'SS2',
      },
      {
        q: "A projectile's range is 100 m when launched at 45°. Estimate the range at 30° with the same speed (sin60°≈sin90°×0.866).",
        o: ['86.6 m', '100 m', '50 m', '115.5 m'],
        a: 0,
        e: 'R∝sin(2θ). At 45°: sin(90°)=1, giving R=100m, so U²/g=100. At 30°: sin(60°)≈0.866, so R=100×0.866=86.6 m.',
        h: 'Use the ratio of sin(2θ) values between the two angles to scale the known range.',
        yr: 'SS2',
      },
      {
        q: 'A ball thrown horizontally from a height falls for 3 seconds before landing. Find the height of the fall (g=10 m/s², using h=½gt² since initial vertical velocity is 0).',
        o: ['45 m', '30 m', '90 m', '15 m'],
        a: 0,
        e: 'h = ½gt² = ½(10)(9) = 45 m.',
        h: "Since there's no initial vertical velocity for a horizontal throw, use h=½gt² directly.",
        yr: 'SS2',
      },
      {
        q: "Which pair of quantities remains constant throughout a projectile's flight (assuming no air resistance)?",
        o: [
          'Horizontal velocity and the acceleration due to gravity',
          'Vertical velocity and horizontal velocity',
          'Total speed and height',
          'Range and maximum height',
        ],
        a: 0,
        e: 'Horizontal velocity stays constant (no horizontal force), and the vertical acceleration g is constant throughout — these two never change during flight.',
        h: 'Consider which quantities are unaffected by the changing vertical velocity.',
        yr: 'SS2',
      },
      {
        q: 'A projectile is launched at 10 m/s vertically upward (θ=90°). Find the maximum height (g=10 m/s²).',
        o: ['5 m', '10 m', '1 m', '100 m'],
        a: 0,
        e: 'H = (Usin90°)²/2g = 10²/20 = 5 m (this is just vertical motion, since horizontal component is zero).',
        h: 'For a purely vertical launch, use the standard height formula with sin90°=1.',
        yr: 'SS2',
      },
      {
        q: 'For the previous vertically-launched projectile, what is its range (horizontal distance travelled)?',
        o: ["0 m, since there's no horizontal component of velocity", '5 m', '10 m', 'Undefined'],
        a: 0,
        e: 'Since θ=90°, horizontal component Uₓ=Ucos90°=0, so the projectile goes straight up and down with zero horizontal range.',
        h: 'Consider the horizontal velocity component when the launch angle is exactly 90°.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Binomial Expansion',
    topicCode: 'SS2-FMATH-30',
    module: 'Algebra',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  The <span class="learn-keyword">Binomial Theorem</span> gives a fast way to expand expressions like (a+b)&#8319; without multiplying it out term by term, using combinations to generate the coefficients.</div>\n\n<h3 class="learn-subheading">1. The Binomial Theorem</h3>\n<p class="learn-p">(a+b)&#8319; = &#8319;C&#8320;a&#8319;b&#8304; + &#8319;C&#8321;a&#8319;&#8315;&sup1;b&sup1; + &#8319;C&#8322;a&#8319;&#8315;&sup2;b&sup2; + ... + &#8319;C&#8319;a&#8304;b&#8319;<br>\nThe general term: T<sub>r+1</sub> = &#8319;C&#8348;a&#8319;&#8315;&#8348;b&#8348;.</p>\n\n<h3 class="learn-subheading">2. Pascal\'s Triangle</h3>\n<p class="learn-p">The coefficients of (a+b)&#8319; can also be read from row n of Pascal\'s Triangle:</p>\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>n</th><th>Coefficients</th></tr></thead>\n    <tbody>\n      <tr><td>0</td><td>1</td></tr>\n      <tr><td>1</td><td>1, 1</td></tr>\n      <tr><td>2</td><td>1, 2, 1</td></tr>\n      <tr><td>3</td><td>1, 3, 3, 1</td></tr>\n      <tr><td>4</td><td>1, 4, 6, 4, 1</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<h3 class="learn-subheading">3. Worked Example</h3>\n<p class="learn-p">Expand (x+2)&#8308;.<br>\n= &#8308;C&#8320;x&#8308; + &#8308;C&#8321;x&sup3;(2) + &#8308;C&#8322;x&sup2;(4) + &#8308;C&#8323;x(8) + &#8308;C&#8308;(16)<br>\n= x&#8308; + 4(2)x&sup3; + 6(4)x&sup2; + 4(8)x + 16<br>\n= x&#8308; + 8x&sup3; + 24x&sup2; + 32x + 16.</p>\n\n<h3 class="learn-subheading">4. Finding a Specific Term</h3>\n<p class="learn-p"><strong>Example:</strong> Find the term in x&sup3; in the expansion of (x+3)&#8308;&#8309;&#8320;.<br>\nGeneral term: T<sub>r+1</sub> = &#8319;C&#8348;x&#8319;&#8315;&#8348;3&#8348;. We need n&minus;r=3.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> The powers of a and b always add up to n in every term, and the coefficients &#8319;C&#8348; are symmetric (matching Pascal\'s Triangle). Double-check your expansion has exactly n+1 terms.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> (a+b)&#8319; expands into n+1 terms using binomial coefficients &#8319;C&#8348;. Powers of a decrease from n to 0 while powers of b increase from 0 to n. Pascal\'s Triangle offers a quick way to find small coefficients.\n</div>\n',
    questions: [
      {
        q: 'Expand (x + 1)³ using the binomial theorem.',
        o: ['x³ + 3x² + 3x + 1', 'x³ + 3x + 1', 'x³ + x² + x + 1', 'x³ + 3x² + 1'],
        a: 0,
        e: "Using Pascal's row for n=3 (1,3,3,1): (x+1)³ = x³ + 3x²(1) + 3x(1) + 1 = x³+3x²+3x+1.",
        h: "Use Pascal's Triangle row for n=3: coefficients 1, 3, 3, 1.",
        yr: 'SS2',
      },
      {
        q: 'Expand (x + 2)⁴ using the binomial theorem.',
        o: [
          'x⁴ + 8x³ + 24x² + 32x + 16',
          'x⁴ + 2x³ + 4x² + 8x + 16',
          'x⁴ + 8x³ + 12x² + 8x + 16',
          'x⁴ + 4x³ + 6x² + 4x + 2',
        ],
        a: 0,
        e: "Using Pascal's row for n=4 (1,4,6,4,1): x⁴+4x³(2)+6x²(4)+4x(8)+16 = x⁴+8x³+24x²+32x+16.",
        h: "Use Pascal's row (1,4,6,4,1), multiplying each term by increasing powers of 2.",
        yr: 'SS2',
      },
      {
        q: 'Find the value of ⁵C₂ (a binomial coefficient).',
        o: ['10', '20', '5', '15'],
        a: 0,
        e: '⁵C₂ = 5!/(2!3!) = (5×4)/2 = 10.',
        h: 'Apply the combination formula for binomial coefficients.',
        yr: 'SS2',
      },
      {
        q: 'In the expansion of (a+b)⁶, how many terms are there in total?',
        o: ['7', '6', '5', '8'],
        a: 0,
        e: '(a+b)ⁿ always has n+1 terms, so (a+b)⁶ has 6+1=7 terms.',
        h: 'The number of terms in a binomial expansion is always one more than the power.',
        yr: 'SS2',
      },
      {
        q: 'Find the coefficient of x² in the expansion of (x+3)⁴.',
        o: ['54', '12', '36', '6'],
        a: 0,
        e: 'The x² term is ⁴C₂x²(3)² = 6×x²×9 = 54x². Coefficient = 54.',
        h: 'Find the term with x², using ⁴C₂ and the appropriate power of 3.',
        yr: 'SS2',
      },
      {
        q: "Write down row n=5 of Pascal's Triangle.",
        o: ['1, 5, 10, 10, 5, 1', '1, 5, 10, 5, 1', '1, 4, 6, 4, 1', '1, 5, 15, 5, 1'],
        a: 0,
        e: "Row 5 of Pascal's Triangle is 1, 5, 10, 10, 5, 1 (symmetric, 6 entries for n=5).",
        h: "Each row of Pascal's Triangle has n+1 entries, formed by adding adjacent entries from the row above.",
        yr: 'SS2',
      },
      {
        q: 'Expand (x − 1)³ using the binomial theorem (note alternating signs).',
        o: ['x³ − 3x² + 3x − 1', 'x³ + 3x² + 3x + 1', 'x³ − 3x² − 3x − 1', 'x³ − x² + x − 1'],
        a: 0,
        e: 'Using (a−b)³ pattern (or substituting b=−1 into the general expansion): x³−3x²+3x−1.',
        h: 'When b is negative, the signs of the terms alternate: +,−,+,−...',
        yr: 'SS2',
      },
      {
        q: 'Find the general term T(r+1) in the expansion of (x+y)ⁿ.',
        o: ['ⁿCᵣ xⁿ⁻ʳ yʳ', 'ⁿCᵣ xʳ yⁿ⁻ʳ', 'ⁿCᵣ (xy)ʳ', 'ⁿCᵣ xⁿ yⁿ'],
        a: 0,
        e: 'The general term formula is T(r+1) = ⁿCᵣ xⁿ⁻ʳ yʳ, with the power of x decreasing and y increasing as r increases.',
        h: 'Recall the standard general term formula for a binomial expansion.',
        yr: 'SS2',
      },
      {
        q: 'Find the constant term in the expansion of (x + 2)³.',
        o: ['8', '6', '2', '3'],
        a: 0,
        e: 'The constant term occurs when x has power 0: ³C₃x⁰(2)³ = 1×1×8 = 8.',
        h: 'The constant term appears in the last term of the expansion, where x has power zero.',
        yr: 'SS2',
      },
      {
        q: 'Find the coefficient of x³ in the expansion of (2x + 1)⁵.',
        o: ['80', '40', '10', '20'],
        a: 0,
        e: 'Term with x³: ⁵C₂(2x)³(1)² = 10×8x³×1 = 80x³. Coefficient = 80.',
        h: 'Identify which term has x³ (here, r=2 since the power of 2x is 3=5−r→r=2), then compute its coefficient.',
        yr: 'SS2',
      },
      {
        q: 'In the expansion of (a+b)⁷, what is the coefficient of the term a⁴b³?',
        o: ['⁷C₃ = 35', '⁷C₄ = 35', '7', '21'],
        a: 0,
        e: 'Since a⁴b³ means r=3 (power of b), the coefficient is ⁷C₃ = 35. (Note ⁷C₃ = ⁷C₄ by symmetry, so both give the same numeric value.)',
        h: 'Match the power of b to r in the general term formula ⁿCᵣaⁿ⁻ʳbʳ.',
        yr: 'SS2',
      },
      {
        q: 'Simplify ⁶C₁.',
        o: ['6', '1', '36', '15'],
        a: 0,
        e: '⁶C₁ = 6!/(1!5!) = 6.',
        h: 'Choosing 1 item from n always gives exactly n.',
        yr: 'SS2',
      },
      {
        q: 'Expand (1 + x)² using the binomial theorem.',
        o: ['1 + 2x + x²', '1 + x + x²', '1 + 2x', '2 + 2x + x²'],
        a: 0,
        e: "Using Pascal's row for n=2 (1,2,1): 1 + 2x + x².",
        h: 'Apply the standard n=2 expansion (perfect square).',
        yr: 'SS2',
      },
      {
        q: 'Find the sum of ALL binomial coefficients in the expansion of (a+b)⁵ (i.e., ⁵C₀+⁵C₁+...+⁵C₅).',
        o: ['32', '5', '10', '25'],
        a: 0,
        e: 'The sum of all binomial coefficients ⁿCᵣ for r=0 to n always equals 2ⁿ. Here 2⁵=32.',
        h: 'The sum of a full row of binomial coefficients equals 2 to the power of that row number.',
        yr: 'SS2',
      },
      {
        q: 'Find the term containing x² in the expansion of (2 + x)⁵.',
        o: ['80x²', '40x²', '10x²', '20x²'],
        a: 0,
        e: 'Term: ⁵C₂(2)³(x)² = 10×8×x² = 80x².',
        h: 'Identify r=2 (power of x), then compute ⁵C₂ times the appropriate power of 2.',
        yr: 'SS2',
      },
      {
        q: 'Expand (x + y)¹ (the simplest binomial expansion).',
        o: ['x + y', 'x¹y¹', 'xy', 'x − y'],
        a: 0,
        e: 'For n=1, (x+y)¹ is simply x+y itself — no actual expansion needed.',
        h: 'The power of 1 means no real expansion is required.',
        yr: 'SS2',
      },
      {
        q: "Which row of Pascal's Triangle gives the coefficients for (a+b)⁶?",
        o: ['1, 6, 15, 20, 15, 6, 1', '1, 5, 10, 10, 5, 1', '1, 6, 12, 6, 1', '1, 6, 20, 6, 1'],
        a: 0,
        e: "Row 6 of Pascal's Triangle is 1, 6, 15, 20, 15, 6, 1 (symmetric, 7 entries).",
        h: 'Build row 6 by adding adjacent pairs from row 5 (1,5,10,10,5,1).',
        yr: 'SS2',
      },
      {
        q: 'Find the coefficient of x in the expansion of (x + 4)³.',
        o: ['48', '12', '16', '64'],
        a: 0,
        e: 'Term with x¹: ³C₂ x(4)² = 3×x×16 = 48x. Coefficient = 48.',
        h: 'Identify which term in the expansion contains exactly x to the power 1.',
        yr: 'SS2',
      },
      {
        q: 'For (a+b)ⁿ, which binomial coefficient is always equal to 1?',
        o: [
          'ⁿC₀ and ⁿCₙ (the first and last coefficients)',
          'ⁿC₁ only',
          'The middle coefficient only',
          'None, they vary',
        ],
        a: 0,
        e: 'The first term ⁿC₀ and the last term ⁿCₙ are always equal to 1, corresponding to aⁿ and bⁿ respectively.',
        h: 'Recall that choosing 0 items or all n items from n can each only be done in exactly 1 way.',
        yr: 'SS2',
      },
      {
        q: 'Expand (3 + x)².',
        o: ['9 + 6x + x²', '3 + 6x + x²', '9 + 3x + x²', '9 + 9x + x²'],
        a: 0,
        e: 'Using (a+b)²=a²+2ab+b² with a=3, b=x: 9 + 2(3)(x) + x² = 9+6x+x².',
        h: 'Apply the basic (a+b)² identity with a=3 and b=x.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Vector Geometry in Mechanics',
    topicCode: 'SS2-FMATH-31',
    module: 'Mechanics',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Vectors</span> describe quantities with both magnitude AND direction &mdash; essential for representing forces, velocities, and displacements in mechanics. We use column vectors and i-j notation to combine and resolve these quantities.</div>\n\n<h3 class="learn-subheading">1. Vector Notation</h3>\n<p class="learn-p">A vector can be written as a column (x, y) or using unit vectors: v = xi + yj, where i and j are unit vectors along the x and y axes.<br>\n<strong>Example:</strong> A force of 3i + 4j (N) means 3 N in the x-direction and 4 N in the y-direction.</p>\n\n<h3 class="learn-subheading">2. Magnitude of a Vector</h3>\n<p class="learn-p">|v| = &radic;(x&sup2; + y&sup2;) for v = xi + yj.<br>\n<strong>Example:</strong> |3i + 4j| = &radic;(9+16) = &radic;25 = 5.</p>\n\n<h3 class="learn-subheading">3. Adding Vectors (Resultant Force)</h3>\n<p class="learn-p">Add corresponding components. <strong>Example:</strong> Forces F&#8321;=(2i+3j)N and F&#8322;=(5i&minus;j)N act on an object. Find the resultant.<br>\nF<sub>resultant</sub> = (2+5)i + (3&minus;1)j = 7i + 2j (N).</p>\n\n<h3 class="learn-subheading">4. Velocity and Displacement Vectors</h3>\n<p class="learn-p">Velocity v = displacement/time (as a vector). <strong>Example:</strong> An object moves from position (1,2) to (5,7) in 4 seconds. Displacement = (4,5). Velocity = (4/4, 5/4) = (1, 1.25) m/s.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Operation</th><th>Rule</th></tr></thead>\n    <tbody>\n      <tr><td>Addition</td><td>Add corresponding i and j components</td></tr>\n      <tr><td>Magnitude</td><td>|v| = &radic;(x&sup2;+y&sup2;)</td></tr>\n      <tr><td>Direction (angle)</td><td>&theta; = tan&#8315;&sup1;(y/x)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always add vectors component-by-component (i\'s with i\'s, j\'s with j\'s) &mdash; never combine the magnitudes directly unless the vectors point in exactly the same or opposite directions.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Vectors combine magnitude and direction, written as xi+yj. Magnitude uses Pythagoras: |v|=&radic;(x&sup2;+y&sup2;). Resultant forces or displacements are found by adding vectors component-wise.\n</div>\n',
    questions: [
      {
        q: 'Find the magnitude of the vector v = 3i + 4j.',
        o: ['5', '7', '12', '25'],
        a: 0,
        e: '|v| = √(3²+4²) = √(9+16) = √25 = 5.',
        h: 'Apply |v| = √(x²+y²) using the i and j components.',
        yr: 'SS2',
      },
      {
        q: 'Find the resultant of forces F₁=(2i+3j)N and F₂=(5i−j)N.',
        o: ['7i + 2j', '7i + 4j', '3i + 2j', '10i − 3j'],
        a: 0,
        e: 'Add corresponding components: (2+5)i + (3+(−1))j = 7i + 2j.',
        h: 'Add the i-components together and the j-components together separately.',
        yr: 'SS2',
      },
      {
        q: 'Find the magnitude of the vector v = 6i + 8j.',
        o: ['10', '14', '48', '100'],
        a: 0,
        e: '|v| = √(36+64) = √100 = 10.',
        h: 'This is a scaled version of the classic 3-4-5 triangle.',
        yr: 'SS2',
      },
      {
        q: 'Object moves from position (1,2) to (5,7) in 4 seconds. Find the displacement vector.',
        o: ['4i + 5j', '6i + 9j', '5i + 7j', '1i + 2j'],
        a: 0,
        e: 'Displacement = final − initial = (5−1)i + (7−2)j = 4i + 5j.',
        h: 'Subtract the initial position vector from the final position vector.',
        yr: 'SS2',
      },
      {
        q: 'Using the displacement 4i+5j over 4 seconds, find the velocity vector.',
        o: ['i + 1.25j', '4i + 5j', '1.25i + j', '16i + 20j'],
        a: 0,
        e: 'v = displacement/time = (4i+5j)/4 = i + 1.25j.',
        h: 'Divide each component of the displacement vector by the time taken.',
        yr: 'SS2',
      },
      {
        q: 'Find the sum of vectors a = 2i − 3j and b = −i + 5j.',
        o: ['i + 2j', '3i − 8j', 'i − 2j', '3i + 2j'],
        a: 0,
        e: '(2+(−1))i + (−3+5)j = i + 2j.',
        h: 'Add the i-components and j-components of each vector separately.',
        yr: 'SS2',
      },
      {
        q: 'Find the magnitude of vector p = 5i + 12j.',
        o: ['13', '17', '7', '60'],
        a: 0,
        e: '|p| = √(25+144) = √169 = 13.',
        h: 'This is the classic 5-12-13 Pythagorean triple.',
        yr: 'SS2',
      },
      {
        q: 'Two forces act on an object: F₁ = 4i N and F₂ = 3j N. Find the magnitude of the resultant force.',
        o: ['5 N', '7 N', '12 N', '1 N'],
        a: 0,
        e: 'Resultant = 4i+3j. |Resultant| = √(16+9) = √25 = 5 N.',
        h: 'Add the two force vectors first, then find the magnitude of the sum.',
        yr: 'SS2',
      },
      {
        q: 'Find 2a − b, where a = 3i + j and b = i − 2j.',
        o: ['5i + 4j', '7i − j', '5i − 4j', '6i + 4j'],
        a: 0,
        e: '2a = 6i+2j. 2a−b = (6−1)i + (2−(−2))j = 5i+4j.',
        h: 'Scale vector a by 2 first, then subtract vector b component-wise.',
        yr: 'SS2',
      },
      {
        q: 'A particle has position vector r = 2ti + 3t²j (t in seconds). Find its position at t=2.',
        o: ['4i + 12j', '2i + 3j', '4i + 6j', '8i + 12j'],
        a: 0,
        e: 'At t=2: r = 2(2)i + 3(4)j = 4i + 12j.',
        h: 'Substitute t=2 into both the i and j components of the position vector.',
        yr: 'SS2',
      },
      {
        q: 'Find the angle that the vector v = i + j makes with the positive x-axis.',
        o: ['45°', '0°', '90°', '30°'],
        a: 0,
        e: 'θ = tan⁻¹(y/x) = tan⁻¹(1/1) = tan⁻¹(1) = 45°.',
        h: 'Apply θ = tan⁻¹(y/x) using the components of the vector.',
        yr: 'SS2',
      },
      {
        q: 'Find the unit vector in the direction of v = 3i + 4j.',
        o: ['0.6i + 0.8j', '3i + 4j', '0.5i + 0.5j', '1i + 1j'],
        a: 0,
        e: 'Unit vector = v/|v| = (3i+4j)/5 = 0.6i + 0.8j.',
        h: 'Divide each component of the vector by its magnitude.',
        yr: 'SS2',
      },
      {
        q: 'Three forces act on a particle: F₁=2i+j, F₂=−i+3j, F₃=i−2j. Find the resultant.',
        o: ['2i + 2j', '2i + 4j', '4i + 2j', '0i + 2j'],
        a: 0,
        e: 'Sum i-components: 2+(−1)+1=2. Sum j-components: 1+3+(−2)=2. Resultant = 2i+2j.',
        h: "Add all three vectors' i-components together, then all three j-components together.",
        yr: 'SS2',
      },
      {
        q: 'For the resultant 2i + 2j found above, find its magnitude.',
        o: ['2√2 (≈ 2.83)', '4', '2', '8'],
        a: 0,
        e: '|2i+2j| = √(4+4) = √8 = 2√2 ≈ 2.83.',
        h: 'Apply the magnitude formula to the resultant vector found in the previous question.',
        yr: 'SS2',
      },
      {
        q: "A boat's velocity relative to water is 5i m/s, and the water current is 2j m/s. Find the boat's resultant velocity relative to the ground.",
        o: ['5i + 2j', '7i', '3i', '5i − 2j'],
        a: 0,
        e: 'Resultant velocity = velocity relative to water + current velocity = 5i + 2j.',
        h: "Add the boat's own velocity vector to the current's velocity vector.",
        yr: 'SS2',
      },
      {
        q: 'For the resultant velocity 5i+2j m/s, find its magnitude (speed) to 2 d.p.',
        o: ['5.39 m/s', '7 m/s', '3 m/s', '29 m/s'],
        a: 0,
        e: '|5i+2j| = √(25+4) = √29 ≈ 5.39 m/s.',
        h: 'Apply the magnitude formula, then take the square root of 29 with a calculator.',
        yr: 'SS2',
      },
      {
        q: 'If vector a = 4i − 3j, find |a|.',
        o: ['5', '7', '1', '25'],
        a: 0,
        e: "|a| = √(16+9) = √25 = 5. (Note: the negative sign doesn't affect magnitude since we square the components.)",
        h: 'Squaring removes any negative signs, so magnitude is always non-negative.',
        yr: 'SS2',
      },
      {
        q: 'Find the vector from point A(2,3) to point B(7,9).',
        o: ['5i + 6j', '9i + 12j', '7i + 9j', '5i + 12j'],
        a: 0,
        e: 'Vector AB = (position of B) − (position of A) = (7−2)i + (9−3)j = 5i + 6j.',
        h: "Subtract point A's coordinates from point B's coordinates, component by component.",
        yr: 'SS2',
      },
      {
        q: 'Two equal and opposite forces act on an object: F and −F. Find the resultant force.',
        o: ['0 (the zero vector)', '2F', 'F', '−F'],
        a: 0,
        e: 'F + (−F) = 0. Equal and opposite forces always cancel to give a zero resultant.',
        h: 'Adding a vector to its exact negative always gives the zero vector.',
        yr: 'SS2',
      },
      {
        q: 'A vector has magnitude 10 and makes an angle of 30° with the x-axis. Find its i-component (horizontal), using cos30°≈0.866.',
        o: ['8.66', '5', '10', '0.866'],
        a: 0,
        e: 'i-component = |v|cosθ = 10×0.866 = 8.66.',
        h: 'Use the horizontal component formula: magnitude times cosine of the angle.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Integration I',
    topicCode: 'SS2-FMATH-32',
    module: 'Calculus',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  <span class="learn-keyword">Integration</span> is the reverse process of differentiation &mdash; given a derivative (gradient function), we find the original function. This is also called <span class="learn-keyword">anti-differentiation</span>, and introduces the "+C" constant of integration.</div>\n\n<h3 class="learn-subheading">1. The Power Rule for Integration</h3>\n<p class="learn-p">&#8747;x&#8319; dx = x&#8319;&#8314;&sup1;/(n+1) + C (for n &ne; &minus;1). <strong>Example:</strong> &#8747;x&sup3; dx = x&#8308;/4 + C.</p>\n\n<h3 class="learn-subheading">2. Why "+C"?</h3>\n<p class="learn-p">Since the derivative of ANY constant is zero, when reversing differentiation we cannot know what constant was originally present. We always add "+C" (the constant of integration) to indicate this uncertainty, unless given additional information to find its exact value.</p>\n\n<h3 class="learn-subheading">3. Integrating Sums and Constant Multiples</h3>\n<p class="learn-p">Integrate term by term. <strong>Example:</strong> &#8747;(3x&sup2; &minus; 4x + 5) dx = x&sup3; &minus; 2x&sup2; + 5x + C.</p>\n\n<h3 class="learn-subheading">4. Finding the Constant C</h3>\n<p class="learn-p"><strong>Example:</strong> Given dy/dx = 2x + 3 and y = 5 when x = 1, find y.<br>\ny = &#8747;(2x+3)dx = x&sup2;+3x+C. Substituting: 5 = 1+3+C &rArr; C=1. So y = x&sup2;+3x+1.</p>\n\n<div class="learn-table-wrap">\n  <table class="learn-table">\n    <thead><tr><th>Function</th><th>Integral</th></tr></thead>\n    <tbody>\n      <tr><td>x&#8319;</td><td>x&#8319;&#8314;&sup1;/(n+1) + C</td></tr>\n      <tr><td>k (constant)</td><td>kx + C</td></tr>\n      <tr><td>1/x&#8319; = x&#8315;&#8319;</td><td>x&#8315;&#8319;&#8314;&sup1;/(&minus;n+1) + C (for n&ne;1)</td></tr>\n    </tbody>\n  </table>\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> Always ADD 1 to the power and then DIVIDE by the new power &mdash; this is the exact reverse of differentiation\'s "bring down and subtract 1". Never forget the +C unless the question involves definite limits.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Integration reverses differentiation: &#8747;x&#8319;dx = x&#8319;&#8314;&sup1;/(n+1)+C. Always include the constant of integration C, and use given conditions (a known point on the curve) to solve for its exact value when required.\n</div>\n',
    questions: [
      {
        q: 'Evaluate ∫x³ dx.',
        o: ['x⁴/4 + C', '3x² + C', 'x⁴ + C', '4x³ + C'],
        a: 0,
        e: 'Using the power rule for integration: ∫x³ dx = x⁴/4 + C.',
        h: 'Add 1 to the power, then divide by the new power.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫(3x² − 4x + 5) dx.',
        o: ['x³ − 2x² + 5x + C', '3x³ − 4x² + 5x + C', 'x³ − 4x² + 5x + C', '6x − 4 + C'],
        a: 0,
        e: 'Integrate term by term: ∫3x²dx=x³, ∫−4xdx=−2x², ∫5dx=5x. Total: x³−2x²+5x+C.',
        h: 'Apply the power rule to each term separately, remembering to add 1 to each power.',
        yr: 'SS2',
      },
      {
        q: 'Given dy/dx = 2x + 3 and y = 5 when x = 1, find y.',
        o: ['y = x² + 3x + 1', 'y = x² + 3x + 5', 'y = x² + 3x', 'y = 2x² + 3x + 1'],
        a: 0,
        e: 'y = ∫(2x+3)dx = x²+3x+C. At x=1, y=5: 5=1+3+C ⇒ C=1. So y=x²+3x+1.',
        h: 'Integrate first, then substitute the given point to find the constant C.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫5 dx (integrating a constant).',
        o: ['5x + C', '5 + C', '0 + C', 'x + C'],
        a: 0,
        e: 'The integral of a constant k is kx + C: ∫5 dx = 5x + C.',
        h: 'Integrating a plain number always gives that number times x, plus C.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫x⁵ dx.',
        o: ['x⁶/6 + C', '6x⁵ + C', 'x⁶ + C', '5x⁴ + C'],
        a: 0,
        e: '∫x⁵ dx = x⁶/6 + C.',
        h: 'Add 1 to the power (5→6), then divide by that new power.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫4x³ dx.',
        o: ['x⁴ + C', '4x⁴ + C', '12x² + C', 'x⁴/4 + C'],
        a: 0,
        e: '∫4x³ dx = 4×(x⁴/4) + C = x⁴ + C.',
        h: 'Apply the power rule first, then simplify the constant coefficient.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫(6x² + 2x − 3) dx.',
        o: ['2x³ + x² − 3x + C', '6x³ + x² − 3x + C', '2x³ + 2x² − 3x + C', '12x + 2 + C'],
        a: 0,
        e: '∫6x²dx=2x³, ∫2xdx=x², ∫−3dx=−3x. Total: 2x³+x²−3x+C.',
        h: 'Integrate each term separately, applying the power rule to each.',
        yr: 'SS2',
      },
      {
        q: 'Given dy/dx = 4x − 1 and y = 3 when x = 0, find y.',
        o: ['y = 2x² − x + 3', 'y = 2x² − x', 'y = 4x² − x + 3', 'y = 2x² + x + 3'],
        a: 0,
        e: 'y=∫(4x−1)dx=2x²−x+C. At x=0, y=3: 3=0−0+C⇒C=3. So y=2x²−x+3.',
        h: 'Integrate first, then substitute x=0 directly since it simplifies the calculation.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫1 dx.',
        o: ['x + C', '1 + C', '0 + C', 'x²/2 + C'],
        a: 0,
        e: 'Integrating the constant 1 gives 1×x + C = x + C.',
        h: 'Treat 1 as a constant and apply the constant integration rule.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫x⁻² dx (for x ≠ 0).',
        o: ['−x⁻¹ + C (i.e., −1/x + C)', 'x⁻¹ + C', '−2x⁻³ + C', '−x⁻³ + C'],
        a: 0,
        e: '∫x⁻²dx = x⁻¹/(−1) + C = −x⁻¹ + C = −1/x + C.',
        h: 'Apply the power rule even with negative exponents: add 1 to −2 to get −1, then divide by −1.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫√x dx (i.e., ∫x¹ᐞ² dx).',
        o: ['(2/3)x³ᐞ² + C', '½x⁻¹ᐞ² + C', 'x³ᐞ² + C', '(3/2)x¹ᐞ² + C'],
        a: 0,
        e: '∫x¹ᐞ²dx = x³ᐞ²/(3/2) + C = (2/3)x³ᐞ² + C.',
        h: 'Add 1 to the power 1/2 to get 3/2, then divide by 3/2 (equivalent to multiplying by 2/3).',
        yr: 'SS2',
      },
      {
        q: 'Given dy/dx = 3x² and the curve passes through (2, 10), find y.',
        o: ['y = x³ + 2', 'y = x³ + 10', 'y = x³', 'y = 3x³ + 2'],
        a: 0,
        e: 'y=∫3x²dx=x³+C. At x=2, y=10: 10=8+C⇒C=2. So y=x³+2.',
        h: 'Integrate first, then use the given point to solve for the constant.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫(x² + 2x + 1) dx.',
        o: ['x³/3 + x² + x + C', 'x³ + x² + x + C', 'x³/3 + 2x² + x + C', '2x + 2 + C'],
        a: 0,
        e: '∫x²dx=x³/3, ∫2xdx=x², ∫1dx=x. Total: x³/3+x²+x+C.',
        h: 'Integrate each of the three terms individually using the power rule.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is the correct reverse process check: differentiate x⁴/4 + C. What do you get?',
        o: ['x³', 'x⁴', '4x³', 'x⁴/4'],
        a: 0,
        e: 'd/dx(x⁴/4 + C) = 4x³/4 + 0 = x³, confirming that ∫x³dx=x⁴/4+C is correct (differentiation undoes integration).',
        h: 'Differentiating your integration answer should always return you to the original function — a great way to check your work.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫(2x − 5) dx.',
        o: ['x² − 5x + C', '2x² − 5x + C', 'x² − 5 + C', '2x² − 5 + C'],
        a: 0,
        e: '∫2xdx=x², ∫−5dx=−5x. Total: x²−5x+C.',
        h: 'Apply the power rule and constant rule to each term separately.',
        yr: 'SS2',
      },
      {
        q: 'Given dy/dx = 6x² − 2 and y = 4 when x = 1, find y.',
        o: ['y = 2x³ − 2x + 4', 'y = 2x³ − 2x', 'y = 6x³ − 2x + 4', 'y = 2x³ + 2x + 4'],
        a: 0,
        e: 'y=∫(6x²−2)dx=2x³−2x+C. At x=1,y=4: 4=2−2+C⇒C=4. So y=2x³−2x+4.',
        h: 'Integrate the gradient function, then use the given point to solve for C.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫7x⁶ dx.',
        o: [
          'x⁷ + C',
          '7x⁷/7 + C written differently is same as x⁷+C, but check: 7x⁷ + C is wrong',
          '42x⁵ + C',
          'x⁶ + C',
        ],
        a: 0,
        e: '∫7x⁶dx = 7×(x⁷/7) + C = x⁷ + C.',
        h: 'The coefficient 7 and the new denominator 7 (from n+1=7) cancel out neatly.',
        yr: 'SS2',
      },
      {
        q: "Why must we ALWAYS add '+C' when finding an indefinite integral (without given conditions)?",
        o: [
          "Because any constant's derivative is zero, so we can't know the original constant from the derivative alone",
          'Because C always equals zero',
          'Because integration always increases the power by exactly 1',
          "It's just a notational convention with no mathematical meaning",
        ],
        a: 0,
        e: 'Since differentiating ANY constant gives zero, reversing this process (integrating) cannot recover what that constant originally was — hence we must acknowledge this uncertainty with +C.',
        h: "Think about what information is 'lost' when you differentiate a function that includes a constant term.",
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫(4x³ − 3x² + 2x) dx.',
        o: ['x⁴ − x³ + x² + C', '4x⁴ − 3x³ + 2x² + C', 'x⁴ − x³ + x²', '12x² − 6x + 2 + C'],
        a: 0,
        e: '∫4x³dx=x⁴, ∫−3x²dx=−x³, ∫2xdx=x². Total: x⁴−x³+x²+C.',
        h: 'Integrate each of the three terms using the power rule, remembering the +C at the end.',
        yr: 'SS2',
      },
      {
        q: 'Given dy/dx = x² and the curve passes through the origin (0,0), find y.',
        o: ['y = x³/3', 'y = x³', 'y = x³/3 + C where C≠0', 'y = 3x³'],
        a: 0,
        e: 'y = ∫x²dx = x³/3 + C. At (0,0): 0 = 0 + C ⇒ C = 0. So y = x³/3.',
        h: 'Integrate first, then use the fact that the curve passes through the origin to find C = 0.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Integration II and Applications',
    topicCode: 'SS2-FMATH-33',
    module: 'Calculus',
    term: 'Third Term',
    contentHTML:
      '\n<div class="learn-intro">\n  We complete Further Mathematics with <span class="learn-keyword">definite integrals</span> (which give exact numerical answers) and their real applications: finding the <span class="learn-keyword">area under a curve</span> and solving kinematics problems by integrating acceleration and velocity.</div>\n\n<h3 class="learn-subheading">1. The Definite Integral</h3>\n<p class="learn-p">&#8747;<sub>a</sub><sup>b</sup> f(x) dx = F(b) &minus; F(a), where F(x) is any antiderivative of f(x). No "+C" is needed since it cancels out.<br>\n<strong>Example:</strong> &#8747;<sub>1</sub><sup>3</sup> x&sup2; dx = [x&sup3;/3]&#8321;&sup3; = 27/3 &minus; 1/3 = 9 &minus; 0.333 = 8.667.</p>\n\n<h3 class="learn-subheading">2. Area Under a Curve</h3>\n<p class="learn-p">The area between y=f(x), the x-axis, and the lines x=a, x=b is given by &#8747;<sub>a</sub><sup>b</sup> f(x) dx (assuming f(x) &ge; 0 in this interval).<br>\n<strong>Example:</strong> Find the area under y=x&sup2; between x=0 and x=2.<br>\nArea = &#8747;&#8320;&sup2; x&sup2; dx = [x&sup3;/3]&#8320;&sup2; = 8/3 &minus; 0 = 8/3 square units.</p>\n\n<h3 class="learn-subheading">3. Area Between Two Curves</h3>\n<p class="learn-p">Area = &#8747;<sub>a</sub><sup>b</sup> [f(x) &minus; g(x)] dx, where f(x) is the upper curve and g(x) the lower curve, between their intersection points a and b.</p>\n\n<h3 class="learn-subheading">4. Kinematics via Integration</h3>\n<p class="learn-p">Since a=dv/dt and v=ds/dt, we can integrate to reverse the process: v = &#8747;a dt, and s = &#8747;v dt.<br>\n<strong>Example:</strong> A particle has acceleration a=6t. If v=0 at t=0, find v at t=3.<br>\nv = &#8747;6t dt = 3t&sup2; + C. At t=0, v=0: C=0. So v=3t&sup2;. At t=3: v=27 m/s.</p>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#128161;</span>\n  <strong>Exam Tip:</strong> For definite integrals, ALWAYS substitute the upper limit first, then subtract the result of substituting the lower limit &mdash; F(b) &minus; F(a), never the other way round.\n</div>\n\n<div class="learn-tip-box">\n  <span class="learn-tip-icon">&#127919;</span>\n  <strong>Summary:</strong> Definite integrals give exact numerical values by evaluating the antiderivative at the limits and subtracting. This computes areas under curves directly. In kinematics, integrating acceleration gives velocity, and integrating velocity gives displacement.\n</div>\n',
    questions: [
      {
        q: 'Evaluate ∫₁³ x² dx.',
        o: ['26/3 (≈ 8.67)', '8', '9', '2/3'],
        a: 0,
        e: '[x³/3]₁³ = 27/3 − 1/3 = 26/3 ≈ 8.67.',
        h: 'Find the antiderivative, then substitute the upper limit minus the lower limit.',
        yr: 'SS2',
      },
      {
        q: 'Find the area under y = x² between x = 0 and x = 2.',
        o: ['8/3', '4', '8', '2'],
        a: 0,
        e: 'Area = ∫₀² x² dx = [x³/3]₀² = 8/3 − 0 = 8/3.',
        h: 'Set up the definite integral with the given limits, then evaluate.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₀² (3x² + 1) dx.',
        o: ['10', '9', '8', '12'],
        a: 0,
        e: '[x³+x]₀² = (8+2) − (0+0) = 10.',
        h: 'Find the antiderivative first, then evaluate at the upper and lower limits.',
        yr: 'SS2',
      },
      {
        q: 'A particle has acceleration a = 6t. If v = 0 at t = 0, find v at t = 3.',
        o: ['27 m/s', '18 m/s', '9 m/s', '54 m/s'],
        a: 0,
        e: 'v=∫6tdt=3t²+C. At t=0,v=0: C=0. So v=3t². At t=3: v=3(9)=27 m/s.',
        h: 'Integrate the acceleration to find velocity, use the initial condition to find C, then substitute t=3.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₁² 4x³ dx.',
        o: ['15', '16', '1', '60'],
        a: 0,
        e: '[x⁴]₁² = 16 − 1 = 15.',
        h: 'The antiderivative of 4x³ is x⁴; evaluate at the given limits.',
        yr: 'SS2',
      },
      {
        q: 'Find the area under y = 2x between x = 1 and x = 4.',
        o: ['15', '8', '16', '30'],
        a: 0,
        e: 'Area = ∫₁⁴ 2x dx = [x²]₁⁴ = 16−1 = 15.',
        h: 'Integrate 2x to get x², then apply the limits.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₀¹ (x² + x) dx.',
        o: ['5/6', '1', '1/2', '1/3'],
        a: 0,
        e: '[x³/3+x²/2]₀¹ = (1/3+1/2)−0 = 2/6+3/6 = 5/6.',
        h: 'Integrate each term, evaluate at x=1, then subtract the value at x=0 (which is 0).',
        yr: 'SS2',
      },
      {
        q: 'A particle has velocity v = 3t² − 2t. If s = 0 at t = 0, find s at t = 2.',
        o: ['4 m', '8 m', '16 m', '0 m'],
        a: 0,
        e: 's=∫(3t²−2t)dt=t³−t²+C. At t=0,s=0: C=0. At t=2: s=8−4=4 m.',
        h: 'Integrate velocity to find displacement, using the initial condition to determine C.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₂⁴ (x − 1) dx.',
        o: ['4', '6', '2', '10'],
        a: 0,
        e: '[x²/2−x]₂⁴ = (8−4)−(2−2) = 4−0 = 4.',
        h: 'Integrate first, then carefully substitute both limits and subtract.',
        yr: 'SS2',
      },
      {
        q: 'Find the area between y = x² and y = x (upper curve minus lower curve) from x=0 to x=1.',
        o: ['1/6', '1/3', '1/2', '5/6'],
        a: 0,
        e: 'Between 0 and 1, y=x is above y=x². Area = ∫₀¹(x−x²)dx = [x²/2−x³/3]₀¹ = (1/2−1/3)−0 = 1/6.',
        h: 'Identify which curve is on top in this interval, then integrate their difference.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₁³ (2x + 3) dx.',
        o: ['16', '10', '22', '8'],
        a: 0,
        e: '[x²+3x]₁³ = (9+9)−(1+3) = 18−4 = 14. Recomputing: at x=3: 9+9=18; at x=1: 1+3=4; 18-4=14.',
        h: 'Find the antiderivative first, then carefully substitute both limits.',
        yr: 'SS2',
      },
      {
        q: 'Which formula correctly gives displacement from a velocity-time function?',
        o: ['s = ∫v dt', 's = dv/dt', 's = v × t always', 's = ∫a dt'],
        a: 0,
        e: 'Since v = ds/dt, reversing this (integrating velocity with respect to time) gives displacement: s = ∫v dt.',
        h: 'Displacement is the antiderivative (integral) of velocity, not its derivative.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₀³ (x² − 2x) dx.',
        o: ['0', '9', '−9', '3'],
        a: 0,
        e: '[x³/3−x²]₀³ = (9−9)−0 = 0.',
        h: 'Evaluate the antiderivative carefully at x=3, noting the terms may cancel.',
        yr: 'SS2',
      },
      {
        q: 'A particle has acceleration a = 4 (constant). If v = 2 at t = 0, find v at t = 5.',
        o: ['22 m/s', '20 m/s', '10 m/s', '18 m/s'],
        a: 0,
        e: 'v=∫4dt=4t+C. At t=0,v=2: C=2. So v=4t+2. At t=5: v=20+2=22 m/s.',
        h: 'Integrate the constant acceleration, use the initial velocity to find C, then substitute t=5.',
        yr: 'SS2',
      },
      {
        q: 'Find the area under y = x³ between x = 0 and x = 3.',
        o: ['81/4', '27', '9', '20.25 (same as 81/4)'],
        a: 0,
        e: 'Area = ∫₀³ x³ dx = [x⁴/4]₀³ = 81/4 − 0 = 81/4 = 20.25.',
        h: 'Integrate x³ to get x⁴/4, then apply the given limits.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₁⁴ √x dx (i.e., ∫x¹ᐞ² dx).',
        o: ['14/3 (≈ 4.67)', '16/3', '7', '2'],
        a: 0,
        e: '[(2/3)x³ᐞ²]₁⁴ = (2/3)(8)−(2/3)(1) = 16/3−2/3 = 14/3.',
        h: 'Integrate using fractional powers, then substitute the limits carefully.',
        yr: 'SS2',
      },
      {
        q: 'A particle decelerates with a = −2 m/s². If v = 10 m/s at t = 0, find the displacement at t = 3 (using s = ∫v dt after finding v(t)).',
        o: ['21 m', '30 m', '24 m', '9 m'],
        a: 0,
        e: 'v=∫−2dt=−2t+10 (using v=10 at t=0). s=∫(−2t+10)dt=−t²+10t+C. At t=0,s=0(assumed): C=0. At t=3: s=−9+30=21 m.',
        h: 'First integrate acceleration to get velocity, then integrate velocity to get displacement.',
        yr: 'SS2',
      },
      {
        q: 'Evaluate ∫₀¹ 6x² dx.',
        o: ['2', '6', '3', '1'],
        a: 0,
        e: '[2x³]₀¹ = 2−0 = 2.',
        h: 'The antiderivative of 6x² is 2x³; substitute the given limits.',
        yr: 'SS2',
      },
      {
        q: 'For a definite integral ∫ₐᵇ f(x) dx, why is the constant of integration NOT needed?',
        o: [
          'Because the constant C cancels out when subtracting F(a) from F(b)',
          'Because definite integrals never have antiderivatives',
          'Because a and b are always equal',
          'Constants ARE still needed in definite integrals',
        ],
        a: 0,
        e: 'Since ∫ f(x)dx = F(x)+C, evaluating [F(x)+C] at b minus at a gives [F(b)+C]−[F(a)+C] = F(b)−F(a) — the C terms cancel out completely.',
        h: 'Consider what happens to the +C when you subtract F(a)+C from F(b)+C.',
        yr: 'SS2',
      },
      {
        q: 'Find the area under y = 4 (a horizontal line) between x = 1 and x = 5.',
        o: ['16', '20', '4', '5'],
        a: 0,
        e: 'Area = ∫₁⁵ 4 dx = [4x]₁⁵ = 20−4 = 16. (This also matches the simple rectangle formula: width×height = 4×4=16.)',
        h: 'This is simply a rectangle; you can verify using width × height directly.',
        yr: 'SS2',
      },
    ],
  },
];
